import { buildBackButton } from "./sheet-ui.js";
import { isNativePlatform } from "./native-voice.js";
import { createTelegramKarte } from "./telegram-karte.js";
import { createModellZugangKarte } from "./modell-zugang-karte.js";
import { createKontoGuthabenKarte } from "./konto-guthaben-karte.js";
import { sichtbareVersionJetzt } from "./app-version.js";
import { zeichen } from "./zeichen.js";
/**
 * voice-v8 — Settings-Tab UI (Tag 19).
 *
 * Wird beim ersten Tab-Switch lazy-geladen. Änderungen schreiben sofort
 * in den LocalStorage; der nächste Connect zieht sie automatisch.
 */

import {
  deleteUserLocation,
  disconnectGoogle,
  fetchConnectors,
  fetchCoachMode,
  fetchDaemonStatus,
  fetchExtensionStatus,
  fetchUserLocation,
  generateDaemonPairingCode,
  generatePairingCode,
  saveUserLocation,
  setCoachMode,
  type ConnectorsState,
  type DaemonStatus,
  type ExtensionStatus,
  type UserLocation,
} from "./connectors-api.js";
import {
  isAutoLocationEnabled,
  setAutoLocationEnabled,
  startGeoWatch,
} from "./geo-watch.js";
import { createPersonaBlock } from "./settings/persona-block.js";
import { createModellKetteCard } from "./settings/modell-status.js";
import { createPanelKarte } from "./settings/panel-karte.js";
import { verbindungsKarte } from "./settings/verbindungs-karte.js";
import { baueWoerterSeite } from "./settings/woerter-seite.js";
import { einklappbar, einstAbschnitt, einstMehr, einstSchalter, einstText, geraeteMarke } from "./settings/einst-bausteine.js";
import { baueSuche, hebeHervor, suchIndex, type Fund } from "./settings/einst-suche.js";
import { datenApi } from "./settings/daten-api.js";
import { baueDeineDatenSeite, letzterExportText, type DeineDatenSeite } from "./settings/deine-daten-seite.js";
import { baueEntwicklerSeite, holeTeamStand, teamKontoAus, teamKurzText } from "./settings/entwickler-seite.js";
import { baueEntwicklerSchnellschalter } from "./settings/entwickler-schnellschalter.js";
import { ENTWICKLER_KONTO_EREIGNIS, baueLageKarte } from "./settings/lage-karte.js";
import { baueServerKarte } from "./settings/server-karte.js";
import { loadAutoPlay, saveAutoPlay } from "./lautsprecher.js";
import { LESEMODUS_EREIGNIS, lesemodusAn, setzeLesemodus } from "./lesemodus.js";
import {
  BRIGHTNESSES,
  BRIGHTNESS_LABELS,
  getBrightness,
  getPalette,
  PALETTES,
  PALETTE_LABELS,
  setBrightness,
  setPalette,
  ZUHAUSE_STILE,
  ZUHAUSE_STIL_LABELS,
  getZuhauseStil,
  setZuhauseStil,
} from "./appearance.js";
import * as push from "./push.js";
import { archivSatz, ARCHIV_TAGE_AUSWAHL } from "../../server/auto-archiv.js";
import { modellAusKurzform } from "../../server/models.js";
import { claudeName } from "../../shared/modell-namen.js";
import {
  starkesModellAus,
  type StarkesModell,
} from "../../shared/abo-modelle.js";
import {
  DENKSTUFE_NAME,
  DENKSTUFE_TIEF_VORGABE,
  TIEFE_DENKSTUFEN,
  istTiefeDenkstufe,
} from "../../shared/denkstufe.js";
import {
  chosenReadVoice,
  DEFAULT_SETTINGS,
  GEMINI_READ_VOICES,
  GEMINI_REALTIME_MODELS,
  GEMINI_REALTIME_VOICES,
  loadSettings,
  speichereEinstellungen,
  autoMikroStufenFuer,
  autoMikroStufeFuerKonto,
  NACHLAUSCH_STUFEN,
  nachlauschFenster,
  VOICE_OPTIONS,
  type VoiceGender,
  type VoiceV8Settings,
} from "./settings.js";
import type { SpeicherErgebnis } from "./einstellungen-ausstehend.js";
import { wartendeAenderungen } from "./einstellungen-abgleich.js";
import { warteSatzAn, warteSatzFuerKonto } from "../../shared/warte-satz.js";
import { kontoGruppen, kontoKennung, kontoLage } from "./konto-lage.js";
import { feldErlaubtFuerKonto } from "../../shared/einstellungen-sichtbar.js";
import { baueSelbstverbesserungKarte } from "./settings/selbstverbesserung-karte.js";
import { morseAn, morseFuerKonto } from "../../shared/morse-konto.js";
import { VORLESER_NATIV_FELD, vorleserNativAn } from "../../shared/vorleser-nativ.js";
import { mikroBeimVorlesenAnwenden } from "./mikro-beim-vorlesen.js";
import { MUSIK_OHNE_PROBE_FELD, musikOhneProbeAn } from "../../shared/musik-ohne-probe.js";
import {
  istJahreszeitWahl,
  JAHRESZEIT_NAME,
  JAHRESZEIT_VORGABE,
  JAHRESZEIT_WAHLEN,
  jahreszeitAmDatum,
} from "../../shared/jahreszeit.js";
import {
  istElevenStimmKennung,
  MEDITATION_ELEVEN_STIMMEN,
  MEDITATIONS_STIMMEN,
  meditationElevenWaehlbar,
  meditationsVorgabe,
  vorgabeBeschreibung,
} from "../../shared/meditations-stimme.js";
import {
  EMOJI_STUFEN,
  emojiStufe,
  emojiStufenName,
  type EmojiStufe,
  type EmojiStufenName,
} from "../../shared/emoji-stufe.js";
import { EMOJI_TEXT, EMOJI_WANN, tempoText } from "./schnell-einstellungen.js";
import { baueStufenWahl } from "./stufen-wahl.js";
import { VORLESE_TEMPO } from "../../shared/vorlese-tempo.js";
import { stilleBestaetigung } from "../../shared/stille-auto.js";
import { ABSENDEN_NAME, STILLE_ABSCHNITT, STILLE_WAHL_NAME, baueAbsendenWahl, baueStilleWahl, stilleAutoMoeglich } from "./stille-wahl.js";
import { desktopStand, erweiterungStand } from "../../shared/app-version.js";
import { versionZeile } from "./version-zeile.js";
import { jevMoeglich, jevStufeGeschuetzt } from "./jev-konto.js";
import {
  TAGESLIMIT_MAX,
  TAGESLIMIT_MIN,
  TAGESLIMIT_UNBEGRENZT,
  anrufGrenzenText,
  anrufGrenzenVon,
} from "../../shared/anruf-grenzen.js";
import { baueKopplungsCode, CODE_GUELTIG_MS } from "./kopplungs-code.js";
import {
  bedienungshilfenHinweis,
  CODE_HINWEIS,
  istMacBrowser,
  istNeueDesktopApp,
  downloadText,
  DOWNLOAD_HINWEIS,
  downloadHinweis,
  seitText,
  koppelnMitNeuerApp,
  oeffneLink,
  verbindenLink,
  zweitesProgrammTeile,
} from "./desktop-verbinden.js";
import { browserAlsGeraet, MAC_HOECHSTENS, neueVerbindung, VIER_MACS_HINWEIS, vierHinweis } from "./mac-geraete.js";
import { baueGeraeteListe, baueMacGeraeteListe } from "./mac-geraete-liste.js";
import { baueBlenderFreigabe } from "./blender-freigabe-schalter.js";
import { DESKTOP_STATUS_EREIGNIS } from "./mac-zeichen.js";
import { meldeSpur } from "./mikro-spur.js";
import { entsperrePcm, schliessePcmKontext } from "./tts-pcm.js";
import { echtzeitTestKarte } from "./tts-echtzeit-test.js";
import { reflexHoerprobeKarte } from "./reflex-hoerprobe-karte.js";
import { entsperreDateiSpieler } from "./datei-spieler.js";
import { pcmRueckfall, pcmRueckfallZuruecksetzen } from "./tts-pcm-rueckfall.js";
import { setzeWiederanlauf, wiederanlaufAn } from "./vorlese-wiederanlauf.js";
import { merkeSeitenwechsel } from "./scroll-ort.js";
import { drosselErlaubt, nameVon } from "./scroll-wache.js";
import { nachRuhe as nachRuheGemeinsam, nutzerScrolltGerade, schreibeScrollTop, vergiss } from "./scroll-ruhe.js";
import { gesteuert, ruckelAus } from "./ruckel-schalter.js";
import { ruckelTestKarten } from "./ruckel-test.js";
import { bildrateKarte } from "./bildrate-hinweis.js";
import {
  deuteElevenProbe,
  ELEVEN_AUS,
  ELEVEN_MEHR_ANTWORT,
  ELEVEN_MEHR_FRAGE,
  ELEVEN_TITEL,
  ELEVEN_VERWAIST,
  ELEVEN_VERWAIST_HINWEIS,
  elevenGespeichert,
  MEDITATIONS_PROBE,
  PROBE_ZWECK,
  type ProbeDeutung,
} from "./meditation-eleven.js";
import {
  MEDITATIONS_STILE,
  MEDITATIONS_STIL_NAMEN,
  MEDITATIONS_STIL_TITEL,
  meditationsStilAus,
} from "../../shared/meditations-stil.js";
import { meditationKostenSatz } from "../../shared/meditation-kosten.js";

import { EINST_GRUPPEN, EINST_SEITEN, type EinstSeite, type SeitenId } from "../../shared/einst-seiten.js";

// Die Liste der Seiten steht in shared/einst-seiten.ts (der Server braucht sie für seine Absagen); hier weitergereicht.
export { EINST_GRUPPEN, EINST_SEITEN };
export type { SeitenId };

const SEITEN_IDS = new Set<string>(EINST_SEITEN.map((x) => x.id));

/**
 * Alte Deeplinks („bot", „connectors", „system" …) und die früheren Reiter
 * landen auf der passenden Seite — oder auf der Übersicht (null).
 */
export function resolveSubTab(raw: unknown): SeitenId | null {
  const v = String(raw ?? "").toLowerCase();
  if (SEITEN_IDS.has(v)) return v as SeitenId;
  if (v === "identity" || v === "ich" || v === "mich" || v === "profile") return "profil";
  if (v === "bot" || v === "konto" || v === "account" || v === "version") return "modelle";
  if (v === "voice" || v === "stimme") return "stimme";
  if (v === "vokabular" || v === "vocabulary") return "woerter";
  if (v === "connectors" || v === "modules" || v === "abilities") return "verbindungen";
  if (v === "notify") return "mitteilungen";
  // 17.10.2026 („Deine Daten“): kurze Namen, die ein Link oder eine Absage nennen kann.
  if (v === "daten" || v === "deine-daten" || v === "export" || v === "loeschen") return "deinedaten";
  return null;
}

/**
 * Wie lange die Stimmprobe hoechstens laufen darf.
 *
 * 2026-09-02 (Martin: "bitte eine längere Aufnahmemöglichkeit für meine
 * Referenz"). Vorher stoppte die Oberflaeche hart nach 20 Sekunden — der
 * Server nimmt bis 25 MB und kuerzt nichts, die Grenze war also allein hier.
 * Martins Referenz war entsprechend rund 200 kB kurz.
 *
 * Warum das zaehlt, an seinen Daten gemessen: die Referenz ist die EINZIGE
 * Stelle, an der das System weiss, wie er klingt. In der 72-min-Aufnahme vom
 * 02.09. trennte sie sauber — sein Cluster 0.72, die anderen 0.058 / 0.079 /
 * 0.024. Je mehr Stimme in der Referenz liegt, desto stabiler bleibt dieser
 * Abstand bei schwierigem Ton.
 *
 * 120 s ist die REISSLEINE, nicht das Ziel: der Knopf schaltet auf
 * "Stoppen & speichern", der Nutzer beendet selbst. Der Hinweis nennt eine
 * Minute als guten Richtwert.
 */
const STIMMPROBE_MAX_S = 120;

/*
 * Der Körper wird nie unter dem Finger ausgetauscht. (02.10.2026, Ticket 2912)
 *
 * Martin, iPhone: Scrollen in den Einstellungen „total unflüssig, ruckelt, springt wild herum“.
 * Was belegt ist: (1) `aenderungHandler` baut bei jeder Änderung vom Server den ganzen Körper neu
 * und schreibt `scrollTop` zurück — das Schreiben bricht auf dem iPhone ein laufendes Auslaufen ab
 * (lib/scroll-ort.ts, `ortHalten`); (2) die Zeile „n Änderungen warten“ steht GANZ OBEN im Körper:
 * Erscheint oder verschwindet sie, rutscht alles darunter um ihre Höhe (Nachbau ohne Verankerung,
 * wie WebKit: 71 Punkte unter dem Finger). Beides geschieht jetzt erst, wenn niemand mehr scrollt
 * (`nutzerScrolltGerade`, lib/scroll-ruhe.ts — seit pre.3607 dieselbe Regel wie im Chat, ohne
 * Höchstfrist: Finger auf dem Glas, Nachlaufen, Ausfedern), und die Zeile hält den Ort. Was NICHT belegt ist: dass genau das Martins
 * Ruckeln war — dafür misst lib/scroll-takt.ts die Bildzeit, und die Zeilen `art=einst-neu` sagen,
 * wie oft und warum der Körper neu gebaut wird.
 */
/** Höchstens so viele Zeilen `art=einst-neu` je Minute (dieselbe Grenze wie die Scroll-Wache). */
const neuZeiten: number[] = [];
/** So lange (ms) nach dem Aufbau schaut `art=einst-ein` noch einmal nach, ob sich Stelle oder Höhe von selbst geändert haben. */
const EINTRITT_NACH_MS = 900;

/** Die Kontofelder, die den Zustand der Entwickler bestimmen (Hauptschalter und die drei Einzelschalter). */
const ENTWICKLER_KONTO_FELDER = ["entwicklerGesamt", "entwicklerImChat", "zimmermannImChat", "hueterImChat"];

export class SettingsView {
  private current: VoiceV8Settings;
  private status!: HTMLElement;
  /** „1 Änderung wartet" — steht, solange etwas nicht angekommen ist. */
  private wartendZeile!: HTMLElement;
  private root: HTMLElement;
  private body!: HTMLElement;
  /** Die offene Seite — null ist die Übersicht. */
  private seite: SeitenId | null = null;
  private titelEl: HTMLElement | null = null;
  /**
   * Aktiv abspielende Vorschau-Audio. Nur EINE gleichzeitig — beim
   * Anstoßen einer neuen wird die alte gestoppt + Source freigegeben.
   * Wir tracken das Element separat damit wir bei Tab-Switch / Re-Render
   * den Audio-Kontext sauber abreißen können (Memory-Leak-Vermeidung).
   */
  private previewAudio: HTMLAudioElement | null = null;
  private previewBlobUrl: string | null = null;
  /** Was auf das Ende des Scrollens wartet (Schlüssel → Aufgabe), und der Zeitgeber dafür. */
  /** Je Aufgabe ein Schlüssel für lib/scroll-ruhe.ts (je Ansicht eigene, damit zwei Ansichten sich nicht überschreiben). */
  private aufschubSchluessel = new Map<string, object>();
  /** Wie lange die letzte Aufgabe gewartet hat (ms) — für die Zeile `art=einst-neu`. */
  private letzterAufschubMs = 0;
  private zerstoert = false;

  constructor(root: HTMLElement) {
    this.root = root;
    // Wo stand die Fläche, als dieser Besuch begann (vor dem Leeren)? — Stoff für die Zeile `art=einst-ein`.
    const vor = this.messeVor();
    // Neuer Inhalt, Anfang: kein Sprung für die Scroll-Wache (lib/scroll-ort.ts).
    merkeSeitenwechsel(root);
    while (root.firstChild) root.removeChild(root.firstChild);
    root.classList.add("settings-view");

    this.current = loadSettings();

    this.renderHeader();
    this.body = document.createElement("div");
    this.body.className = "settings-body";
    root.appendChild(this.body);

    this.status = document.createElement("div");
    this.status.className = "settings-status";
    this.wartendZeile = this.baueWartendZeile();

    this.renderActive();
    this.anAnfang();
    this.meldeEintritt(vor);

    // Pre.202 / Pre.414: Externe Sub-Tab-Steuerung. Legacy-Werte wie
    // "connectors"/"modules"/"bot" werden via resolveSubTab() auf die
    // neuen 4 Tabs gemappt — alte Deeplinks brechen nicht.
    window.addEventListener("voice-v8:settings-subtab", this.subTabHandler);
    window.addEventListener("voice-v8:settings-changed", this.aenderungHandler);
    window.addEventListener("voice-v8:settings-sync", this.syncHandler);
  }

  /*
   * ⚠️ 23.09.2026, Ticket 2511 — die Seite las `this.current` EINMAL im
   *    Konstruktor und hörte danach auf niemanden. Kam ein Wert vom Server
   *    (anderes Gerät) oder aus dem Mikro-Fenster, zeigte sie weiter den
   *    alten Stand — und hätte ihn beim nächsten Speichern als Vollobjekt
   *    zurückgeschrieben (ein zweiter Besitzer). Jetzt: bei JEDER Änderung
   *    den Stand frisch lesen; kommt sie vom Server, neu zeichnen.
   */
  /** Hörer auf den Lesemodus (lib/lesemodus.ts); null-Hörer, solange die Karte nicht gebaut ist. */
  private leseHoerer: () => void = () => undefined;
  private aenderungHandler = (ev: Event): void => {
    this.current = loadSettings();
    const quelle = (ev as CustomEvent<{ quelle?: string } | null>).detail?.quelle;
    if (quelle === "server") this.neuLive();
  };

  /** Ruckel-Test (Ticket 2912): bei „Live-Aktualisierungen pausieren“ wartet der Neuaufbau und kommt beim Ausschalten nach. */
  private neuLive = gesteuert("live", (): void => this.nachRuhe("neu", () => this.zeichneNachFokus()), true);

  /** Neu zeichnen — aber nie unter einem Eingabefeld weg, in dem man tippt. */
  private zeichneNachFokus(): void {
    const fokus = document.activeElement;
    if (
      fokus instanceof HTMLElement &&
      this.root.contains(fokus) &&
      fokus.matches("input, textarea, select")
    ) {
      fokus.addEventListener("blur", () => this.zeichneNeu(), { once: true });
      return;
    }
    this.zeichneNeu();
  }

  private zeichneNeu(): void {
    if (!this.root.isConnected) return;
    const vor = this.messeVor();
    const oben = this.body.scrollTop;
    const obenAussen = this.root.scrollTop;
    this.renderActive();
    try {
      // Nur, was sich wirklich verschoben hat (kein Schreiben derselben Stelle).
      schreibeScrollTop(this.body, oben, "einstellungen");
      schreibeScrollTop(this.root, obenAussen, "einstellungen");
    } catch {
      /* ohne Layout (Tests) */
    }
    this.meldeNeu("server", vor);
  }

  /**
   * Läuft `tun` jetzt — oder, solange der Leser die Fläche bewegt, sobald es still ist (lib/scroll-ruhe.ts).
   * Dieselbe Aufgabe (gleicher Schlüssel) wartet nur einmal; die letzte gilt.
   */
  private nachRuhe(schluessel: string, tun: () => void): void {
    let k = this.aufschubSchluessel.get(schluessel);
    if (!k) {
      k = {};
      this.aufschubSchluessel.set(schluessel, k);
    }
    const seit = Date.now();
    nachRuheGemeinsam(
      this.root,
      k,
      () => {
        if (this.zerstoert) return;
        this.letzterAufschubMs = Date.now() - seit;
        try {
          tun();
        } finally {
          this.letzterAufschubMs = 0;
        }
      },
      "einstellungen",
    );
  }

  /** Eine Änderung OBEN im Inhalt vornehmen, ohne dass der Leser darunter verrutscht. */
  private halteOrt(aendere: () => void): void {
    const flaeche = this.root;
    if (flaeche.scrollTop <= 0) {
      aendere();
      return;
    }
    const vorHoehe = flaeche.scrollHeight;
    aendere();
    const unterschied = flaeche.scrollHeight - vorHoehe;
    if (unterschied !== 0) schreibeScrollTop(flaeche, Math.max(0, flaeche.scrollTop + unterschied), "einstellungen", true);
  }

  /** Stand vor einem Neuaufbau (nur lesen). `null` ohne Layout oder bei unsichtbarer Ansicht. */
  private messeVor(): { oben: number; hoehe: number; t: number } | null {
    if (!this.root.isConnected || this.root.clientHeight === 0) return null;
    return { oben: this.root.scrollTop, hoehe: this.root.scrollHeight, t: performance.now() };
  }

  /**
   * Eine Zeile `[scroll-t] art=einst-neu …` je Neuaufbau: warum, welche Seite, wo die Fläche vorher und
   * nachher stand, wie hoch sie war, wie lange es dauerte, ob der Nutzer gerade scrollte. Nur Namen von
   * Seiten und Zahlen. Damit steht beim nächsten Ruckeln da, ob der Körper je Minute dreimal oder
   * dreißigmal neu gebaut wird.
   */
  private meldeNeu(grund: "server" | "seite", vor: { oben: number; hoehe: number; t: number } | null): void {
    if (!vor || ruckelAus("wachen") || document.visibilityState !== "visible" || !drosselErlaubt(neuZeiten, Date.now())) return;
    try {
      const hoehe = this.root.scrollHeight;
      meldeSpur(
        "scroll-t",
        `art=einst-neu grund=${grund} seite=${this.seite ?? "uebersicht"} oben=${Math.round(vor.oben)}>${Math.round(this.root.scrollTop)} ` +
          `hoehe=${Math.round(vor.hoehe)}>${Math.round(hoehe)} ms=${Math.round(performance.now() - vor.t)} ` +
          `bewegt=${nutzerScrolltGerade() ? "ja" : "nein"} aufschub=${this.letzterAufschubMs}`,
      );
    } catch {
      /* Messen darf nie stören. */
    }
  }

  /**
   * Eine Zeile `[scroll-t] art=einst-ein …` je Besuch der Einstellungen (Ticket 2946): wo die Fläche stand, als der Besuch
   * begann (`oben` = was WebKit von der versteckten Ansicht wiederhergestellt hat), direkt nach dem Aufbau und
   * `EINTRITT_NACH_MS` später; dasselbe für die Höhe; wer den Fokus hat; ob jemand scrollte. Steht `oben=A>0>0`, war alles ruhig.
   * Springt es am Gerät trotzdem, steht hier, ob die Fläche NACH dem Aufbau von selbst weiterwanderte (`oben=…>0>X`) oder die
   * Höhe sich änderte (`hoehe=a>b>c`). Nur Zahlen und Namen von Flächen, nie Inhalte.
   */
  private meldeEintritt(vor: { oben: number; hoehe: number; t: number } | null): void {
    if (!vor || ruckelAus("wachen")) return;
    try {
      const direkt = { oben: this.root.scrollTop, hoehe: this.root.scrollHeight, ms: performance.now() - vor.t };
      window.setTimeout(() => {
        if (this.zerstoert || !this.root.isConnected || this.root.clientHeight === 0) return;
        if (document.visibilityState !== "visible" || !drosselErlaubt(neuZeiten, Date.now())) return;
        const r = Math.round;
        const fokus = document.activeElement;
        meldeSpur(
          "scroll-t",
          `art=einst-ein seite=${this.seite ?? "uebersicht"} oben=${r(vor.oben)}>${r(direkt.oben)}>${r(this.root.scrollTop)} ` +
            `hoehe=${r(vor.hoehe)}>${r(direkt.hoehe)}>${r(this.root.scrollHeight)} ms=${r(direkt.ms)} ` +
            `fokus=${fokus && fokus !== document.body ? nameVon(fokus) : "-"} bewegt=${nutzerScrolltGerade() ? "ja" : "nein"}`,
        );
      }, EINTRITT_NACH_MS);
    } catch {
      /* Messen darf nie stören. */
    }
  }

  /*
   * Was der Abgleich meldet, sagt die Seite — für JEDES Feld. (23.09.2026,
   * Ticket 2511)
   *
   * ⚠️ Vorher zeigte sie nur, was sie selbst über speichere() geschrieben
   *    hatte. Helligkeit, Farbe und Zuhause schreibt aber appearance.ts, und
   *    der Hinweis am Rand (app.ts) schweigt, solange diese Seite offen ist:
   *    Scheiterte dort das Speichern, sah man nichts. Jetzt kommen Fehler
   *    und „überholt" für alle Felder über dieses Ereignis; speichere()
   *    meldet nur noch den Erfolg — so steht nichts doppelt da.
   */
  private syncHandler = gesteuert(
    "live",
    (ev: Event): void => {
      const d = (ev as CustomEvent<SpeicherErgebnis | null>).detail;
      if (d?.text) this.flashStatus(d.text, d.zustand === "ueberholt" ? "hinweis" : "fehler");
      this.zeigeWartende();
    },
    true,
  );

  /** Die ruhige Dauerzeile — role=status, damit ein Vorleser sie ansagt. */
  private baueWartendZeile(): HTMLElement {
    const zeile = document.createElement("p");
    zeile.className = "einst-zustand einst-wartet";
    zeile.setAttribute("role", "status");
    const punkt = document.createElement("span");
    punkt.className = "einst-punkt";
    punkt.dataset.art = "wartet";
    const text = document.createElement("span");
    text.className = "einst-zustand-text";
    zeile.append(punkt, text);
    zeile.hidden = true;
    return zeile;
  }

  /**
   * Steht die Zeile im Baum, ändert sich bei jedem Zeigen/Verbergen die Höhe ÜBER dem Leser — und der
   * Inhalt rutscht (WebKit verankert nicht). Dann: erst wenn niemand scrollt, und mit Ort-Halten.
   * Beim Neuaufbau hängt die Zeile noch nicht im Baum — dort gibt es nichts zu halten.
   */
  private zeigeWartende(): void {
    if (!this.wartendZeile.isConnected) {
      this.setzeWartende();
      return;
    }
    this.nachRuhe("wartet", () => this.halteOrt(() => this.setzeWartende()));
  }

  private setzeWartende(): void {
    const n = wartendeAenderungen();
    this.wartendZeile.hidden = n === 0;
    const text = this.wartendZeile.querySelector<HTMLElement>(".einst-zustand-text");
    if (text && n > 0) {
      text.textContent =
        n === 1
          ? "1 Änderung ist noch nicht auf dem Server. Sie wird nachgereicht."
          : `${n} Änderungen sind noch nicht auf dem Server. Sie werden nachgereicht.`;
    }
  }

  /**
   * Speichern — und erst nach der Antwort des Servers melden. (23.09.2026,
   * Ticket 2511)
   *
   * ⚠️ Vorher stand überall `saveSettings(...)` und gleich danach die
   *    Erfolgsmeldung, bevor ein Server irgendetwas gesehen hatte. Schlug der
   *    Weg fehl, stand „gespeichert" trotzdem da (dasselbe Muster wie #2497,
   *    „Anzeige behauptet zu viel"). Jetzt kommt `erfolg` erst mit der
   *    Bestätigung. Was schiefging, sagt syncHandler.
   */
  private speichere(
    patch: Partial<VoiceV8Settings>,
    erfolg?: string,
  ): Promise<SpeicherErgebnis> {
    this.current = { ...this.current, ...patch };
    const antwort = speichereEinstellungen(patch);
    // Die Karten der Startseite zeigen einen umgeschalteten Entwickler sofort (ohne neue Frage an den Server).
    if (ENTWICKLER_KONTO_FELDER.some((f) => f in patch)) window.dispatchEvent(new CustomEvent(ENTWICKLER_KONTO_EREIGNIS));
    void antwort.then((e) => {
      if (erfolg && (e.zustand === "gespeichert" || e.zustand === "geraet")) {
        this.flashStatus(erfolg);
      }
    });
    return antwort;
  }

  // Pre.796 (#64 B0.2): benannter Handler statt anonymer Closure — vorher
  // leckte JEDER resetToDefaults-Aufruf einen Listener (new SettingsView
  // registrierte neu, der alte hing für immer am window).
  /** Die offene Seite „Deine Daten“ (ihr Abfrage-Takt endet mit der Ansicht). */
  private deineDaten: DeineDatenSeite | null = null;

  private subTabHandler = (ev: Event): void => {
    const detail = (ev as CustomEvent<{ sub?: string; fokus?: string }>).detail;
    if (!detail?.sub) return;
    this.zeigeSeite(resolveSubTab(detail.sub));
    if (detail.fokus) this.fokusAufAbschnitt(detail.fokus);
  };

  /**
   * Den Fokus auf die Überschrift eines Abschnitts der offenen Seite setzen (und dorthin scrollen). (22.10.2026,
   * Prüfbefund) Wer aus einem Blatt hierher geschickt wird („In den Einstellungen ändern“), stand sonst mit dem Fokus
   * auf dem Auslöser im nun versteckten Chat — Tastatur und Vorleser im Leeren. Die Überschrift ist kein Bedienelement:
   * tabindex -1, kein Ring (style.css .einst-gruppe-titel:focus).
   */
  private fokusAufAbschnitt(titel: string): void {
    const h = [...this.body.querySelectorAll<HTMLElement>(".einst-gruppe-titel, .settings-extension-head h3, .konto-guthaben-titel")].find((e) => e.textContent === titel);
    if (!h) return;
    h.tabIndex = -1;
    h.focus();
  }

  // public: app.ts ruft destroy() vor jeder Neu-Instanziierung — sonst
  // leckt der window-Listener (voice-v8:settings-subtab) bei jedem Oeffnen.
  destroy(): void {
    this.zerstoert = true;
    this.deineDaten?.destroy();
    this.deineDaten = null;
    for (const k of this.aufschubSchluessel.values()) vergiss(k);
    this.stopPreview();
    window.removeEventListener("voice-v8:settings-subtab", this.subTabHandler);
    window.removeEventListener("voice-v8:settings-changed", this.aenderungHandler);
    window.removeEventListener(LESEMODUS_EREIGNIS, this.leseHoerer);
    window.removeEventListener("voice-v8:settings-sync", this.syncHandler);
  }

  private renderHeader(): void {
    const header = document.createElement("header");
    header.className = "settings-header";

    // 2026-07-05 (Martin, Nav-Vereinheitlichung): Zurueck-Pfeil links —
    // klappt das Menue wieder aus (wie auf allen anderen Seiten).
    const titleRow = document.createElement("div");
    titleRow.className = "settings-title-row page-head";
    // 23.09.2026: Auf einer Unterseite (Telefon) führt „Zurück" in die
    // Übersicht, sonst wie überall zurück ins Menü.
    titleRow.appendChild(buildBackButton(() => this.zurueck(), { reopenMenu: false }));
    const title = document.createElement("h2");
    title.className = "settings-title hd-title";
    title.textContent = "Einstellungen";
    // Auf einer Unterseite liest sich die Zeile wie „‹ Einstellungen" — ein
    // Tipp auf das Wort führt ebenso zurück wie der Pfeil.
    title.addEventListener("click", () => {
      if (this.root.hasAttribute("data-unterseite")) this.zurueck();
    });
    this.titelEl = title;
    titleRow.appendChild(title);
    header.appendChild(titleRow);
    this.root.appendChild(header);
  }

  /** Am Rechner stehen Übersicht und Seite nebeneinander. */
  private istBreit(): boolean {
    try {
      return window.matchMedia("(min-width: 1024px)").matches;
    } catch {
      return false;
    }
  }

  /**
   * Zurück: von einer Unterseite (Telefon) in die Übersicht, aus der Übersicht
   * dorthin, wo man vorher war (Chat, Zuhause …; ohne Vorgänger Zuhause).
   * Vorher öffnete der Pfeil nur die Menü-Leiste — wo sie schon stand, tat er
   * nichts. (28.10.2026)
   */
  private zurueck(): void {
    if (this.seite && !this.istBreit()) {
      this.zeigeSeite(null);
      return;
    }
    document.dispatchEvent(new CustomEvent("vespera:einstellungen-verlassen"));
  }

  /** Eine Seite öffnen (null = Übersicht) und oben beginnen. */
  zeigeSeite(id: SeitenId | null): void {
    // 20.10.2026: eine Seite, die dieses Konto nicht hat (Erweitert, Entwickler-Team), öffnet auch per Ereignis nicht.
    this.seite = id !== null && !this.seiteSichtbar(id) ? null : id;
    // Der Neuaufbau, der noch auf das Ende des Scrollens wartet, wäre jetzt überholt.
    const neu = this.aufschubSchluessel.get("neu");
    if (neu) vergiss(neu);
    const vor = this.messeVor();
    // Neuer Inhalt, Anfang: kein Sprung für die Scroll-Wache (lib/scroll-ort.ts).
    merkeSeitenwechsel(this.root);
    this.renderActive();
    this.anAnfang();
    this.meldeNeu("seite", vor);
  }

  /**
   * Neuer Inhalt: oben beginnen — beim Wechsel der Seite UND bei jedem Besuch (Konstruktor).
   * (11.10.2026, Ticket 2946)
   *
   * Martin, iPhone, 02.10.: *„Beim Wiedereintritt in die Einstellungen springt das Fenster wild hin und her.“*
   * Gemessen in echtem WebKit (tests/browser/einst-wiedereintritt-webkit.mts; Chromium verhält sich gleich): Die Fläche
   * bleibt beim Verlassen als versteckte Ansicht stehen; der Browser merkt sich ihre Scrollstelle und stellt sie beim
   * nächsten Sichtbarwerden wieder her — auf den ALTEN Inhalt, den der Konstruktor im selben Zug durch die Übersicht
   * ersetzt. Ist die alte Seite länger (Journal 02.10.: Seiten bis 2.336 Punkte, Übersicht 1.528; in der Messung Stelle
   * 1.669 → 680), liegt die Stelle hinter dem neuen Ende, und die Übersicht öffnet sich am Ende der Liste statt oben —
   * ohne dass die App irgendetwas schrieb, darum sah die Journal-Messung nichts. Ob das am iPhone als Zurückfedern
   * sichtbar wird, ist NICHT belegt (kein Gerät); die Zeile `art=einst-ein` (`meldeEintritt`) zeigt es beim nächsten Besuch.
   * Gleiches beim Zahnrad in den Einstellungen (Ansicht bleibt sichtbar, Inhalt wird ersetzt).
   *
   * ⚠️ Das gehört in dieselbe Aufgabe wie das Austauschen: wer den Inhalt der Fläche ersetzt, stellt sie auch auf den
   *    Anfang. Läuft im selben Zug (nichts ist gemalt, niemand scrollt) — kein Schreiben gegen den Finger.
   */
  private anAnfang(): void {
    try {
      this.root.scrollTop = 0;
      this.body.scrollTop = 0;
    } catch {
      /* ohne Layout (Tests) */
    }
  }

  private renderActive(): void {
    // Falls eine Voice-Preview noch läuft (z.B. User wechselt die Seite,
    // während ElevenLabs noch streamt) — sauber abreißen + Blob-URL revoken.
    this.stopPreview();
    /*
     * ⚠️ Geliehene Knoten zuerst zurueckgeben. Sie gehoeren anderen Teilen
     *    der App (die Statusleiste fuettert sie ueber ihre ID); wer sie
     *    einfach mitloescht, macht sie fuer den Rest der Sitzung stumm.
     */
    for (const geliehen of this.body.querySelectorAll<HTMLElement>(
      "[data-geliehen-von]",
    )) {
      const heimat = document.getElementById(geliehen.dataset.geliehenVon ?? "");
      if (heimat) heimat.appendChild(geliehen);
      delete geliehen.dataset.geliehenVon;
    }
    const leisteVorher = this.leistenZustand();
    while (this.body.firstChild) this.body.removeChild(this.body.firstChild);

    const breit = this.istBreit();
    // Am Rechner steht rechts immer eine Seite — ohne Wahl die erste.
    const offen: SeitenId | null = this.seite ?? (breit ? "profil" : null);
    this.body.dataset.seite = offen ?? "uebersicht";
    this.root.toggleAttribute("data-unterseite", this.seite !== null && !breit);

    const rahmen = document.createElement("div");
    rahmen.className = "einst-rahmen";
    rahmen.dataset.ansicht = offen ? "seite" : "uebersicht";
    // Am Telefon entweder die Übersicht oder eine Seite, am Rechner beides.
    const uebersicht = !offen || breit ? this.baueUebersicht(offen) : null;
    if (uebersicht) rahmen.appendChild(uebersicht);
    if (offen) rahmen.appendChild(this.baueSeite(offen));
    this.zeigeWartende();
    this.body.append(this.wartendZeile, rahmen, this.status);
    if (uebersicht && breit) this.leistenZustandZurueck(uebersicht, leisteVorher);
  }

  /*
   * Am Rechner ist die Übersicht die feste Leiste links (Ticket 2570). Sie
   * wird bei jedem Seitenwechsel neu gebaut — ohne diese zwei Schritte
   * sprang sie dabei nach oben, das Suchwort war weg und der Fokus fiel aus
   * der Leiste (wer mit der Tastatur wählte, stand danach im Nichts).
   */
  private leistenZustand(): { oben: number; suche: string; fokus: string | null } | null {
    const alt = this.body.querySelector<HTMLElement>(".einst-uebersicht");
    if (!alt) return null;
    const fokus = document.activeElement;
    return {
      oben: alt.scrollTop,
      suche: alt.querySelector<HTMLInputElement>(".einst-suche-feld")?.value ?? "",
      // Eine Seitenzeile oder ein Treffer der Suche — beide tragen ihren Namen.
      fokus:
        fokus instanceof HTMLElement && alt.contains(fokus) && fokus.matches(".einst-zeile, .einst-fund")
          ? (fokus.dataset.seite ?? fokus.dataset.kennung ?? null)
          : null,
    };
  }

  private leistenZustandZurueck(
    leiste: HTMLElement,
    z: { oben: number; suche: string; fokus: string | null } | null,
  ): void {
    if (!z) return;
    const feld = leiste.querySelector<HTMLInputElement>(".einst-suche-feld");
    if (feld && z.suche) {
      feld.value = z.suche;
      feld.dispatchEvent(new Event("input"));
    }
    try {
      leiste.scrollTop = z.oben;
    } catch {
      /* ohne Layout (Tests) */
    }
    if (z.fokus) {
      leiste
        .querySelector<HTMLElement>(`.einst-zeile[data-seite="${z.fokus}"], .einst-fund[data-kennung="${z.fokus}"]`)
        ?.focus({ preventScroll: true });
    }
  }

  /** Nur in der iPhone-App sinnvoll — im Browser gibt es kein Dauer-Mikro. */
  private seiteSichtbar(id: SeitenId): boolean {
    if (id === "tagesaufnahme") return isNativePlatform();
    // 16.10.2026: das Entwickler-Team gibt es nur im Hauptkonto (shared/entwickler-im-chat.ts).
    if (id === "entwickler") return kontoGruppen().entwickler;
    // 20.10.2026: die Versuche (Erweitert) nur im Hauptkonto (shared/einstellungen-sichtbar.ts).
    if (id === "versuche") return kontoGruppen().versuche;
    return true;
  }

  /**
   * Die Übersicht: Suche, „Gerade", dann je Gruppe eine Karte mit Zeilen.
   * Jede Zeile nennt ihren Stand und öffnet ihre Seite.
   */
  private baueUebersicht(offen: SeitenId | null): HTMLElement {
    const nav = document.createElement("nav");
    nav.className = "einst-uebersicht";
    nav.setAttribute("aria-label", "Einstellungen");
    // Titel und Fuß tragen die Übersicht nur am Rechner, wo sie als zweite Spalte
    // neben der schmalen App-Leiste steht (Ticket 2570, 28.10.2026). Am Telefon blendet das Stylesheet sie aus.
    nav.append(this.baueLeistenTitel());

    const gerade = this.baueGerade();
    const werte = new Map<SeitenId, HTMLElement>();
    const zeilen: HTMLElement[] = [];
    const abschnitte: HTMLElement[] = [];
    let profilBild: HTMLElement | null = null;
    for (const g of EINST_GRUPPEN) {
      const seiten = EINST_SEITEN.filter(
        (x) => x.gruppe === g.id && this.seiteSichtbar(x.id),
      );
      if (seiten.length === 0) continue;
      const abschnitt = document.createElement("section");
      abschnitt.className = "einst-gruppe";
      abschnitt.dataset.gruppe = g.id;
      const titel = document.createElement("h3");
      titel.className = "einst-gruppe-titel";
      titel.textContent = g.titel;
      const karte = document.createElement("div");
      karte.className = "einst-karte";
      for (const seite of seiten) {
        const z = this.baueZeile(seite, offen);
        werte.set(seite.id, z.wert);
        zeilen.push(z.zeile);
        if (z.bild) profilBild = z.bild;
        karte.appendChild(z.zeile);
      }
      abschnitt.append(titel, karte);
      abschnitte.push(abschnitt);
    }

    // Die Suche findet Seiten UND einzelne Einstellungen (settings/einst-suche.ts).
    const uid = (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid;
    // 17.10.2026 (B5): „Was gerade läuft“ — nur Konto 1, direkt unter der Suche (settings/lage-karte.ts).
    const lage = baueLageKarte({ uid, konto: () => teamKontoAus(this.current as unknown as Record<string, unknown>, uid) });
    // 18.10.2026: der Hauptschalter „Entwickler“ ganz oben — nur Konto 1, schreibt nur `entwicklerGesamt` über den gewöhnlichen Speicherweg.
    const schnell = baueEntwicklerSchnellschalter({
      uid,
      werte: () => this.current as unknown as Record<string, unknown>,
      speichere: (felder, satz) => void this.speichere(felder, satz),
    });
    // 18.10.2026 (Server-Netz 1b): „Server“ — nur Konto 1 und nur, wenn der Server das Server-Netz freigibt (settings/server-karte.ts).
    const serverKarte = baueServerKarte({ uid });
    const suche = baueSuche({
      index: suchIndex(kontoGruppen().entwickler),
      werte: () => this.current as unknown as Record<string, unknown>,
      seitenZeilen: zeilen,
      abschnitte,
      gerade,
      weitere: [schnell, lage, serverKarte],
      oeffne: (f) => this.oeffneFund(f),
      schalte: (f, an, zurueck) => this.schalteFund(f, an, zurueck),
    });
    nav.append(...(schnell ? [schnell] : []), suche.feld, ...(lage ? [lage] : []), ...(serverKarte ? [serverKarte] : []), gerade, suche.funde, ...abschnitte, suche.leer, this.baueLeistenFuss());

    this.ladeZeilenWerte(werte, profilBild);
    return nav;
  }

  /** Ein Treffer der Suche: seine Seite öffnen und die Einstellung dort hervorheben. */
  private oeffneFund(f: Fund): void {
    if (!f.seite) return;
    this.zeigeSeite(f.seite);
    hebeHervor(this.root, f.eintrag);
  }

  /**
   * Der Schalter in der Trefferzeile (eine Einstellung ohne Seite, etwa „Getipptes vorlesen“ aus dem
   * Plus-Menü): schreibt, was die Einstellung selbst als Felder nennt — keine zweite Regel.
   */
  private schalteFund(f: Fund, an: boolean, zurueck: () => void): void {
    const satz = `„${f.eintrag.titel}“ ist jetzt ${an ? "an" : "aus"}.`;
    if (f.geraet) {
      f.geraet.schreibe(an);
      this.flashStatus(satz);
      return;
    }
    if (!f.konto) return;
    void this.speichere(f.konto.schreibe(an) as Partial<VoiceV8Settings>, satz).then((r) => {
      if (r.zustand === "fehler") zurueck();
    });
  }

  /**
   * Der Titel oben in der Einstellungsliste. (Ticket 2570; 28.10.2026)
   *
   * Das „← Menü" ist weg: die schmale App-Leiste mit Zuhause und Chat steht
   * am Rechner jetzt auch in den Einstellungen daneben (style.css), und ein
   * Tipp darauf verlässt die Einstellungen. Der Weg zurück ist derselbe
   * Weg wie überall.
   */
  private baueLeistenTitel(): HTMLElement {
    const titel = document.createElement("h2");
    titel.className = "einst-leiste-titel";
    titel.textContent = "Einstellungen";
    return titel;
  }

  /**
   * Unten in der Leiste: wer angemeldet ist und welche Version läuft — wie in
   * der App-Leiste, die sie am Rechner ersetzt. (Ticket 2570)
   *
   * ⚠️ Name und Bild schreibt app.ts in die App-Leiste; hier wird nur
   *    abgelesen, nicht ein zweites Mal beim Server gefragt. Ein zweiter
   *    Weg zu denselben Werten liefe irgendwann auseinander.
   */
  private baueLeistenFuss(): HTMLElement {
    const fuss = document.createElement("div");
    fuss.className = "einst-leiste-fuss";
    const name = document.getElementById("nav-user-name")?.textContent?.trim() ?? "";
    if (name) {
      const karte = document.createElement("div");
      karte.className = "nav-user-card";
      const bild = document.createElement("span");
      bild.className = "nav-user-avatar";
      bild.setAttribute("aria-hidden", "true");
      const vorlage = document.getElementById("nav-user-avatar");
      if (vorlage) {
        for (const k of vorlage.childNodes) bild.appendChild(k.cloneNode(true));
        bild.classList.toggle("has-photo", vorlage.classList.contains("has-photo"));
      }
      const text = document.createElement("span");
      text.className = "nav-user-text";
      const n = document.createElement("span");
      n.className = "nav-user-name";
      n.textContent = name;
      const mail = document.createElement("span");
      mail.className = "nav-user-email";
      mail.textContent = document.getElementById("nav-user-email")?.textContent?.trim() ?? "";
      text.append(n, mail);
      karte.append(bild, text);
      fuss.appendChild(karte);
    }
    const version = document.createElement("div");
    version.className = "nav-version";
    const label = document.createElement("span");
    label.className = "nav-version-label";
    label.textContent = "Version";
    const nummer = document.createElement("span");
    nummer.className = "nav-version-num";
    nummer.textContent = sichtbareVersionJetzt() || "—";
    version.append(label, nummer);
    fuss.appendChild(version);
    return fuss;
  }

  private baueZeile(
    seite: EinstSeite,
    offen: SeitenId | null,
  ): { zeile: HTMLButtonElement; wert: HTMLElement; bild: HTMLElement | null } {
    const zeile = document.createElement("button");
    zeile.type = "button";
    zeile.className = "einst-zeile";
    zeile.dataset.seite = seite.id;
    // 19.10.2026: ohne Anruf im Paket führen „Anruf“-Wörter auf keine vorhandene Einstellung.
    const stichworte = kontoGruppen().anrufModell
      ? seite.stichworte
      : seite.stichworte.replace(/\banruf\w*|\bruft an\b/g, "").replace(/\s+/g, " ").trim();
    zeile.dataset.suche = `${seite.titel} ${stichworte}`.toLowerCase();
    if (offen === seite.id) zeile.setAttribute("aria-current", "page");
    let bild: HTMLElement | null = null;
    if (seite.id === "profil") {
      // Das Profil zeigt den Anfangsbuchstaben statt eines Zeichens.
      bild = document.createElement("span");
      bild.className = "einst-zeile-bild einst-zeile-bild--kreis";
      bild.setAttribute("aria-hidden", "true");
      zeile.appendChild(bild);
    } else if (seite.zeichen) {
      const z = document.createElement("span");
      z.className = "einst-zeile-bild";
      z.innerHTML = zeichen(seite.zeichen, 22);
      zeile.appendChild(z);
    }
    const titel = document.createElement("span");
    titel.className = "einst-zeile-titel";
    titel.textContent = seite.titel;
    const wert = document.createElement("span");
    wert.className = "einst-zeile-wert";
    const pfeil = document.createElement("span");
    pfeil.className = "einst-zeile-pfeil";
    pfeil.innerHTML = zeichen("pfeil-rechts", 16);
    zeile.append(titel, wert, pfeil);
    zeile.addEventListener("click", () => this.zeigeSeite(seite.id));
    return { zeile, wert, bild };
  }

  /** Stimme fürs Vorlesen und fürs Telefonieren — eine, wenn beide gleich. */
  private stimmeWert(): string {
    const c = this.current;
    const anrufId = c.realtimeVoice || "Aoede";
    const anruf =
      GEMINI_REALTIME_VOICES.find((v) => v.id === anrufId)?.label ?? anrufId;
    const leseId = c.readVoiceChosen ? c.voiceId : "";
    const lese = leseId
      ? ([...GEMINI_READ_VOICES, ...VOICE_OPTIONS, ...(c.customVoices ?? [])].find(
          (v) => v.id === leseId,
        )?.label ?? "")
      : "";
    if (!lese || lese === anruf) return anruf;
    // 19.10.2026: ohne Anruf im Paket gibt es nur die Vorlese-Stimme.
    if (!kontoGruppen().anrufStimme) return lese;
    return `${lese} · Anruf ${anruf}`;
  }

  /** „Gemini 3.8 Live" — ohne den Zusatz „— empfohlen". */
  private anrufModellName(): string {
    const m = GEMINI_REALTIME_MODELS.find(
      (x) => x.id === (this.current.realtimeModel ?? ""),
    );
    return (m?.label ?? "").replace(/\s+—.*$/, "");
  }

  /**
   * Die Werte rechts in den Zeilen. Was in den Einstellungen steht, sofort;
   * was der Server weiß, sobald er antwortet. Scheitert eine Abfrage, bleibt
   * die Zeile ohne Wert — sie führt trotzdem auf ihre Seite.
   */
  private ladeZeilenWerte(
    werte: Map<SeitenId, HTMLElement>,
    profilBild: HTMLElement | null,
  ): void {
    const c = this.current;
    const setze = (id: SeitenId, text: string): void => {
      const el = werte.get(id);
      if (el) el.textContent = text;
    };
    const an = (b: boolean): string => (b ? "an" : "aus");
    const kurz = c.starkesModell ?? "opus";
    const tage = c.archivTage ?? 30;
    setze("stimme", this.stimmeWert());
    setze("tagesaufnahme", an(c.alwaysRecord === true));
    setze("verlauf", tage === 0 ? "alles sichtbar" : `${tage} Tage`);
    setze("waechter", an(c.achtsamkeit?.aktiv !== false));
    setze("wissen", an(c.passiveKnowledge === true));
    setze("darstellung", PALETTE_LABELS[getPalette()]);
    // 19.10.2026: die tiefen Auswertungen (Opus/Sonnet, Denktiefe) gibt es nur im Hauptkonto — sonst zeigt die Zeile die Wahl der Antwort-Kette.
    setze(
      "modelle",
      kontoGruppen().tiefeAuswertung
        ? `${claudeName(modellAusKurzform(kurz)) || kurz} · ` +
            DENKSTUFE_NAME[c.tiefeDenkstufe ?? DENKSTUFE_TIEF_VORGABE]
        : c.modellBevorzugt === "gemini"
          ? "Gemini"
          : "Auto",
    );

    const json = async <T,>(url: string): Promise<T | null> => {
      try {
        const r = await fetch(url, { credentials: "same-origin" });
        return r.ok ? ((await r.json()) as T) : null;
      } catch {
        return null;
      }
    };
    void json<{ profile?: { firstName?: string | null; lastName?: string | null } }>(
      "/api/user/profile",
    ).then((d) => {
      const vor = d?.profile?.firstName?.trim() ?? "";
      const nach = d?.profile?.lastName?.trim() ?? "";
      setze("profil", `${vor} ${nach}`.trim());
      if (profilBild) profilBild.textContent = vor.charAt(0).toUpperCase();
    });
    void import("./persona-api.js")
      .then((m) => m.fetchPersona())
      .then((p) => setze("persoenlichkeit", p.botName))
      .catch(() => undefined);
    void json<unknown[] | { entries?: unknown[] }>("/api/vocabulary").then((d) => {
      const liste = Array.isArray(d) ? d : d?.entries;
      if (Array.isArray(liste)) setze("woerter", String(liste.length));
    });
    void json<{ hasVoiceprint?: boolean }>("/api/user/voiceprint").then((d) => {
      if (d) setze("stimmprobe", d.hasVoiceprint ? "vorhanden" : "fehlt");
    });
    void json<{ themes?: string[] }>("/api/user/focus-themes").then((d) => {
      const n = d?.themes?.length;
      if (typeof n === "number") setze("schwerpunkte", n === 1 ? "1 Thema" : `${n} Themen`);
    });
    // Verbindungen: was wirklich verbunden ist, in einem Wort je Gerät.
    const uid = (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid;
    // 17.10.2026 (B6): der Stand der Entwickler in einer Zeile — nur Konto 1, nur Booleans vom Server (gleiche Quelle wie die Seite).
    // 17.10.2026 („Deine Daten“): wann der letzte Export war — einmal beim Öffnen, kein Takt.
    if (uid !== undefined && uid !== null) void datenApi.stand().then((st) => setze("deinedaten", letzterExportText(st))).catch(() => undefined);
    if (uid === 1) void holeTeamStand().then((stand) => setze("entwickler", teamKurzText(stand, teamKontoAus(this.current as unknown as Record<string, unknown>, uid))));
    void Promise.all([
      fetchConnectors().catch(() => null),
      json<unknown[] | { feeds?: unknown[] }>("/api/ical-feeds"),
      uid === 1 ? fetchDaemonStatus().catch(() => null) : Promise.resolve(null),
      uid === 1 ? fetchExtensionStatus().catch(() => null) : Promise.resolve(null),
      json<{ verbunden?: boolean }>("/api/panel/verbindung"),
    ]).then(([konn, feeds, mac, browser, panel]) => {
      const feedListe = Array.isArray(feeds) ? feeds : feeds?.feeds;
      const teile: string[] = [];
      if (konn?.google?.connected) teile.push("Google");
      if (panel?.verbunden) teile.push("Panel");
      if (Array.isArray(feedListe) && feedListe.length > 0) teile.push("Kalender");
      if (mac?.connected) teile.push("Mac");
      if (browser?.connected) teile.push("Browser");
      setze("verbindungen", teile.length ? teile.join(", ") : "keine");
    });
  }

  /**
   * „Gerade": was in diesem Moment läuft — oben in der Übersicht, damit man
   * nicht erst eine Seite öffnen muss, um zu sehen, ob Vespera zuhört.
   */
  private baueGerade(): HTMLElement {
    const abschnitt = document.createElement("section");
    abschnitt.className = "einst-gerade";
    const titel = document.createElement("h3");
    titel.className = "einst-gruppe-titel";
    titel.textContent = "Gerade";
    const karte = document.createElement("div");
    karte.className = "einst-gerade-karte";
    abschnitt.append(titel, karte);

    const zeile = (
      name: string,
      ziel: SeitenId,
    ): { punkt: HTMLElement; wert: HTMLElement } => {
      const z = document.createElement("button");
      z.type = "button";
      z.className = "einst-gerade-zeile";
      z.dataset.ziel = ziel;
      const punkt = document.createElement("span");
      punkt.className = "einst-punkt";
      punkt.setAttribute("aria-hidden", "true");
      const t = document.createElement("span");
      t.className = "einst-gerade-titel";
      t.textContent = name;
      const wert = document.createElement("span");
      wert.className = "einst-gerade-wert";
      z.append(punkt, t, wert);
      z.addEventListener("click", () => this.zeigeSeite(ziel));
      karte.appendChild(z);
      return { punkt, wert };
    };
    const setze = (
      z: { punkt: HTMLElement; wert: HTMLElement },
      art: string,
      text: string,
    ): void => {
      z.punkt.dataset.art = art;
      z.wert.dataset.art = art;
      z.wert.textContent = text;
    };

    if (isNativePlatform()) {
      const hey = zeile("„Hey Vespera“", "sprechen");
      const zeige = (): void => {
        if (this.current.wachwort !== true) {
          setze(hey, "aus", "aus");
          return;
        }
        void import("./wachwort-zustand.js").then((wz) => {
          const z = wz.wachwortZustand();
          if (!z) {
            setze(hey, "wartet", "an");
            return;
          }
          const art = wz.wachwortArt(z.zeile);
          const text: Record<string, string> = {
            lauscht: "hört zu",
            wartet: "wartet",
            aus: "aus",
            stoerung: "Störung",
          };
          setze(hey, art, text[art] ?? art);
        });
      };
      zeige();
      const hoerer = gesteuert(
        "live",
        (): void => {
          if (!hey.wert.isConnected) {
            window.removeEventListener("voice-v8:wachwort-zustand", hoerer);
            return;
          }
          zeige();
        },
        true,
      );
      window.addEventListener("voice-v8:wachwort-zustand", hoerer);

      const tag = zeile("Tagesaufnahme", "tagesaufnahme");
      setze(tag, "aus", this.current.alwaysRecord === true ? "an" : "aus");
      void import("./native-recorder.js").then((m) => {
        if (m.getRecorderState() === "recording") setze(tag, "lauscht", "läuft");
        else if (this.current.alwaysRecord === true) setze(tag, "wartet", "an");
      });
    }
    if (kontoGruppen().anrufModell) setze(zeile("Anruf", "sprechen"), "ruhig", this.anrufModellName());
    // 19.10.2026: nichts, was gerade läuft (kein Live, nicht nativ) — keine leere Karte (die Suche hält sie dann auch verborgen).
    if (!karte.hasChildNodes()) {
      abschnitt.hidden = true;
      abschnitt.dataset.leer = "1";
    }
    return abschnitt;
  }

  /** Eine Seite: großer Titel, darunter ihre Karten. */
  private baueSeite(id: SeitenId): HTMLElement {
    const inhalt = document.createElement("div");
    inhalt.className = "einst-inhalt";
    inhalt.dataset.seite = id;
    const titel = document.createElement("h2");
    titel.className = "einst-seite-titel";
    titel.textContent = EINST_SEITEN.find((x) => x.id === id)?.titel ?? "Einstellungen";
    inhalt.appendChild(titel);
    for (const teil of this.seitenInhalt(id)) inhalt.appendChild(teil);
    // Ticket 2715: trägt die erste Karte denselben Titel wie die Seite
    // („Verlauf" über „Verlauf"), blendet ihn die Rechner-Ansicht aus
    // (style.css, [data-wie-seite]). Am Telefon bleibt er stehen.
    const erste = inhalt.children[1]?.querySelector(":scope > h3");
    if (erste && erste.textContent?.trim() === titel.textContent) {
      erste.setAttribute("data-wie-seite", "");
    }
    return inhalt;
  }

  /*
   * ⚠️ JEDE Karte hat hier genau EINEN Platz. Am 22.09. (pre.3163) fiel beim
   *    Umordnen „Stimmung aus der Stimme" durch — eine Zustimmung, die man
   *    danach nicht mehr zurücknehmen konnte. Ein Test prüft seither, dass
   *    jeder Kartenbauer dieser Datei irgendwo aufgerufen wird.
   */
  private seitenInhalt(id: SeitenId): HTMLElement[] {
    const uid = (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid;
    switch (id) {
      case "profil":
        return [this.profileBlock()];
      case "persoenlichkeit":
        // Ticket 2550: „Innen und außen" direkt unter der Persönlichkeit, in
        // der die innere Ausrichtung steht — die beiden gehören zusammen.
        return [
          this.personaBlock(),
          this.selbstermaechtigungToggle(),
          this.hdPersonaToggle(),
          this.emojisInRepliesToggle(),
        ];
      case "sprechen":
        return this.buildSprechenSeite();
      case "stimme":
        // 11.10.2026 (Ticket 3077): „Musik ohne Probestück“ unter der Stimme — Klang gehört hierher.
        // 15.10.2026: „Vorlesen“ (Lautsprecher, Getipptes) gleich nach der Stimme — bis dahin nur im Plus-Menü.
        // 16.10.2026: die Karte „Entwicklung / Test“ ist aufgeteilt — Versuche auf „Erweitert“, das Team auf „Entwickler-Team“.
        return [this.voiceSection(), this.vorlesenKarte(), this.musikOhneProbeKarte()];
      case "woerter":
        return baueWoerterSeite({
          // Mit der Stimme, die auch vorliest — sonst hört man eine andere.
          vorschauStimme: () => chosenReadVoice() ?? `de-DE-Chirp3-HD-${this.current.realtimeVoice || "Aoede"}`,
          spieleVorschau: (stimme, knopf, text) => this.playReadVoicePreview(stimme, knopf, undefined, text),
        });
      case "tagesaufnahme":
        return [this.buildAlwaysRecordCard()];
      case "stimmprobe":
        // Beides ist, was Vespera aus deiner Stimme hört: wer du bist, und
        // wie es dir geht.
        return [this.buildVoiceprintCard(), this.stimmungAusStimmeToggle()];
      case "verlauf":
        return [this.buildVerlaufCard()];
      case "deinedaten":
        return this.deineDatenSeite();
      case "schwerpunkte":
        return [this.focusThemesBlock()];
      case "waechter":
        return [this.achtsamkeitBlock()];
      case "wissen":
        return [this.passiveKnowledgeToggle()];
      case "mitteilungen":
        // Ticket 2728: „Vespera darf mich anrufen“ — nur das Hauptkonto.
        return [this.pushBlock(), this.sperrbildschirmKarte(), ...(kontoGruppen().anrufeVonVespera ? [this.anrufErlaubtToggle()] : [])];
      case "darstellung":
        // 06.10.2026 (Morse-Modul): „Morse im Menü“ — nur Konto 1 (shared/morse-konto.ts).
        // 07.10.2026: „Jahreszeit der Szene“ — gleich unter der Gestalt von Zuhause.
        return [
          this.appearanceBlock(),
          this.szeneJahreszeitKarte(),
          ...(morseFuerKonto(uid) ? [this.morseMenueKarte(uid)] : []),
        ];
      case "verbindungen": {
        // 06.10.2026 (Martin: „die Seite gehört etwas zusammengeräumt … Kalenderfeeds, iCal gehört überhaupt ganz
        // runter … und die anderen eher oben“): das Tägliche zuerst, die Dienste (Google, WHOOP, Panel) danach,
        // die Kalender-Feeds ganz unten und eingeklappt.
        const ical = einklappbar("Kalender-Feeds (iCal)", this.buildIcalFeedsCard(), true);
        // 27.10.2026 (Martin: „leichter nutzbar, wie bei anderen Plattformen“): oben Konto und Guthaben mit dem
        // Knopf zum Nachladen. Danach das Tägliche; Browser-Erweiterung und Standort sind selten und stehen
        // zusammen in einer zugeklappten Zeile. Nichts davon ist weggefallen.
        const selten = (): HTMLElement => {
          const d = document.createElement("div");
          d.className = "settings-verbindungen-selten";
          d.append(this.extensionBlock(), this.locationBlock());
          return d;
        };
        return uid === 1
          ? [
              createKontoGuthabenKarte(),
              this.daemonBlock(),
              // Die Bildschirm-Hilfe setzt eine verbundene Desktop-Instanz
              // voraus — sie gehört direkt darunter.
              this.coachBlock(),
              // 29.09.2026 (Ticket 2819): Telegram als Kanal von Vespera.
              createTelegramKarte(),
              this.connectorsBlock(),
              einklappbar("Browser-Erweiterung und Standort", selten()),
              ical,
            ]
          : [createKontoGuthabenKarte(), this.connectorsBlock(), ical];
      }
      case "modelle":
        // 25.10.2026: „Modelle“ und „Konto und Version“ in EINER Seite — erst womit Vespera antwortet, dann Modellwahl, dann Konto.
        // 16.10.2026: Bildrate und Ruckel-Test stehen seither auf „Erweitert“ (Versuche).
        return [
          createModellZugangKarte(),
          createModellKetteCard(),
          ...(kontoGruppen().tiefeAuswertung ? [this.buildStarkesModellCard()] : []),
          this.accountBlock(),
          this.versionBlock(),
          this.zuruecksetzenBlock(),
          this.logoutBlock(),
        ];
      case "entwickler":
        return this.entwicklerSeite();
      case "versuche":
        return this.versucheSeite();
    }
  }

  /** Alles auf Standard — hart, deshalb mit Rückfrage und ganz unten. */
  private zuruecksetzenBlock(): HTMLElement {
    const actions = document.createElement("div");
    actions.className = "settings-actions";
    const reset = document.createElement("button");
    reset.type = "button";
    reset.className = "settings-reset";
    reset.textContent = "auf Standard zurücksetzen";
    reset.addEventListener("click", () => {
      const sicher = window.confirm(
        "Wirklich ALLE Einstellungen auf Standard zurücksetzen?\n\n" +
          "Das betrifft unter anderem Stimme, Erzählerstimme, Human Design, " +
          "passives Wissen, Emojis und „Immer aufzeichnen“. " +
          "Rückgängig machen geht nicht.",
      );
      if (!sicher) return;
      this.resetToDefaults(this.root);
    });
    actions.appendChild(reset);
    return actions;
  }

  /**
   * „Morse im Menü“ (06.10.2026, Morse-Modul). Nur Konto 1, Vorgabe an; ob er steht und was er zeigt,
   * rechnet shared/morse-konto.ts — dieselbe Regel wie das Menü. Ausblenden löscht nichts: der Fortschritt
   * liegt am Konto (server/morse-routen.ts) und ist beim Wiedereinblenden da.
   */
  private morseMenueKarte(uid: number | undefined): HTMLElement {
    const lernen = einstAbschnitt("Lernen");
    lernen.karte.appendChild(
      einstSchalter(
        "Morse im Menü",
        "Die Lernseite für Morse-Code steht unter „Täglich“.",
        morseAn(this.current.morseLernen, uid),
        (an) => {
          void this.speichere(
            { morseLernen: an },
            an ? "Morse steht wieder im Menü." : "Morse ist aus dem Menü. Dein Fortschritt bleibt gespeichert.",
          );
        },
        "morseLernen",
      ).zeile,
    );
    return lernen.abschnitt;
  }

  /**
   * „Vorlesen“: der Lautsprecher dieses Geräts und „Getipptes vorlesen“. (15.10.2026)
   *
   * Martin fand „Getipptes vorlesen“ nicht — es lebte nur im Plus-Menü des Chats. Beide Zeilen lesen
   * und schreiben DIESELBEN Werte wie das Plus-Menü: der Lautsprecher über lib/lautsprecher.ts (ein
   * Gerätewert, darum die Marke), „Getipptes“ als Kontowert `tippenVorlesen`. Stellt man es hier um,
   * zeigt es das Plus-Menü — app.ts hört auf das Lautsprecher-Ereignis und auf settings-changed.
   */
  private vorlesenKarte(): HTMLElement {
    const vorlesen = einstAbschnitt("Vorlesen");
    vorlesen.karte.appendChild(
      einstSchalter(
        "Antworten vorlesen",
        "Vespera spricht ihre Antworten. Aus: stumm, nur Text.",
        loadAutoPlay(),
        (an) => {
          saveAutoPlay(an);
          this.flashStatus(an ? "Antworten werden vorgelesen." : "Der Lautsprecher ist stumm — Antworten nur als Text.");
        },
        "lautsprecher",
      ).zeile,
    );
    vorlesen.karte.appendChild(
      einstSchalter(
        "Getipptes vorlesen",
        "Auch Antworten auf getippte Fragen laut. Aus: Getipptes bleibt Text. Wirkt nur, wenn „Antworten vorlesen“ an ist.",
        this.current.tippenVorlesen === true,
        (an) => {
          void this.speichere(
            { tippenVorlesen: an },
            an ? "Getippte Fragen werden laut beantwortet." : "Getippte Fragen bleiben Text.",
          );
        },
        "tippenVorlesen",
      ).zeile,
    );
    // 20.10.2026: Lesemodus für E-Mails — je Konto und Gerät (lib/lesemodus.ts), derselbe Schalter wie an der Mail-Karte.
    const lese = einstSchalter(
        "Lesemodus für E-Mails",
        "Mails in großer Schrift mit starkem Kontrast, aufgeklappt, und ohne dass Vespera sie von selbst vorliest. Vorlesen bleibt per Knopf möglich.",
        lesemodusAn(),
        (an) => {
          setzeLesemodus(an);
          this.flashStatus(an ? "Lesemodus an — Mails als Text." : "Lesemodus aus.");
        },
        "lesemodus",
      );
    vorlesen.karte.appendChild(lese.zeile);
    // Der Knopf an der Mail-Karte stellt denselben Schalter um: hier nachziehen. EIN Hörer je Ansicht; destroy() und jeder Neuaufbau melden ihn ab.
    window.removeEventListener(LESEMODUS_EREIGNIS, this.leseHoerer);
    this.leseHoerer = (): void => lese.schalter.setAttribute("aria-checked", String(lesemodusAn()));
    window.addEventListener(LESEMODUS_EREIGNIS, this.leseHoerer);
    return vorlesen.abschnitt;
  }

  /**
   * „Musik ohne Probestück“. (11.10.2026, Ticket 3077)
   *
   * Martin sagte zweimal, er wolle keine Probestücke mehr — die Vorschlagskarte bot trotzdem eins an. Der Schalter hält
   * es fest: an, und ein Lied entsteht nach seinem Ja gleich ganz (kein 30-Sekunden-Probestück davor). Das Ja bleibt, und
   * das Lied zählt ins Kontingent — der Satz sagt es. Dieselbe Einstellung stellt Vespera auf Zuruf um
   * (shared/einstellung-aendern.ts); die Regel steht in shared/musik-ohne-probe.ts.
   */
  private musikOhneProbeKarte(): HTMLElement {
    const musik = einstAbschnitt("Musik");
    musik.karte.appendChild(
      einstSchalter(
        "Musik ohne Probestück",
        "Ein Lied entsteht nach deinem Ja gleich ganz, ohne 30-Sekunden-Probestück davor. Es zählt ins Kontingent.",
        musikOhneProbeAn(this.current.musikOhneProbe),
        (an) => {
          void this.speichere(
            { musikOhneProbe: an },
            an ? "Lieder entstehen jetzt ohne Probestück." : "Vor einem Lied gibt es wieder ein Probestück.",
          );
        },
        MUSIK_OHNE_PROBE_FELD,
      ).zeile,
    );
    return musik.abschnitt;
  }

  /**
   * „Antworten im Hintergrund vorlesen (Test)“. (13.10.2026)
   *
   * Nur für den iPhone-Test des nativen Vorlesers: Vorgabe aus, je Konto. Greift erst, wenn die Seite verborgen ist
   * (frontend/lib/vorlesen-nativ.ts). Vespera stellt ihn auch auf Zuruf um (shared/einstellung-aendern.ts).
   * 16.10.2026: steht seither auf der Seite „Erweitert“ (Versuche); die Schalter des Entwickler-Teams haben ihre eigene Seite
   * (settings/entwickler-seite.ts).
   */
  private vorleserNativKarte(): HTMLElement {
    const test = einstAbschnitt("Vorlesen im Hintergrund");
    test.karte.appendChild(
      einstSchalter(
        "Antworten im Hintergrund vorlesen (Test)",
        "Nur für den iPhone-Test: liest Antworten vor, auch wenn eine andere App (z. B. Navigation) vorn ist.",
        vorleserNativAn(this.current.vorleserNativ),
        (an) => {
          void this.speichere(
            { vorleserNativ: an },
            an ? "Antworten werden im Hintergrund vorgelesen (Test)." : "Antworten werden im Hintergrund nicht mehr nativ vorgelesen.",
          );
        },
        VORLESER_NATIV_FELD,
      ).zeile,
    );
    test.karte.appendChild(
      einstSchalter(
        "Mikro beim Vorlesen offen halten (Test)",
        "Kann das Vorlesen mitten im Satz abbrechen, bis die nächste App-Version das behebt.",
        this.current.mikroOffenBeimVorlesen === true,
        (an) => {
          void this.speichere(
            { mikroOffenBeimVorlesen: an },
            an ? "Das Mikro bleibt beim Vorlesen offen (Test)." : "Das Mikro wird beim Vorlesen wieder geschlossen.",
          );
          void mikroBeimVorlesenAnwenden(an);
        },
        "mikroOffenBeimVorlesen",
      ).zeile,
    );
    return test.abschnitt;
  }

  /**
   * Die Seite „Erweitert“ — die Versuche. (16.10.2026, Einstellungen neu ordnen B3)
   *
   * Was noch geprüft wird, stand verstreut: unter „Stimme und Vorlesen“ (Vorlesen sofort, Schneller Ton, Echtzeit-Test,
   * Hintergrund-Vorlesen) und unter „Modelle und Konto“ (Bildrate, Ruckel-Test). Jetzt an einem Ort, jeder Block eingeklappt
   * und als „Versuch“ markiert. Die Karten sind dieselben; nur Konto 1 sieht, was schon vorher nur Konto 1 sah.
   */
  private versucheSeite(): HTMLElement[] {
    const uid = (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid;
    const satz = document.createElement("p");
    satz.className = "einst-seite-satz";
    satz.textContent = "Versuche, die noch geprüft werden. Aufklappen, ausprobieren, Bescheid geben — sie können sich noch ändern.";
    const versuch = (titel: string, inhalt: HTMLElement): HTMLElement => einklappbar(titel, inhalt, true, "Versuch");
    // 17.10.2026: Selbstverbesserung Phase 1 (settings/selbstverbesserung-karte.ts) — offen, nicht eingeklappt; nur Konto 1.
    const selbstv = baueSelbstverbesserungKarte({
      uid,
      werte: () => this.current as unknown as Record<string, unknown>,
      speichere: (felder, s) => void this.speichere(felder, s),
    });
    return [
      satz,
      ...[selbstv].filter((k): k is HTMLElement => k !== null),
      versuch("Vorlesen im Hintergrund", this.vorleserNativKarte()),
      ...(uid === 1
        ? [
            versuch("Vorlesen sofort", this.direktStreamBlock()),
            // Ticket 2919 (02.10.2026): drei Schalter für den schnelleren Soniox-Weg, alle AUS, nur Konto 1.
            versuch("Schneller Ton", this.schnellerWegBlock()),
            // 07.10.2026 (Bahn eleven-echtzeit): Soniox gegen ElevenLabs v4 Turbo, nur Konto 1 (der Server weist alle anderen ab).
            versuch("Echtzeit-Test", echtzeitTestKarte()),
            // 17.10.2026 (Jarvis): Martins blinde Hörprobe der Sofort-Sätze (frontend/lib/reflex-hoerprobe-karte.ts), nur Konto 1.
            versuch("Hörprobe: Sofort-Sätze", reflexHoerprobeKarte()),
          ]
        : []),
      versuch("Bildrate beim Scrollen", bildrateKarte()),
      ...ruckelTestKarten().map((k) => versuch("Ruckel-Test", k)),
    ];
  }

  /** Die Seite „Deine Daten“ (17.10.2026, settings/deine-daten-seite.ts) — die Routen des Servers stehen in daten-api.ts. */
  private deineDatenSeite(): HTMLElement[] {
    this.deineDaten?.destroy();
    const seite = baueDeineDatenSeite({
      uid: (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid,
      api: datenApi,
      nativ: isNativePlatform(),
      verlaufKuerzen: () => (window as unknown as { __v8VerlaufArchivieren?: () => Promise<void> }).__v8VerlaufArchivieren?.(),
    });
    this.deineDaten = seite;
    return seite.elemente;
  }

  /** Die Seite „Entwickler-Team“ (16.10.2026, settings/entwickler-seite.ts) — dieselben Kontowerte, derselbe Speicherweg. */
  private entwicklerSeite(): HTMLElement[] {
    return baueEntwicklerSeite({
      werte: () => this.current as unknown as Record<string, unknown>,
      uid: (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid,
      speichere: (felder, satz) => void this.speichere(felder, satz),
      spieleProbe: (stimme, tempo, zeige) => void this.spieleEntwicklerProbe(stimme, tempo, zeige),
      stoppeProbe: () => this.stopPreview(),
    });
  }

  /** Die Hörprobe einer Entwickler-Stimme: Soniox, mit SEINEM Tempo (nicht Vesperas Regler). Nie stumm: ein Fehler steht in der Zeile. */
  private async spieleEntwicklerProbe(stimme: string, tempo: number, zeige: (z: "idle" | "loading" | "playing") => void): Promise<void> {
    this.stopPreview();
    zeige("loading");
    try {
      const res = await fetch("/api/voice-preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          voiceId: stimme,
          ttsProvider: "soniox",
          speakingRate: tempo,
          text: "Hallo Martin, so klinge ich, wenn ich vorgelesen werde.",
        }),
      });
      if (!res.ok) {
        zeige("idle");
        this.flashStatus("Soniox antwortet gerade nicht — die Probe kann nicht spielen.", "fehler");
        return;
      }
      const url = URL.createObjectURL(await res.blob());
      const audio = new Audio(url);
      this.previewAudio = audio;
      this.previewBlobUrl = url;
      const fertig = (): void => {
        zeige("idle");
        URL.revokeObjectURL(url);
        if (this.previewBlobUrl === url) this.previewBlobUrl = null;
        if (this.previewAudio === audio) this.previewAudio = null;
      };
      audio.addEventListener("ended", fertig);
      audio.addEventListener("error", fertig);
      zeige("playing");
      await audio.play();
    } catch {
      zeige("idle");
      this.flashStatus("Die Probe kann gerade nicht spielen — bitte gleich noch einmal.", "fehler");
    }
  }

  /**
   * „Antwortvorschau auf dem Sperrbildschirm“ (14.10.2026, Build 110) — eine Mitteilung, darum hier
   * (15.10.2026: von der Test-Karte unter „Stimme“ zu den Mitteilungen gerückt). Zeigt Text Fremden,
   * deshalb nur in den Einstellungen wählbar (shared/einstellung-aendern.ts, „datenschutz“).
   */
  private sperrbildschirmKarte(): HTMLElement {
    const sperr = einstAbschnitt("Sperrbildschirm");
    sperr.karte.appendChild(
      einstSchalter(
        "Antwortvorschau auf dem Sperrbildschirm",
        "Die Mitteilung „Vespera hat geantwortet“ zeigt die ersten Worte der Antwort. Aus: nur „Antwort bereit“.",
        this.current.antwortVorschauSperrbildschirm === true,
        (an) => {
          void this.speichere(
            { antwortVorschauSperrbildschirm: an },
            an ? "Die Mitteilung zeigt die ersten Worte der Antwort." : "Die Mitteilung zeigt nur „Antwort bereit“.",
          );
        },
        "antwortVorschauSperrbildschirm",
      ).zeile,
    );
    return sperr.abschnitt;
  }

  /**
   * Die Jahreszeit der Szene über Zuhause. (07.10.2026)
   *
   * Martin: *„Bitte gleich in der Entwicklung auch noch den Herbst, den
   * Winter und den Frühling mit einbauen, sodass wir dann wechseln können,
   * je nach Jahreszeit."* Vorgabe ist „Automatisch" — nach dem Kalender
   * (shared/jahreszeit.ts); die Zeile sagt, welche das gerade ist. Eine
   * feste Wahl gilt, bis man sie zurückstellt. Das Auswahlfeld des Systems
   * (am iPhone das Rad von unten), gestapelt wie „Auslösewort": nebeneinander
   * blieben am Telefon für Name und Satz keine 100 Punkte (Bild, 390 breit).
   */
  private szeneJahreszeitKarte(): HTMLElement {
    const szene = einstAbschnitt("Szene auf Zuhause");
    const zeile = document.createElement("label");
    zeile.className = "einst-feld einst-feld--gestapelt";
    zeile.dataset.feld = "szeneJahreszeit";
    zeile.appendChild(
      einstText("Jahreszeit der Szene", "Das Bild über Zuhause — von selbst nach dem Kalender oder fest gewählt."),
    );
    const wahl = document.createElement("select");
    wahl.className = "einst-eingabe";
    const jetzt = JAHRESZEIT_NAME[jahreszeitAmDatum(new Date())];
    for (const w of JAHRESZEIT_WAHLEN) {
      const o = document.createElement("option");
      o.value = w;
      o.textContent = w === "auto" ? `Automatisch (jetzt: ${jetzt})` : JAHRESZEIT_NAME[w];
      wahl.appendChild(o);
    }
    wahl.value = this.current.szeneJahreszeit ?? JAHRESZEIT_VORGABE;
    wahl.addEventListener("change", () => {
      const w = wahl.value;
      if (!istJahreszeitWahl(w)) return;
      void this.speichere(
        { szeneJahreszeit: w },
        w === "auto"
          ? `Die Szene folgt wieder dem Kalender — jetzt ${jetzt}.`
          : `Die Szene zeigt jetzt den ${JAHRESZEIT_NAME[w]}.`,
      );
    });
    zeile.appendChild(wahl);
    szene.karte.appendChild(zeile);
    return szene.abschnitt;
  }

  /**
   * Sprechtaste und „Hey Vespera". (23.09.2026, Entwurf freigegeben)
   *
   * Drei Fragen, drei Abschnitte: wann das Mikro aufgeht, wann es von selbst
   * abschickt, und womit im Anruf gesprochen wird. Jede Einstellung hat EINEN
   * kurzen Satz; die lange Erklärung klappt auf.
   *
   * ⚠️ Die Stille beginnt bei 3 Sekunden, nicht bei 2 wie im Entwurf: eine
   *    knappe Stille schneidet beim Nachdenken das Wort ab — so stand es
   *    schon seit Ticket 2420 im Hinweis, und niemand hatte bisher 2 gewählt.
   */
  private buildSprechenSeite(): HTMLElement[] {
    const mikro = einstAbschnitt("Mikro öffnen");
    /*
     * Das Wach-Wort. (22.09.2026, Ticket 2503)
     *
     * ⚠️ Nur in der nativen Huelle sichtbar. Ein Browser-Tab bekommt im
     *    Hintergrund kein Mikrofon — ein Schalter, der dort nichts tut,
     *    waere ein Versprechen, das die Seite nicht halten kann. Lieber
     *    gar nicht anbieten als still nichts tun.
     */
    if (isNativePlatform()) {
      const block = document.createElement("div");
      block.className = "einst-feld-block";
      const wach = einstSchalter(
        "Auf „Hey Vespera“ hören",
        "Das Mikro geht auf, ohne dass du drückst.",
        this.current.wachwort === true,
        (an, zuruecksetzen) => {
          void this.speichere({ wachwort: an });
          void import("./native-voice.js").then(async (nv) => {
            const wirklich = await nv.wachwortSetzen(an);
            // Das ruhende Mikro gehoert dann dem Lauscher (siehe ptt.ts).
            void import("./ptt.js").then((p) => p.setzeWachwortVorrang(wirklich));
            /*
             * ⚠️ Die Rueckmeldung sagt, was WIRKLICH gilt — nicht, was
             *    gewuenscht war. Fehlt die Erlaubnis fuer Mikro oder
             *    Spracherkennung, bleibt es aus, und der Schalter darf das
             *    nicht verschweigen.
             */
            if (an && !wirklich) {
              zuruecksetzen();
              void this.speichere({ wachwort: false });
              this.flashStatus(
                "Dafür fehlt die Erlaubnis für Mikrofon oder Spracherkennung — " +
                  "in den iPhone-Einstellungen bei Vespera freigeben.",
              );
              return;
            }
            this.flashStatus(
              an ? "Vespera hört auf ihren Namen." : "Das Wach-Wort ist aus.",
            );
          });
        },
        "wachwort",
      );
      block.append(wach.zeile, this.wachwortZustandZeile());
      mikro.karte.appendChild(block);
      /*
       * Ticket 2624 (Martin, 25.09.): *„Wir könnten den Stimmabdruckvergleich
       * vorbereiten und in den Einstellungen einstellbar machen, aber vorerst
       * muss er nicht aktiv sein, damit wir wirklich schnelle Ergebnisse
       * haben. Es könnte ein gutes Sicherungselement sein.“*
       *
       * ⚠️ DIREKT unter dem Wach-Wort-Schalter und wie er nur in der nativen
       *    Hülle: „Hey Vespera“ hört nur dort. Vorgabe AUS. Für jedes Konto —
       *    der Server liest den Wert selbst (server/wachwort-stimme.ts) und
       *    braucht dafür keinen Jev.
       * ⚠️ Er hängt vom Wach-Wort-Schalter darüber ab (ohne „Hey Vespera“
       *    gibt es nichts zu prüfen). Ein Muster für abhängige Schalter gibt
       *    es in den Einstellungen nicht — die Nachbarn sind alle unabhängig —,
       *    darum sagt es der Satz am Schalter und die Meldung beim Einschalten
       *    (wirkt erst, wenn „Auf Hey Vespera hören“ an ist), statt ihn
       *    auszublenden: ein Schalter, der beim Wach-Wort-Wechsel verschwindet,
       *    verliert seinen Zustand aus dem Blick.
       * ⚠️ Ehrlich, was er tut: verworfen wird nur, wenn Abdruck UND Klang
       *    zusammen eindeutig eine andere Stimme sagen; eine fremde Stimme, die
       *    deiner ähnelt, geht durch. Ein Schutz gegen versehentliche Stimmen
       *    im Raum, kein Sicherheitsmerkmal.
       */
      mikro.karte.append(
        einstSchalter(
          "Hey Vespera nur mit meiner Stimme",
          "Übergeht eine eindeutig fremde Stimme im Raum — nur mit „Auf Hey Vespera hören“ an.",
          this.current.wachwortNurMeineStimme === true,
          (an) => {
            void this.speichere(
              { wachwortNurMeineStimme: an },
              !an
                ? "Hey Vespera reagiert wieder auf jede Stimme."
                : this.current.wachwort === true
                  ? "Hey Vespera prüft jetzt die Stimme — eine eindeutig fremde wird übergangen."
                  : "Gespeichert — wirkt, sobald „Auf Hey Vespera hören“ an ist.",
            );
          },
          "wachwortNurMeineStimme",
        ).zeile,
        einstMehr(
          "Wie genau prüft das die Stimme?",
          "Nach „Hey Vespera“ vergleicht der Server die Stimme mit deiner Stimmprobe und hört " +
            "sich den Klang an. Verworfen wird nur, wenn beides zusammen eindeutig eine andere " +
            "Stimme sagt — ein anderes Geschlecht oder mehrere Stimmen. Dann antwortet Vespera " +
            "nicht und legt nichts davon ab, weder im Gedächtnis noch im Stimmverlauf. Eine " +
            "fremde Stimme, die deiner ähnelt, geht durch, ebenso alles, bei dem der Abgleich " +
            "fehlt, unklar ist oder zu spät kommt. Das ist ein Schutz vor versehentlichen " +
            "Stimmen im Raum, kein Sicherheitsmerkmal. Es gilt für den Satz direkt nach „Hey " +
            "Vespera“ und für die Aufnahme, die das Wach-Wort öffnet — nie für die " +
            "Sprechtaste. Ist der Schalter an, kommt die Antwort auf „Hey Vespera“ manchmal " +
            "einen Moment später, im ungünstigsten Fall bis zu 3 Sekunden nach dem Hochladen; " +
            "ist er aus, ändert sich nichts.",
        ),
      );
    }
    /*
     * Ticket 2403 (Martin, 19.09.): nach einer Antwort, die mit einer FRAGE
     * endet, das Mikro von selbst oeffnen.
     *
     * 23.09.2026: drei Stufen statt eines Schalters — Martin will es auch
     * „nach einem Austausch", also nach jeder Antwort (20.09., 21:25).
     * Dieselbe Bauform wie „Nach Stille" darunter, dieselben Stufen wie der
     * Knopf im Mikrofon-Fenster (AUTO_MIKRO_STUFEN).
     *
     * 07.10.2026 (Ticket 3048): „Immer“ heißt jetzt „Jev entscheidet“ —
     * Martin: „immer“ war nie so gemeint. Nach einer Frage geht es auf wie
     * bei „Nach Frage“; zu bleibt es nur, wenn Jev SICHER ist, dass das
     * Gespräch zu Ende ist — sonst geht es auf wie bisher (bei „vermutlich
     * Schluss“ nur kurz). Der Satz unten darf nicht mehr versprechen
     * (Prüfbericht 07.10.: „bleibt zu, wenn es zu Ende scheint“ war zu viel).
     * (shared/mikro-nach-antwort.ts)
     */
    const auto = document.createElement("div");
    auto.className = "einst-feld einst-feld--gestapelt";
    auto.appendChild(
      einstText(
        "Mikro von selbst öffnen",
        jevMoeglich()
          ? "Nach einer Frage hört Vespera gleich zu — oder Jev entscheidet, ob das Gespräch weitergeht."
          : "Nach einer Frage hört Vespera gleich zu.",
        "autoMikroNachFrage",
      ),
    );
    const autoWahl = document.createElement("div");
    autoWahl.className = "einst-segmente";
    autoWahl.setAttribute("role", "radiogroup");
    autoWahl.setAttribute("aria-label", "Mikro von selbst öffnen");
    const mitJev = jevMoeglich();
    // Ohne Jev gibt es die Stufe nicht; ein gespeichertes „jev“ liest sich dort als „Nach Frage“.
    const autoJetzt = autoMikroStufeFuerKonto(this.current.autoMikroNachFrage, mitJev) ?? false;
    /*
     * Ticket 3048: sichtbar nur „Jev“ — „Jev entscheidet“ bricht am Telefon auf
     * zwei Zeilen um (Chromium, 300–358 px Breite gemessen, wie früher „Jede
     * Antwort“). Der ganze Name steht im Vorlesenamen und im Satz darüber.
     */
    const autoText: Record<"aus" | "frage" | "jev", [string, string, string?]> = {
      aus: ["Aus", "Das Mikro bleibt zu, bis du den Knopf drückst."],
      frage: ["Nach Frage", "Das Mikro geht künftig nach einer Frage von selbst auf."],
      jev: [
        "Jev",
        "Nach einer Frage geht das Mikro auf. Ist Jev sicher, dass das Gespräch zu Ende ist, bleibt es zu — sonst geht es auf wie bisher.",
        "Jev entscheidet",
      ],
    };
    const autoKnoepfe: HTMLButtonElement[] = [];
    for (const stufe of autoMikroStufenFuer(mitJev)) {
      const [text, meldung, name] = autoText[stufe === "jev" ? "jev" : stufe ? "frage" : "aus"];
      const b = document.createElement("button");
      b.type = "button";
      b.className = "einst-segment";
      b.setAttribute("role", "radio");
      b.dataset.stufe = String(stufe);
      b.textContent = text;
      if (name) b.setAttribute("aria-label", name);
      b.setAttribute("aria-checked", String(stufe === autoJetzt));
      b.addEventListener("click", () => {
        // Konto noch unbekannt und „jev“ gespeichert: nichts überschreiben (lib/jev-konto.ts).
        if (jevStufeGeschuetzt(this.current.autoMikroNachFrage)) return;
        for (const k of autoKnoepfe) k.setAttribute("aria-checked", String(k === b));
        void this.speichere({ autoMikroNachFrage: stufe }, meldung);
      });
      autoKnoepfe.push(b);
      autoWahl.appendChild(b);
    }
    auto.appendChild(autoWahl);
    mikro.karte.appendChild(auto);
    /*
     * Ticket 2668 (Martin, 26.09.): geht das Mikro von selbst auf, lauscht es
     * nur kurz — sagt er nichts, schließt es wieder. Direkt unter „Mikro von
     * selbst öffnen", weil es nur dort wirkt; dieselbe Bauform wie „Nach
     * Stille" (NACHLAUSCH_STUFEN).
     */
    const lausch = document.createElement("div");
    lausch.className = "einst-feld einst-feld--gestapelt";
    lausch.appendChild(
      einstText(
        "Kurz zuhören",
        "Sagst du nach dem Öffnen so lange nichts, geht das Mikro wieder zu. Nach einer Frage hört es mindestens 6 Sekunden zu.",
        "nachlauschMs",
      ),
    );
    const lauschWahl = document.createElement("div");
    lauschWahl.className = "einst-segmente";
    lauschWahl.setAttribute("role", "radiogroup");
    lauschWahl.setAttribute("aria-label", "Kurz zuhören");
    const lauschJetzt = nachlauschFenster(this.current.nachlauschMs);
    const lauschKnoepfe: HTMLButtonElement[] = [];
    for (const ms of NACHLAUSCH_STUFEN) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "einst-segment";
      b.setAttribute("role", "radio");
      b.dataset.ms = String(ms);
      b.textContent = ms ? `${ms / 1000} s` : "Aus";
      b.setAttribute("aria-checked", String(ms === lauschJetzt));
      b.addEventListener("click", () => {
        for (const k of lauschKnoepfe) k.setAttribute("aria-checked", String(k === b));
        void this.speichere(
          { nachlauschMs: ms },
          ms
            ? `Das Mikro hört ${ms / 1000} Sekunden zu und geht ohne Sprache wieder zu.`
            : "Das Mikro bleibt offen, bis du sprichst oder tippst.",
        );
      });
      lauschKnoepfe.push(b);
      lauschWahl.appendChild(b);
    }
    lausch.appendChild(lauschWahl);
    mikro.karte.appendChild(lausch);
    // 19.10.2026: Mikro-Vorstart und Bereit-Ton (Notschalter) — beides wirkt nur, wo das Mikro von selbst aufgeht.
    mikro.karte.appendChild(
      einstSchalter(
        "Mikro kurz vor dem Ende öffnen",
        "Geht das Mikro nach einer Antwort von selbst auf, holt die App es in den letzten Sekunden der Antwort — dann fehlt dir der Satzanfang nicht. Aus: es öffnet erst nach der Antwort.",
        this.current.mikroVorstart !== false,
        (an) => {
          void this.speichere(
            { mikroVorstart: an },
            an ? "Das Mikro öffnet kurz vor dem Ende der Antwort." : "Das Mikro öffnet erst nach der Antwort.",
          );
        },
        "mikroVorstart",
      ).zeile,
    );
    mikro.karte.appendChild(
      einstSchalter(
        "Leiser Ton, wenn das Mikro bereit ist",
        "Ein ganz kurzer, leiser Ton sagt, dass du sprechen kannst — erst wenn das Mikro wirklich aufnimmt.",
        this.current.bereitTon !== false,
        (an) => {
          void this.speichere(
            { bereitTon: an },
            an ? "Ein leiser Ton sagt, wenn das Mikro bereit ist." : "Kein Ton mehr, wenn das Mikro bereit ist.",
          );
        },
        "bereitTon",
      ).zeile,
    );
    mikro.karte.appendChild(
      einstMehr(
        "Wie hören die beiden zu?",
        (isNativePlatform()
          ? "„Hey Vespera“: Das Zuhören läuft auf dem iPhone; nichts davon wird " +
            "gesendet. Während Vespera spricht, eine Tagesaufnahme oder ein " +
            "Anruf läuft, wartet es. Den Stand zeigt der Punkt an der Sprechtaste. "
          : "") +
          "Nach Frage: Hat Vespera dir eine Frage gestellt und zu Ende " +
          "gesprochen, geht das Mikrofon auf — nur nach echten Fragen, auch " +
          "wenn danach noch ein, zwei kurze Sätze kommen." +
          (jevMoeglich()
            ? " Jev entscheidet: " +
              "nach einer Frage genauso; sonst prüft Jev, ob das Gespräch zu Ende " +
              "scheint. Ist er sicher, bleibt das Mikrofon zu; vermutet er es nur, " +
              "hört es höchstens drei Sekunden zu; sonst geht es auf wie bisher. "
            : " ") +
          (jevMoeglich() ? "Beides" : "Das") + " nur, solange die App " +
          "offen ist und gerade nichts aufgenommen wird. Ein kurzer Ton sagt, " +
          "dass es zuhört; bleibt es ab dem offenen Mikro so lange still wie " +
          "bei „Kurz zuhören“ eingestellt, geht es ohne Senden wieder zu.",
      ),
    );
    /*
     * Ticket 2778 (Stufe 2, Martin, 29.09.): *„Die Verabschiedung hätte schon
     * passieren sollen, aber er hat wieder geöffnet.“* Jev hört sie mit — sagst
     * du „tschüss“, schließt Vespera ab und das Mikro bleibt zu, auch wenn das
     * Modell den Abschied nicht selbst meldet.
     *
     * ⚠️ Nur für Konto 1, wie der Schattenlauf und „Auto“ bei der Stille: Der
     *    Server fragt Jev nur für dieses Konto. Anderen stünde ein Schalter,
     *    der nichts bewirkt. Die Frage „Jev für dieses Konto?“ beantwortet
     *    lib/jev-konto.ts — hier steht sie nicht noch einmal.
     *    Direkt unter „Mikro von selbst öffnen“, weil es dessen Gegenstück
     *    ist: dort geht es auf, hier bleibt es zu.
     */
    if (jevMoeglich()) {
      mikro.karte.appendChild(
        einstSchalter(
          "Verabschiedung erkennen (Jev)",
          "Sagst du „tschüss“, schließt Vespera ab und das Mikro bleibt zu.",
          this.current.abschiedErkennen !== false,
          (an) => {
            void this.speichere(
              { abschiedErkennen: an },
              an
                ? "Vespera erkennt deine Verabschiedung und lässt das Mikro danach zu."
                : "Das Mikro geht nach einem Abschied wieder auf, wenn du es so eingestellt hast.",
            );
          },
          "abschiedErkennen",
        ).zeile,
      );
      mikro.karte.appendChild(
        einstMehr(
          "Wann gilt das als Abschied?",
          "Jev liest deine Worte mit und meldet, wie sicher es eine Verabschiedung ist. " +
            "Ab 85 Prozent, bei höchstens 60 Zeichen und ohne Frage schließt Vespera mit " +
            "einem kurzen Satz ab, und das Mikro geht nicht von selbst wieder auf. " +
            "Längere Sätze mit einem Gruß am Ende zählen nicht, ebenso Aufträge und Diktate " +
            "(„Schreib Anna: tschüss“). Antwortet Jev nicht rasch genug (binnen 0,7 " +
            "Sekunden), bleibt alles wie sonst; hat Vespera das Gespräch schon selbst " +
            "beendet, passiert nichts doppelt.",
        ),
      );
    }

    /*
     * Ticket 2420 (Martin, 20.09.): von selbst absenden — ohne Knopfdruck.
     *
     * ⚠️ ZWEI Wege, weil EINER nicht reicht. Gemessen an sieben Tagen sind
     *    82 % seiner Zuege nativ, und dort gibt es waehrend des Sprechens
     *    keinen Text, in dem ein Wort stehen koennte. Deshalb steht die
     *    Einschraenkung in der aufklappbaren Erklaerung.
     */
    const senden = einstAbschnitt(STILLE_ABSCHNITT);
    const stille = document.createElement("div");
    stille.className = "einst-feld einst-feld--gestapelt";
    /*
     * 20.10.2026 (Martin): wo „Auto“ zur Wahl steht (Konto 1), ist die Zeile der Regler „Automatisch absenden“ —
     * Jev entscheidet / Feste Stille (2, 3, 5 s, nur nach abgeschlossenem Satz) / Aus. Dasselbe Feld `sendeStilleMs`
     * (lib/stille-wahl.ts, baueAbsendenWahl); die übrigen Konten behalten die Knopfreihe.
     */
    const mitRegler = stilleAutoMoeglich();
    stille.appendChild(
      mitRegler
        ? einstText(
            ABSENDEN_NAME,
            "Jev prüft, ob dein Satz fertig ist — oder feste Stille nach einem fertigen Satz — oder nie von selbst.",
            "sendeStilleMs",
          )
        : // 22.10.2026: dasselbe Wort wie im Blatt „Antwort“ (Prüfhinweis: vorher hier „Nach Stille“, dort „Absenden nach Stille“).
          einstText(STILLE_WAHL_NAME, "Schickt ab, wenn du so lange schweigst.", "sendeStilleMs"),
    );
    /*
     * Ticket 2509: dieselben Stufen wie der Schalter im Mikrofon-Fenster.
     * Ticket 2763: dazu „Auto" — nur für Konto 1, wie der Server die Frage an
     * Jev erlaubt (server/stille-auto-routen.ts).
     * 29.09.2026: die Knopfreihe ist EIN Baustein mit dem Blatt der
     * Antwortlänge (lib/stille-wahl.ts) — Werte, Beschriftung und die Regel
     * „Auto nur für Konto 1" stehen dort, nicht mehr hier.
     */
    const waehleStille = (ms: number): void => {
      leiste.zeige(ms);
      void this.speichere({ sendeStilleMs: ms }, stilleBestaetigung(ms, mitRegler));
    };
    const leiste = mitRegler ? baueAbsendenWahl(waehleStille) : baueStilleWahl(waehleStille);
    leiste.zeige(this.current.sendeStilleMs ?? 0);
    stille.appendChild(leiste.el);
    senden.karte.appendChild(stille);

    const wort = document.createElement("div");
    wort.className = "einst-feld einst-feld--gestapelt";
    const wortText = einstText(
      "Auslösewort",
      "Sagst du es am Ende, wird sofort abgeschickt. Mehrere mit Komma trennen.",
      "sendeWort",
    );
    const wortInput = document.createElement("input");
    wortInput.type = "text";
    wortInput.className = "einst-eingabe";
    wortInput.placeholder = "z. B. jetzt senden, weiter";
    wortInput.maxLength = 40;
    wortInput.setAttribute("aria-label", "Auslösewort");
    wortInput.value = this.current.sendeWort ?? "";
    wortInput.addEventListener("change", () => {
      const w = wortInput.value.trim();
      void this.speichere(
        { sendeWort: w },
        w
          ? `${w} am Satzende schickt die Nachricht ab.`
          : "Kein Auslösewort — der Knopf bleibt der Weg.",
      );
    });
    wort.append(wortText, wortInput);
    senden.karte.appendChild(wort);
    senden.karte.appendChild(
      einstMehr(
        "Wann schickt es ab?",
        "Die Stille misst den Mikrofon-Pegel, nicht den Text — wähl lieber zu " +
          "lang als zu kurz, sonst schneidet sie dir beim Nachdenken das Wort " +
          "ab. Das Auslösewort muss am Ende stehen, danach muss einen Moment " +
          "Ruhe sein; zwei Wörter sind sicherer als eines. In der App auf dem " +
          "Handy wirkt es nicht — dort steht der Text erst nach dem Loslassen " +
          "fest. Dafür ist die Stille da. „Weiter“ und „Go“ sind auch gewöhnliche " +
          "Wörter: sie verschwinden nur aus dem Text, wenn sie für sich stehen, " +
          "und sind sie alles, was du sagst, werden sie selbst geschickt. Mit " +
          "einem Auslösewort gibt es zwei weitere Befehle am Ende: „Verwerfen“ " +
          "wirft das Diktat weg, wenn danach zwei Sekunden Ruhe ist, und „ins " +
          "Textfeld“ legt den Text ins Eingabefeld, statt ihn zu senden." +
          (stilleAutoMoeglich()
            ? " Auto: Nach 1,5 Sekunden Pause prüft Jev an der Mitschrift, ob " +
              "dein Satz fertig ist. Wenn ja, geht es raus, sonst wird weiter " +
              "zugehört, höchstens 6 Sekunden Stille. Beurteilt werden die Worte, " +
              "nicht der Tonfall. Ohne Mitschrift während des Sprechens (etwa bei " +
              "laufender Tagesaufnahme) oder wenn Jev nicht rechtzeitig antwortet, " +
              "gelten 4 Sekunden. Feste Stille: ohne Jev, es geht erst nach der " +
              "gewählten Stille hinaus und nur, wenn dein Satz mit Punkt, Frage- oder " +
              "Ausrufezeichen endet — sonst nach der doppelten Stille. Aus: nie von " +
              "selbst. In jeder Stufe wartet es, solange die Mitschrift noch neue " +
              "Wörter bringt, höchstens bis 8 Sekunden Stille."
            : ""),
      ),
    );

    /*
     * Ticket 2741 (Martin, 28.09.): die Mitschrift beim Sprechen abschalten
     * können — sie weicht vom fertigen Text ab und wirkt dann verwirrend.
     *
     * ⚠️ Der Schalter blendet NUR die Anzeige aus. Die Erkennung läuft
     *    weiter: „Auto" (Ticket 2763), das Auslösewort und „schon
     *    gesprochen?" lesen ihren Text. Das steht ehrlich in der Erklärung —
     *    samt der gemessenen Kosten (29.09.: 7 Tage, 487 Minuten, 0,97 $).
     */
    const beim = einstAbschnitt("Beim Sprechen");
    beim.karte.appendChild(
      einstSchalter(
        "Mitschrift anzeigen",
        "Der Text läuft mit, während du sprichst. Aus: nur der Leuchtbalken.",
        this.current.mitschriftZeigen !== false,
        (an) => {
          void this.speichere(
            { mitschriftZeigen: an },
            an
              ? "Der Text läuft beim Sprechen wieder mit."
              : "Beim Sprechen zeigt die Karte nur noch den Leuchtbalken.",
          );
        },
        "mitschriftZeigen",
      ).zeile,
    );
    beim.karte.appendChild(
      einstMehr(
        "Spart das etwas?",
        "Nein, fast nichts: Ausgeschaltet wird nur die Anzeige. Die Erkennung " +
          "hört weiter mit, denn „Auto“ bei „Absenden nach Stille“, das Auslösewort und " +
          "die Frage, ob du schon gesprochen hast, brauchen ihren Text. Sie " +
          "kostet rund einen Dollar pro Woche. Der fertige Text kommt wie " +
          "immer nach dem Loslassen aus der genaueren Erkennung. Statt des " +
          "Textes zeigt die Karte dann einen großen Leuchtbalken, der mit deiner " +
          "Stimme ausschlägt — so siehst du, dass das Mikro wirklich hört.",
      ),
    );

    /*
     * 06.10.2026 (Ticket 2738, Warte-Brücke, server/warte-bruecke.ts): ein EIGENER Schalter,
     * nicht still an dem der Sofort-Antwort (Prüfbefund 05.10.) — Martin soll den Satz hören
     * und für sich abschalten können. Nur Konto 1, Vorgabe an; ob er steht und was er zeigt,
     * rechnet shared/warte-satz.ts — dieselbe Regel wie der Server.
     */
    const uidWarte = (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid;
    if (warteSatzFuerKonto(uidWarte)) {
      beim.karte.appendChild(
        einstSchalter(
          "Kurz Bescheid geben, wenn die Antwort dauert",
          "Bleibt Vespera nach dem Loslassen länger still, sagt sie einmal „Einen Moment.“",
          warteSatzAn(this.current.warteSatz, uidWarte),
          (an) => {
            void this.speichere(
              { warteSatz: an },
              an
                ? "Dauert die Antwort, sagt Vespera zuerst einmal „Einen Moment.“"
                : "Vespera bleibt still, bis die Antwort kommt.",
            );
          },
          "warteSatz",
        ).zeile,
      );
      beim.karte.appendChild(
        einstMehr(
          "Wann kommt der Satz?",
          "Wenn die Antwort nach dem Loslassen auf sich warten lässt — meist etwa fünf " +
            "Sekunden danach, in gut jedem vierten Zug. Vespera sagt ihn einmal, damit du " +
            "weißt, dass sie dich gehört hat; danach kommt die eigentliche Antwort. Bei " +
            "Aufträgen und Sachfragen kann es auch „Ich bin dran.“ heißen, bei allem " +
            "anderen nur „Einen Moment.“ Erzählst du etwas Persönliches, sagt sie nichts: " +
            "dazwischen passt kein Satz. Der Satz sagt nie ein Ergebnis und verspricht " +
            "keine Zeit. Er gilt in jedem Antwortmodus, auch bei „kurz“. Kommt die Antwort " +
            "schneller oder hat die Sofort-Antwort schon gesprochen, hörst du nichts davon. " +
            "Nicht zu verwechseln mit „Sofort-Antwort bei Aufträgen“ (Einstellungen → " +
            "Erweitert): Die sagt gleich nach der Frage, was Vespera tut. Dieser " +
            "Schalter hier überbrückt nur das Warten.",
        ),
      );
    }

    // 19.10.2026: ohne Anruf/Live im Paket gibt es keine Wahl des Live-Modells (shared/einstellungen-sichtbar.ts).
    if (!kontoGruppen().anrufModell) return [mikro.abschnitt, beim.abschnitt, senden.abschnitt];
    const anruf = einstAbschnitt("Im Anruf");
    anruf.karte.appendChild(this.anrufModellWahl());

    return [mikro.abschnitt, beim.abschnitt, senden.abschnitt, anruf.abschnitt];
  }

  /**
   * Das Modell für den Anruf. (23.09.2026)
   *
   * Martin: *„Gemini 3.8 Live ist da. Bitte ersetze unser altes Gemini
   * Realtime."* 3.8 ist jetzt Standard; das bisherige 3.1 bleibt als bewusste
   * Wahl — ein Rückweg mit einem Tipp, falls sich 3.8 im echten Gespräch
   * anders anfühlt als in der Messung. Die Liste und ihre Begründung stehen
   * in settings.ts (GEMINI_REALTIME_MODELS), die Auflösung im Agenten in
   * agent/anruf-modell.ts.
   */
  /**
   * Was der „Hey Vespera"-Lauscher gerade tut. (23.09.2026)
   *
   * Martin: *„Wie kann ich sehen, dass das funktioniert? Da passiert genau
   * gar nichts. Ich sehe nichts."* Der Schalter sagte nur, was gewünscht ist
   * — nicht, ob gelauscht wird. Die Zeile folgt jeder Meldung des Lauschers
   * (ab Build 67), auch während die Einstellungen offen sind.
   */
  private wachwortZustandZeile(): HTMLElement {
    const p = document.createElement("div");
    p.className = "einst-zustand wachwort-zustand";
    p.setAttribute("role", "status");
    p.setAttribute("aria-live", "polite");
    const punkt = document.createElement("span");
    punkt.className = "einst-punkt";
    punkt.setAttribute("aria-hidden", "true");
    const text = document.createElement("span");
    text.className = "einst-zustand-text";
    p.append(punkt, text);
    const zeige = (): void => {
      void import("./wachwort-zustand.js").then((wz) => {
        const z = wz.wachwortZustand();
        punkt.dataset.art = z ? wz.wachwortArt(z.zeile) : "aus";
        text.textContent = wz.wachwortZustandText(z);
      });
    };
    zeige();
    const hoerer = gesteuert(
      "live",
      (): void => {
        // Die Einstellungen werden neu gebaut — eine abgehängte Zeile hört auf.
        if (!p.isConnected) {
          window.removeEventListener("voice-v8:wachwort-zustand", hoerer);
          return;
        }
        zeige();
      },
      true,
    );
    window.addEventListener("voice-v8:wachwort-zustand", hoerer);
    return p;
  }

  private anrufModellWahl(): HTMLElement {
    // Eine Zeile wie in der Übersicht: Name links, Wahl rechts. Die Auswahl
    // selbst ist das Auswahlfeld des Systems — am iPhone das Rad von unten.
    const zeile = document.createElement("label");
    zeile.className = "einst-feld einst-feld--wahl";
    const label = document.createElement("span");
    label.className = "einst-feld-titel";
    label.textContent = "Modell";
    zeile.appendChild(label);
    const wahl = document.createElement("select");
    wahl.className = "einst-wahl";
    for (const m of GEMINI_REALTIME_MODELS) {
      const o = document.createElement("option");
      o.value = m.id;
      // In der Zeile steht nur der Name; „bisher" bleibt als Hinweis stehen.
      o.textContent = m.label.replace(/\s+—\s+empfohlen$/, "");
      o.title = m.description;
      wahl.appendChild(o);
    }
    wahl.value = this.current.realtimeModel ?? "";
    wahl.addEventListener("change", () => {
      const gewaehlt = GEMINI_REALTIME_MODELS.find((m) => m.id === wahl.value);
      if (!gewaehlt) return;
      void this.speichere(
        { realtimeModel: gewaehlt.id },
        `Der nächste Anruf läuft auf ${gewaehlt.label.replace(/\s+—.*$/, "")}.`,
      );
    });
    zeile.appendChild(wahl);
    const pfeil = document.createElement("span");
    pfeil.className = "einst-zeile-pfeil";
    pfeil.innerHTML = zeichen("pfeil-rechts", 16);
    zeile.appendChild(pfeil);
    return zeile;
  }

  private voiceSection(): HTMLElement {
    // Pre.803 (#64): Nur noch Audio/Anruf-Themen — Stimme + Vorlesen,
    // Geduldiges Zuhören, eigene Stimmen, Erweitert (Anruf-Feintuning).
    // Antwort-Modell + CLI wohnen jetzt im „Bot"-Tab.
    const wrap = document.createElement("section");
    wrap.className = "settings-section settings-section--voice";

    // Pre.970: KEINE Zwangs-Migration mehr auf gemini-realtime. Der
    // „Erzähl-Modus" (voiceMode='pipeline', Umschalter unten) ist jetzt eine
    // bewusst wählbare Alternative, die den User ausreden lässt (Silero-VAD,
    // 1,5 s Geduld — gift-frei, anders als das Realtime-VAD-Feld). Default
    // bleibt Real Talk (gemini-realtime, settings.ts), also kein Verhaltens-
    // wechsel für bestehende User, die nichts umstellen.

    // --- 1. STIMME ---
    const voiceCard = document.createElement("section");
    voiceCard.className = "settings-subsection";
    const voiceTitle = document.createElement("h3");
    voiceTitle.className = "settings-subsection-title";
    // Pre.803 (#64-B0.4): realtimeVoice steuert per Design AUCH das
    // Chat-Vorlesen (GEMINI_TO_CHIRP3) — der Titel sagt das ehrlich.
    // 19.10.2026: ohne Anruf im Paket gibt es nur die Stimme zum Vorlesen (shared/einstellungen-sichtbar.ts).
    voiceTitle.textContent = kontoGruppen().anrufStimme ? "Bot-Stimme (Anruf + Vorlesen)" : "Bot-Stimme (Vorlesen)";
    voiceCard.appendChild(voiceTitle);
    voiceCard.appendChild(this.unifiedVoicePicker());
    // 2026-07-05 (Martin): Tempo-Regler ist ZURUECK — seit Cloud-TTS wirkt
    // speakingRate wirklich (vorher ignoriert, darum am 29.6. entfernt).
    // Gilt fuers Vorlesen (Chat/PTT), nicht fuer den Live-Anruf.
    // 29.09.2026 (Ticket 2800): bis 2,0 in Schritten von 0,1 — das Blatt der
    // Antwortlänge hat dazu seit 01.10.2026 sieben Positionen (1,0 bis 1,6).
    voiceCard.appendChild(
      this.fieldRange(
        "Vorlese-Tempo",
        "speed",
        VORLESE_TEMPO.min,
        VORLESE_TEMPO.max,
        VORLESE_TEMPO.schritt,
        tempoText,
      ),
    );
    wrap.appendChild(voiceCard);
    /*
     * ⚠️ 22.09.2026 — UMGEZOGEN aus dem Reiter „Mehr". „Vorlesen sofort"
     *    regelt dasselbe Vorlesen wie der Tempo-Regler eine Zeile darueber.
     *    Wer eine Vorlese-Einstellung suchte, fand bisher die eine Haelfte
     *    im einen und die andere im anderen Reiter.
     *
     * ⚠️ Nur fuer Nutzer 1 — der Block war dort hinter derselben Schranke.
     */
    // 16.10.2026: „Vorlesen sofort“, „Schneller Ton“ und der „Echtzeit-Test“ stehen seither auf der Seite „Erweitert“
    // (Versuche, `versucheSeite`) — weiter nur für Konto 1.

    // 2026-07-08 (Martin, endgueltig): NUR zwei Sprech-Wege — Push-to-Talk
    // (guenstig, Abo) und Real Talk (Anruf). Der Erzaehl-Modus-Umschalter ist
    // raus; der Pipeline-Pfad existiert serverseitig weiter als Kosten-Schutz
    // fuer Gratis-Nutzer (/api/token zwingt free auf pipeline) und laeuft
    // seit pre.1528 crashfrei ueber Gemini. voiceModeToggle() ungenutzt.

    // 2026-07-27 (Ticket 746, Martin: „funktioniert die de facto sowieso
    // nicht?"): Der Aufklapp-Bereich „Erweitert" ist ersatzlos entfallen. Sein
    // einziger Inhalt war die Mikrofon-Empfindlichkeit (leise/normal/laut) —
    // und die war nachweislich wirkungslos: sttSensitivity wurde im ganzen
    // Projekt nur gespeichert, validiert und durchgereicht, aber von KEINEM
    // Audio- oder STT-Pfad je gelesen. Ein Regler, der nichts regelt, verwirrt
    // mehr als er nuetzt. Das Datenfeld bleibt in den Settings-Typen, damit
    // gespeicherte Altwerte nichts brechen.
    // 2026-06-29 (Martin): „Antwort-Stil im Anruf" (responseStyle) entfernt —
    // proaktive Begrüßung ist im Gemini-Realtime-Modus by design tot. „Push-
    // to-Talk-Modus" (pttMode) entfernt — der Agent ignorierte ihn (totes UI).
    // Pre.970: Realtime-Modell-Picker entfernt (nur 3.1 nutzbar, Modell fest).
    // Fallback-Modell bleibt — relevant für Pipeline/PTT.
    // 2026-06-29 (Martin): Fallback-Modell ist ein Profi-/Dev-Thema → nur Superuser
    // (uid===1). Sprache + Mikrofon-Empfindlichkeit bleiben für alle im „Erweitert".
    // 2026-06-27: TTS-Provider-Picker entfernt — der Server nutzt einheitlich die
    // gewählte Gemini-Stimme (toGeminiVoice); die Provider-Wahl hatte keine Wirkung.

    return wrap;
  }

  // Pre.296: Premium-Lock-Block — zeigt ausgegrauten Connector-Block
  // mit Premium-Hinweis für User ohne Freischaltung (non-superadmin).

  private daemonBlock(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.className = "settings-extension";

    const head = document.createElement("div");
    head.className = "settings-extension-head";
    const title = document.createElement("h3");
    title.textContent = "Vespera Desktop (Mac)";
    head.appendChild(title);
    wrap.appendChild(head);

    const desc = document.createElement("p");
    desc.className = "settings-extension-desc";
    desc.textContent =
      "Kleines Hilfsprogramm für deinen Mac — gibt Vespera Zugriff auf den ganzen Bildschirm, native Apps (Numbers, Mail, Terminal …), Maus + Tastatur. Zusätzlich zur Browser-Extension.";
    wrap.appendChild(desc);

    const status = document.createElement("div");
    status.className = "connector-status";
    status.textContent = "Status wird geladen …";
    wrap.appendChild(status);

    // 07.10.2026: Ein zweites Vespera Desktop klopft an — der Server weist es ab; hier steht, was zu tun ist.
    // Ein bleibendes Element mit role=status, damit ein Vorleseprogramm den Wechsel ansagt.
    const zweites = document.createElement("p");
    zweites.className = "connector-zweites";
    zweites.setAttribute("role", "status");
    zweites.hidden = true;
    wrap.appendChild(zweites);

    // 07.10.2026 (Plan „mehrere Geräte“, S4): alle Macs als Liste — wer arbeitet, wer ist bereit, Hier arbeiten,
    // Umbenennen, Trennen (frontend/lib/mac-geraete-liste.ts). Nach einer Aktion kommt der ganze Stand neu.
    const geraeteListe = baueMacGeraeteListe({ nachher: () => void refresh() });
    geraeteListe.element.hidden = true;
    wrap.appendChild(geraeteListe.element);
    // Die Meldung nach einer Aktion steht neben (nicht in) der Liste: sie bleibt, wenn der letzte Mac geht.
    wrap.appendChild(geraeteListe.meldung);

    // 12.10.2026: „Blender ohne Einzelfreigabe“ — für das Betreiberkonto; sonst bleibt der Block unsichtbar (frontend/lib/blender-freigabe-schalter.ts).
    wrap.appendChild(baueBlenderFreigabe());

    // Ticket 2816: „Bei dir: … · Aktuell: …“ unter dem Status (und dem Hinweis oben, falls er steht). Mit der
    // Geräteliste steht die Zeile je Mac; diese eine bleibt für einen älteren Server ohne Liste.
    const desktopVersion = versionZeile(null, desktopStand({ verbunden: false, bei: null, aktuell: null }));
    desktopVersion.hidden = true;
    wrap.appendChild(desktopVersion);

    // Ticket 2816 (Martin: „Ich kann die Mac-App vom System direkt
    // herunterladen“): die neue App — der Server liefert die Adresse nur für
    // Konto 1 (Ticket 2817: die freigegebene Fassung, sonst die Testversion);
    // alle anderen sehen den Installer unten.
    const neueApp = document.createElement("div");
    neueApp.className = "connector-neue-app";
    neueApp.hidden = true;
    const neueAppLink = document.createElement("a");
    neueAppLink.className = "connector-connect connector-neue-app-link";
    neueAppLink.textContent = downloadText(null, null);
    neueAppLink.setAttribute("download", "");
    neueApp.appendChild(neueAppLink);
    const neueAppHinweis = document.createElement("p");
    neueAppHinweis.className = "connector-neue-app-hinweis";
    neueAppHinweis.textContent = DOWNLOAD_HINWEIS;
    neueApp.appendChild(neueAppHinweis);
    wrap.appendChild(neueApp);

    // Ticket 2823: der Code im selben Baustein wie bei der Browser-Erweiterung
    // (groß, Kopieren-Knopf, Restzeit) — vorher stand er als schlichter Text da.
    const kopplung = baueKopplungsCode("Kopplungscode");

    const install = document.createElement("div");
    install.className = "ext-install";
    const installTitle = document.createElement("span");
    installTitle.className = "ext-install-title";
    installTitle.textContent = "Vespera Desktop installieren (einmalig)";
    install.appendChild(installTitle);

    const dl = document.createElement("a");
    dl.className = "ext-install-download";
    dl.href = "/vespera-desktop-installer.zip";
    dl.download = "vespera-desktop-installer.zip";
    const dlIcon = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg",
    );
    dlIcon.setAttribute("viewBox", "0 0 24 24");
    dlIcon.setAttribute("width", "16");
    dlIcon.setAttribute("height", "16");
    dlIcon.setAttribute("aria-hidden", "true");
    const dlPath = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "path",
    );
    dlPath.setAttribute("fill", "currentColor");
    dlPath.setAttribute("d", "M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z");
    dlIcon.appendChild(dlPath);
    dl.appendChild(dlIcon);
    const dlLabel = document.createElement("span");
    dlLabel.textContent = "Installer herunterladen (.zip, ~20 KB)";
    dl.appendChild(dlLabel);
    // Ticket 2396, Nachprüfung Build 77: Eine .zip für den Mac hat in der
    // iPhone-App kein Ziel — die Ansicht zeigte eine leere Fläche. Wie der
    // Herunterladen-Knopf der Werkzeugkarten nur im Browser.
    if (!isNativePlatform()) install.appendChild(dl);

    const steps = document.createElement("ol");
    steps.className = "ext-install-steps";
    const stepsItems = [
      "ZIP entpacken",
      "Terminal öffnen, in den entpackten Ordner wechseln",
      "./install.sh ausführen — installiert Vespera Desktop und startet es bei jeder Anmeldung",
      "Code generieren (Knopf oben), in Terminal-Setup eingeben",
      "macOS-Permissions erlauben: Bildschirmaufzeichnung + Bedienungshilfen für Python",
    ];
    for (const txt of stepsItems) {
      const li = document.createElement("li");
      li.textContent = txt;
      steps.appendChild(li);
    }
    install.appendChild(steps);

    /*
     * Ticket 2823: Knopf, darunter der Code, darunter (nur alte App) der
     * Installer. Die neue App koppelt per Klick: der Knopf holt einen Code
     * und öffnet vespera-desktop://verbinden?code=… — Vespera Desktop
     * übernimmt ihn selbst. Die alte App (0.x) kennt den Link nicht; für sie
     * bleibt der Code fürs Terminal.
     */
    const actions = document.createElement("div");
    actions.className = "connector-actions";
    const genBtn = document.createElement("button");
    genBtn.type = "button";
    genBtn.className = "connector-connect";
    actions.appendChild(genBtn);

    const ohneKlick = document.createElement("button");
    ohneKlick.type = "button";
    ohneKlick.className = "connector-ohne-klick";
    ohneKlick.textContent = "Kein Klick möglich? Code in Vespera Desktop eintippen";
    ohneKlick.hidden = true;

    /*
     * 07.10.2026 (S4): Verbinden ist mit der Liste ein eigener Bereich. Ist schon ein Mac verbunden, steht er
     * hinter „+ Weiteren Mac verbinden“ (auch der Weg, einen getrennten Mac wieder zu verbinden); ist keiner
     * verbunden, steht er offen da — wie bisher. Ein älterer Server ohne Liste: immer offen.
     */
    const weitererMac = document.createElement("button");
    weitererMac.type = "button";
    weitererMac.className = "mac-verbinden-auf";
    weitererMac.hidden = true;
    const plus = document.createElement("span");
    plus.className = "mac-verbinden-plus";
    plus.setAttribute("aria-hidden", "true");
    plus.textContent = "+";
    const weitererText = document.createElement("span");
    weitererText.textContent = "Weiteren Mac verbinden";
    weitererMac.append(plus, weitererText);
    const vierMacs = document.createElement("p");
    vierMacs.className = "mac-verbinden-grenze";
    vierMacs.textContent = VIER_MACS_HINWEIS;
    vierMacs.hidden = true;
    const verbinden = document.createElement("div");
    verbinden.className = "mac-verbinden";
    verbinden.id = `mac-verbinden-${Math.random().toString(36).slice(2, 10)}`;
    weitererMac.setAttribute("aria-controls", verbinden.id);
    weitererMac.setAttribute("aria-expanded", "true");
    verbinden.append(actions, ohneKlick, kopplung.box, install);
    wrap.append(weitererMac, vierMacs, verbinden);

    /** Hat Martin „Weiteren Mac verbinden“ aufgeklappt? (Nur, solange schon einer verbunden ist.) */
    let verbindenGewaehlt = false;
    /** `voll`: vier Macs verbunden — ein fünfter würde abgewiesen; dann gibt es den Knopf nicht (der Satz bleibt). */
    const zeichneVerbinden = (zuklappbar: boolean, voll = false): void => {
      weitererMac.hidden = !zuklappbar || voll;
      verbinden.hidden = zuklappbar && (voll || !verbindenGewaehlt);
      weitererMac.setAttribute("aria-expanded", String(!verbinden.hidden));
    };

    /**
     * Der Hinweis zum doppelten Programm — nur, was sich ändert, wird angefasst (Prüfung 07.10.): vorher änderte
     * jeder 10-s-Abruf den Text dreimal, und ein Vorleseprogramm sagte ihn alle 10 s neu an. Anzahl und Uhrzeit
     * stehen in einem stillen Teil (aria-hidden): sie ändern sich bei jedem Versuch der App.
     */
    const zweitesZahl = document.createElement("span");
    zweitesZahl.setAttribute("aria-hidden", "true");
    let zweitesSatz = "";
    const zeichneZweites = (t: { vor: string; zahl: string; nach: string } | null): void => {
      const satz = t ? t.vor + t.nach : "";
      if (zweitesSatz !== satz) {
        zweitesSatz = satz;
        zweites.replaceChildren(...(t ? [t.vor, zweitesZahl, t.nach] : []));
      }
      if (t && zweitesZahl.textContent !== t.zahl) zweitesZahl.textContent = t.zahl;
      if (zweites.hidden !== !t) zweites.hidden = !t;
    };
    weitererMac.addEventListener("click", () => {
      verbindenGewaehlt = verbinden.hidden;
      zeichneVerbinden(true);
      if (!verbinden.hidden) genBtn.focus();
    });

    /** „neu“: Vespera Desktop ab 1.0 — „alt“: die PawBot-App mit Terminal-Setup. */
    let weg: "neu" | "alt" = "alt";
    let letzterStatus: DaemonStatus | null = null;
    const amMac = istMacBrowser(isNativePlatform());
    const perKlick = (): boolean => weg === "neu" && amMac;
    const zeichneWeg = (): void => {
      genBtn.textContent = perKlick()
        ? "Mit diesem Mac verbinden"
        : weg === "neu"
          ? "Code für den Mac erzeugen"
          : "Pairing-Code generieren";
      ohneKlick.hidden = !perKlick();
    };
    zeichneWeg();

    /** Die Statuszeile: Punkt und Text (ohne Liste, oder solange kein Mac bekannt ist). */
    const statusZeile = (punkt: "is-ok" | "is-warn" | "is-off", text: string): void => {
      const dot = document.createElement("span");
      dot.className = `connector-dot ${punkt}`;
      const txt = document.createElement("span");
      txt.textContent = text;
      status.append(dot, txt);
    };

    /** Älterer Server ohne Geräteliste: der Stand des einen Macs, wie bisher. */
    const zeichneEinenMac = (s: DaemonStatus): void => {
      // Ticket 2823: „Verbunden … seit 17:33“ (letzte Anmeldung der App).
      const seit = s.connected ? seitText(s.seit) : "";
      const text = s.active ? "Verbunden, aktiv" : s.connected && s.paused ? "Verbunden, pausiert" : s.connected ? "Verbunden" : "Nicht verbunden";
      statusZeile(s.active ? "is-ok" : s.connected ? "is-warn" : "is-off", seit ? `${text} · ${seit}` : text);
      versionZeile(
        desktopVersion,
        desktopStand({
          verbunden: s.connected,
          bei: s.version ?? null,
          aktuell: s.aktuell ?? null,
          download: !!s.download,
          downloadVersion: s.downloadVersion ?? null,
        }),
      );
      desktopVersion.hidden = false;
      geraeteListe.element.hidden = true;
      vierMacs.hidden = true;
      zeichneVerbinden(false);
      if (s.connected && s.accessibility === false) {
        const warn = document.createElement("div");
        warn.className = "connector-warn";
        // Ticket 2823: der Python-Satz gilt nur der alten App.
        warn.textContent = bedienungshilfenHinweis(istNeueDesktopApp(s.version));
        status.appendChild(warn);
      }
    };

    /** Die Geräteliste (S1-Server): je Mac eine Zeile; Version und Bedienungshilfen stehen dort. */
    const zeichneListe = (s: DaemonStatus, geraete: NonNullable<DaemonStatus["geraete"]>, abgefragtUm?: number): void => {
      status.hidden = geraete.length > 0;
      if (geraete.length === 0) statusZeile("is-off", "Noch kein Mac verbunden.");
      geraeteListe.element.hidden = geraete.length === 0;
      geraeteListe.zeichne(
        { geraete, wahl: s.wahl ?? null, aktuell: s.aktuell, download: s.download, downloadVersion: s.downloadVersion, updateStarten: s.updateStarten ?? null },
        abgefragtUm,
      );
      desktopVersion.hidden = true;
      const verbundene = geraete.filter((g) => g.verbunden).length;
      // Ist gerade keiner verbunden, steht die Anleitung offen da; sobald einer da ist, klappt sie zu.
      if (verbundene === 0) verbindenGewaehlt = false;
      zeichneVerbinden(verbundene > 0, verbundene >= MAC_HOECHSTENS);
      vierMacs.hidden = verbundene < MAC_HOECHSTENS;
    };

    /** `abgefragtUm`: wann die Abfrage losging (die Liste übergeht einen Stand, der älter ist als ihre letzte Aktion). */
    const renderStatus = (s: DaemonStatus | null, err?: string, abgefragtUm?: number) => {
      while (status.firstChild) status.removeChild(status.firstChild);
      status.hidden = false;
      if (err) {
        // Keine rohe Fehlernummer: was los ist und dass es weitergeht. Eine schon gezeigte Liste bleibt stehen.
        status.textContent = "Der Stand der Macs lässt sich gerade nicht laden – Vespera versucht es gleich wieder.";
        return;
      }
      if (!s) {
        status.textContent = "—";
        return;
      }
      if (Array.isArray(s.geraete)) zeichneListe(s, s.geraete, abgefragtUm);
      else zeichneEinenMac(s);

      zeichneZweites(zweitesProgrammTeile(s));

      if (s.download) {
        neueAppLink.href = s.download;
        neueAppLink.textContent = downloadText(s.downloadVersion, s.aktuell);
        neueAppHinweis.textContent = downloadHinweis(s.downloadVersion, s.aktuell);
        neueApp.hidden = false;
        install.hidden = true;
      } else {
        neueApp.hidden = true;
        install.hidden = false;
      }

      letzterStatus = s;
      // Das Kopfzeichen „Mac“ hört mit — es schaltet sich selbst (lib/mac-zeichen.ts).
      document.dispatchEvent(new CustomEvent(DESKTOP_STATUS_EREIGNIS, { detail: s }));
      weg = koppelnMitNeuerApp(s) ? "neu" : "alt";
      if (!genBtn.disabled) zeichneWeg();
    };

    const refresh = async () => {
      const um = performance.now();
      try {
        const s = await fetchDaemonStatus();
        renderStatus(s, undefined, um);
      } catch (e) {
        renderStatus(null, e instanceof Error ? e.message : String(e));
      }
    };

    /*
     * Nach dem Code alle 3 s nachsehen, ob die App sich verbunden hat — dann
     * geht der Code weg. ⚠️ War schon eine App verbunden (neu koppeln, alte
     * durch neue ersetzen), zählt erst eine NEUE Verbindung. Vorher verschwand
     * der Code dort nach drei Sekunden. Mit der Geräteliste: ein Mac, der
     * vorher nicht verbunden war, oder derselbe mit neuer Anmeldezeit
     * (mac-geraete.ts neueVerbindung); ohne Liste: eine andere Version.
     */
    let codePoll: number | null = null;
    const stopCodePoll = (): void => {
      if (codePoll !== null) window.clearInterval(codePoll);
      codePoll = null;
    };
    const beobachteVerbindung = (): void => {
      const vorher = letzterStatus;
      const ende = Date.now() + CODE_GUELTIG_MS;
      stopCodePoll();
      codePoll = window.setInterval(async () => {
        if (!wrap.isConnected || Date.now() > ende) {
          stopCodePoll();
          return;
        }
        const um = performance.now();
        try {
          const s = await fetchDaemonStatus();
          if (neueVerbindung(vorher, s)) {
            stopCodePoll();
            kopplung.verstecke();
            verbindenGewaehlt = false;
          }
          renderStatus(s, undefined, um);
        } catch {
          /* noch nicht verbunden — nächster Takt */
        }
      }, 3000);
    };

    /**
     * Einen Code holen (oder den gezeigten, solange er noch gilt) und — am
     * Mac, neue App — Vespera Desktop damit öffnen.
     *
     * ⚠️ Der zweite Klick nimmt den gezeigten Code und öffnet den Link OHNE
     *    Warten aufs Netz. Ein Browser, der den ersten Link nach der Anfrage
     *    nicht mehr als Klick gelten lässt, öffnet ihn so beim zweiten.
     */
    const koppeln = async (klick: boolean): Promise<void> => {
      const hinweis = weg === "alt" ? CODE_HINWEIS.alt : klick ? CODE_HINWEIS.klick : CODE_HINWEIS.eintippen;
      let c = kopplung.gueltigerCode(30_000);
      if (c) {
        kopplung.hinweis(hinweis);
      } else {
        genBtn.disabled = true;
        ohneKlick.disabled = true;
        genBtn.textContent = "Code wird erzeugt …";
        try {
          const neu = await generateDaemonPairingCode(klick);
          c = neu.code;
          kopplung.zeige(c, neu.expiresAt, hinweis);
          beobachteVerbindung();
        } catch (err) {
          window.alert(`Code-Fehler: ${err instanceof Error ? err.message : String(err)}`);
          return;
        } finally {
          genBtn.disabled = false;
          ohneKlick.disabled = false;
          zeichneWeg();
        }
      }
      if (klick) oeffneLink(verbindenLink(c));
    };
    genBtn.addEventListener("click", () => void koppeln(perKlick()));
    ohneKlick.addEventListener("click", () => void koppeln(false));

    void refresh();
    // Pre.984: Status-Punkt selbst aktualisieren, solange die Ansicht offen
    // ist. Vorher EIN einziger Fetch beim Öffnen → der Punkt klebte auf einem
    // veralteten Stand (z.B. „gelb/pausiert" aus einer alten Lesung), obwohl
    // der Daemon längst wieder aktiv war. Self-cleaning: stoppt, sobald das
    // Element nicht mehr im DOM hängt (Ansicht verlassen).
    const statusPoll = window.setInterval(
      gesteuert(
        "live",
        () => {
          if (!wrap.isConnected) {
            window.clearInterval(statusPoll);
            return;
          }
          void refresh();
        },
        true,
      ),
      10_000,
    );
    return wrap;
  }

  private coachBlock(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.className = "settings-extension";

    const head = document.createElement("div");
    head.className = "settings-extension-head";
    const title = document.createElement("h3");
    title.textContent = "Bildschirm-Hilfe";
    head.appendChild(title);
    wrap.appendChild(head);

    const desc = document.createElement("p");
    desc.className = "settings-extension-desc";
    desc.textContent =
      "Wenn aktiv, schaut der Bot bei Hilfe-Fragen automatisch auf deinen Mac-Bildschirm (über Vespera Desktop) und antwortet konkret zum aktuellen Fenster. Plappert nicht von sich aus — Hilfe auf Anfrage. Vespera Desktop muss verbunden sein.";
    wrap.appendChild(desc);

    // 2026-07-08 (Martin, "Checkbox ganz anders dargestellt"): identisch zum
    // HD-/Emoji-Toggle — settings-field-toggle (20px Ember, Label links,
    // Checkbox rechts) statt system-blauer Default-Checkbox. Status als Hint
    // unter die Zeile.
    const row = document.createElement("label");
    row.className = "settings-field settings-field-toggle";

    const lbl = document.createElement("span");
    lbl.className = "settings-label";
    lbl.textContent = "Bildschirm-Hilfe aktivieren";
    row.appendChild(lbl);

    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.disabled = true;
    row.appendChild(cb);

    wrap.appendChild(row);

    const status = document.createElement("span");
    status.className = "settings-status settings-status--inline";
    status.textContent = "lädt …";
    wrap.appendChild(status);

    void (async () => {
      try {
        const cur = await fetchCoachMode();
        cb.checked = cur;
        cb.disabled = false;
        status.textContent = cur ? "an" : "aus";
      } catch {
        status.textContent = "Fehler";
      }
    })();

    cb.addEventListener("change", () => {
      const next = cb.checked;
      status.textContent = "speichere …";
      void setCoachMode(next).then(
        (saved) => {
          cb.checked = saved;
          status.textContent = saved ? "an" : "aus";
        },
        () => {
          status.textContent = "Fehler";
        },
      );
    });

    return wrap;
  }

  private locationBlock(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.className = "settings-extension";

    const head = document.createElement("div");
    head.className = "settings-extension-head";
    const title = document.createElement("h3");
    title.textContent = "Standort";
    head.appendChild(title);
    wrap.appendChild(head);

    const desc = document.createElement("p");
    desc.className = "settings-extension-desc";
    desc.textContent =
      'Bot weiß deine grobe Position — kann dann ortsbezogen antworten ("Wetter heute", "Pizza in der Nähe", direkt Google Maps mit Route öffnen). Wird bei dir lokal abgefragt und nur Lat/Lng + Stadt am Server gespeichert. Du kannst es jederzeit löschen.';
    wrap.appendChild(desc);

    const status = document.createElement("div");
    status.className = "connector-status";
    status.textContent = "Wird geladen …";
    wrap.appendChild(status);

    const actions = document.createElement("div");
    actions.className = "connector-actions";

    const enableBtn = document.createElement("button");
    enableBtn.type = "button";
    enableBtn.className = "connector-connect";
    enableBtn.textContent = "Standort freigeben";
    actions.appendChild(enableBtn);

    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.className = "connector-disconnect hidden";
    removeBtn.textContent = "Standort löschen";
    actions.appendChild(removeBtn);

    wrap.appendChild(actions);

    // Pre.760: Auto-Standort-Schalter (Standard an) — hält den Standort
    // automatisch aktuell, solange die App offen ist (kein manuelles Aktualisieren).
    const autoRow = document.createElement("label");
    autoRow.className = "settings-auto-loc";
    const autoCb = document.createElement("input");
    autoCb.type = "checkbox";
    autoCb.checked = isAutoLocationEnabled();
    const autoTxt = document.createElement("span");
    autoTxt.textContent =
      "Automatisch aktuell halten (solange die App offen ist)";
    const standortMarke = geraeteMarke("autoStandort");
    if (standortMarke) autoTxt.appendChild(standortMarke);
    autoRow.append(autoCb, autoTxt);
    autoCb.addEventListener("change", () =>
      setAutoLocationEnabled(autoCb.checked),
    );
    wrap.appendChild(autoRow);

    const renderStatus = (loc: UserLocation | null, err?: string) => {
      while (status.firstChild) status.removeChild(status.firstChild);
      if (err) {
        status.textContent = err;
        return;
      }
      const dot = document.createElement("span");
      dot.className = "connector-dot " + (loc ? "is-ok" : "is-off");
      status.appendChild(dot);
      const txt = document.createElement("span");
      if (loc) {
        const where = [loc.city, loc.country].filter(Boolean).join(", ");
        txt.textContent =
          (where ? where + " · " : "") +
          `Lat ${loc.lat.toFixed(4)}, Lng ${loc.lng.toFixed(4)}`;
        removeBtn.classList.remove("hidden");
        enableBtn.textContent = "Standort aktualisieren";
      } else {
        txt.textContent = "Nicht freigegeben";
        removeBtn.classList.add("hidden");
        enableBtn.textContent = "Standort freigeben";
      }
      status.appendChild(txt);
    };

    const refresh = async () => {
      try {
        const loc = await fetchUserLocation();
        renderStatus(loc);
      } catch (e) {
        renderStatus(null, e instanceof Error ? e.message : String(e));
      }
    };

    // Pre.240: Manueller Fallback wenn Browser-Geolocation fehlschlägt
    // (z.B. macOS-Ortungsdienste aus oder Domain nicht erlaubt).
    const manualWrap = document.createElement("div");
    // 2026-06-30 (Martin): Ortsfeld IMMER sichtbar — in der nativen App
    // gibt es kein GPS (WKWebView), die Texteingabe ist dort der
    // zuverlaessige Weg, den Standort zu setzen.
    manualWrap.className = "settings-extension-manual";
    const manualHint = document.createElement("p");
    manualHint.className = "settings-extension-desc";
    manualHint.textContent =
      "Wenn die automatische Abfrage nicht klappt: Stadt eingeben + Suchen — wir holen die Koordinaten von OpenStreetMap.";
    manualWrap.appendChild(manualHint);
    const manualRow = document.createElement("div");
    manualRow.className = "connector-actions";
    const cityInput = document.createElement("input");
    cityInput.type = "text";
    cityInput.placeholder = "Stadt (z.B. Wien oder Hollenthon, AT)";
    cityInput.className = "settings-extension-input";
    const searchBtn = document.createElement("button");
    searchBtn.type = "button";
    searchBtn.className = "connector-connect";
    searchBtn.textContent = "Suchen";
    manualRow.appendChild(cityInput);
    manualRow.appendChild(searchBtn);
    manualWrap.appendChild(manualRow);
    wrap.appendChild(manualWrap);

    const lookupCity = async () => {
      const q = cityInput.value.trim();
      if (!q) {
        cityInput.focus();
        return;
      }
      searchBtn.disabled = true;
      searchBtn.textContent = "Suche …";
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`;
        const r = await fetch(url, {
          headers: { Accept: "application/json" },
        });
        const arr = (await r.json()) as Array<{
          lat: string;
          lon: string;
          display_name: string;
          address?: {
            city?: string;
            town?: string;
            village?: string;
            country?: string;
          };
        }>;
        if (arr.length === 0) {
          renderStatus(null, `Stadt "${q}" nicht gefunden.`);
          return;
        }
        const hit = arr[0];
        if (!hit) {
          renderStatus(null, `Stadt "${q}" nicht gefunden.`);
          return;
        }
        const city =
          hit.address?.city ?? hit.address?.town ?? hit.address?.village ?? q;
        const saved = await saveUserLocation({
          lat: parseFloat(hit.lat),
          lng: parseFloat(hit.lon),
          city,
          country: hit.address?.country,
        });
        renderStatus(saved);
        manualWrap.classList.add("hidden");
      } catch (err) {
        renderStatus(
          null,
          `Suche fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`,
        );
      } finally {
        searchBtn.disabled = false;
        searchBtn.textContent = "Suchen";
      }
    };
    searchBtn.addEventListener("click", () => void lookupCity());
    cityInput.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter") void lookupCity();
    });

    enableBtn.addEventListener("click", async () => {
      if (!("geolocation" in navigator)) {
        renderStatus(
          null,
          "Browser unterstützt keine Standort-Abfrage. Bitte Stadt manuell eingeben.",
        );
        manualWrap.classList.remove("hidden");
        return;
      }
      enableBtn.disabled = true;
      enableBtn.textContent = "Bitte im Browser erlauben …";
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const saved = await saveUserLocation({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
            });
            renderStatus(saved);
            manualWrap.classList.add("hidden");
            // Pre.760: nach Freigabe sofort automatisch aktuell halten (wenn an).
            if (isAutoLocationEnabled()) startGeoWatch();
          } catch (err) {
            renderStatus(
              null,
              `Speichern fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`,
            );
            manualWrap.classList.remove("hidden");
          } finally {
            enableBtn.disabled = false;
          }
        },
        (err) => {
          // Pre.240: Bei Browser-Fehler manuellen Fallback zeigen
          const hint =
            err.code === err.PERMISSION_DENIED
              ? "Browser hat Standort-Permission abgelehnt"
              : err.code === err.POSITION_UNAVAILABLE
                ? "Position nicht verfügbar — prüfe macOS Systemeinstellungen → Datenschutz & Sicherheit → Ortungsdienste → Chrome aktivieren"
                : err.code === err.TIMEOUT
                  ? "Timeout — bitte nochmal versuchen"
                  : err.message;
          renderStatus(null, hint);
          manualWrap.classList.remove("hidden");
          enableBtn.disabled = false;
          enableBtn.textContent = "Standort freigeben";
        },
        { enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 },
      );
    });

    removeBtn.addEventListener("click", async () => {
      const { confirmSheet } = await import("./sheet-ui.js");
      const ok = await confirmSheet({
        title: "Standort löschen?",
        confirmLabel: "Löschen",
        danger: true,
      });
      if (!ok) return;
      await deleteUserLocation();
      renderStatus(null);
    });

    void refresh();
    return wrap;
  }

  private extensionBlock(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.className = "settings-extension";

    const head = document.createElement("h3");
    head.textContent = "Browser-Erweiterung";
    wrap.appendChild(head);

    const intro = document.createElement("p");
    intro.className = "settings-modules-hint";
    intro.textContent =
      "Verbinde die Vespera Chrome-Extension, dann kann der Bot Tabs öffnen, Seiten lesen, klicken und scrollen — direkt in deinem Browser.";
    wrap.appendChild(intro);

    // Pre.210: Doppelter Download-Block aus pre.206 entfernt — die Card
    // unten hat schon "ZIP herunterladen" + 6-Schritt-Liste.

    // 22.10.2026: Kopf aus dem Baustein der Verbindungs-Karten (settings/verbindungs-karte.ts).
    const { karte: card, status } = verbindungsKarte({ titel: "Chrome-Extension", unter: "Tabs · Lesen · Klicken · Tippen · Scrollen" });

    /*
     * 07.10.2026 (Plan „mehrere Geräte“, S6): alle Browser als Liste — DIESELBE Liste wie bei den Macs
     * (frontend/lib/mac-geraete-liste.ts, art "browser"): wer arbeitet, wer ist bereit, Hier arbeiten, Umbenennen,
     * Trennen mit Rückfrage IN der Seite (vorher confirm()), Wieder automatisch. Nach einer Aktion kommt der Stand neu.
     */
    const geraeteListe = baueGeraeteListe({ art: "browser", nachher: () => void refresh() });
    geraeteListe.element.hidden = true;
    card.appendChild(geraeteListe.element);
    // Die Meldung nach einer Aktion steht neben (nicht in) der Liste: sie bleibt, wenn der letzte Browser geht.
    card.appendChild(geraeteListe.meldung);

    // Ticket 2816: welche Version läuft, welche ist aktuell. Mit der Liste steht die Zeile je Browser; diese eine
    // bleibt für einen älteren Server ohne Liste.
    const extVersion = versionZeile(null, erweiterungStand({ verbunden: false, bei: null, aktuell: null }));
    extVersion.hidden = true;
    card.appendChild(extVersion);

    // Ticket 2823: derselbe Baustein wie in der Karte „Vespera Desktop“.
    const kopplung = baueKopplungsCode("Pairing-Code");

    // Install-Hilfe: Download-ZIP + kurze Anleitung. Immer sichtbar (auch mit verbundenen Browsern): das Update auf eine
    // neue Fassung geht über genau diese ZIP — der Hinweis an einer Zeile sagt „unten neu herunterladen“.
    const install = document.createElement("div");
    install.className = "ext-install";
    const installTitle = document.createElement("span");
    installTitle.className = "ext-install-title";
    installTitle.textContent = "Extension installieren";
    install.appendChild(installTitle);

    const dl = document.createElement("a");
    dl.className = "ext-install-download";
    dl.href = "/vespera-extension.zip";
    dl.download = "vespera-extension.zip";
    const dlIcon = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg",
    );
    dlIcon.setAttribute("viewBox", "0 0 24 24");
    dlIcon.setAttribute("width", "16");
    dlIcon.setAttribute("height", "16");
    dlIcon.setAttribute("aria-hidden", "true");
    const dlPath = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "path",
    );
    dlPath.setAttribute("fill", "currentColor");
    dlPath.setAttribute("d", "M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z");
    dlIcon.appendChild(dlPath);
    dl.appendChild(dlIcon);
    const dlLabel = document.createElement("span");
    dlLabel.textContent = "ZIP herunterladen";
    dl.appendChild(dlLabel);
    // Ticket 2396, Nachprüfung Build 77: die Chrome-Erweiterung gibt es nur
    // am Rechner — in der App kein Knopf, der ins Leere führt.
    if (!isNativePlatform()) install.appendChild(dl);

    const steps = document.createElement("ol");
    steps.className = "ext-install-steps";
    const stepsItems = [
      "ZIP entpacken",
      "Chrome → chrome://extensions",
      "Entwicklermodus AN (oben rechts)",
      "Entpackte Erweiterung laden → Ordner wählen",
      "Vespera-Stern in der Symbolleiste anpinnen",
      "Code oben generieren + ins Popup eingeben",
    ];
    for (const txt of stepsItems) {
      const li = document.createElement("li");
      li.textContent = txt;
      steps.appendChild(li);
    }
    install.appendChild(steps);

    const actions = document.createElement("div");
    actions.className = "connector-actions";
    const genBtn = document.createElement("button");
    genBtn.type = "button";
    genBtn.className = "connector-connect";
    genBtn.textContent = "Pairing-Code generieren";
    actions.appendChild(genBtn);

    /*
     * 07.10.2026 (S6): Verbinden ist mit der Liste ein eigener Bereich — wie bei den Macs. Ist schon ein Browser
     * verbunden, steht er hinter „+ Weiteren Browser verbinden“; ist keiner verbunden, steht er offen da. Bei vier
     * verbundenen gibt es den Knopf nicht (ein fünfter würde abgewiesen), der Satz sagt es.
     */
    const weiterer = document.createElement("button");
    weiterer.type = "button";
    weiterer.className = "mac-verbinden-auf";
    weiterer.hidden = true;
    const plus = document.createElement("span");
    plus.className = "mac-verbinden-plus";
    plus.setAttribute("aria-hidden", "true");
    plus.textContent = "+";
    const weitererText = document.createElement("span");
    weitererText.textContent = "Weiteren Browser verbinden";
    weiterer.append(plus, weitererText);
    const vier = document.createElement("p");
    vier.className = "mac-verbinden-grenze";
    vier.textContent = vierHinweis("browser");
    vier.hidden = true;
    const verbinden = document.createElement("div");
    verbinden.className = "mac-verbinden";
    verbinden.id = `browser-verbinden-${Math.random().toString(36).slice(2, 10)}`;
    weiterer.setAttribute("aria-controls", verbinden.id);
    weiterer.setAttribute("aria-expanded", "true");
    verbinden.append(actions, kopplung.box);
    card.append(weiterer, vier, verbinden, install);
    wrap.appendChild(card);

    let verbindenGewaehlt = false;
    const zeichneVerbinden = (zuklappbar: boolean, voll = false): void => {
      weiterer.hidden = !zuklappbar || voll;
      verbinden.hidden = zuklappbar && (voll || !verbindenGewaehlt);
      weiterer.setAttribute("aria-expanded", String(!verbinden.hidden));
    };
    weiterer.addEventListener("click", () => {
      verbindenGewaehlt = verbinden.hidden;
      zeichneVerbinden(true);
      if (!verbinden.hidden) genBtn.focus();
    });

    /** Das kleine Wort oben rechts: „nicht verbunden“, „online“, „2 verbunden“ — nur aus dem Stand. */
    const zeichneKopf = (text: string, art?: "ok" | "warn"): void => {
      status.textContent = text;
      if (art) status.dataset.kind = art;
      else delete status.dataset.kind;
    };

    let letzterStatus: ExtensionStatus | null = null;
    const apply = (s: ExtensionStatus, abgefragtUm?: number): void => {
      letzterStatus = s;
      if (!s.configured) {
        zeichneKopf("Server-Setup fehlt", "warn");
        genBtn.disabled = true;
        geraeteListe.element.hidden = true;
        extVersion.hidden = true;
        zeichneVerbinden(false);
        return;
      }
      genBtn.disabled = false;
      if (!Array.isArray(s.geraete)) {
        // Älterer Server ohne Liste: der Stand des einen Browsers wie bisher (ohne Trennen — das geht je Zeile).
        zeichneKopf(s.connected ? "online" : s.paired ? "verbunden, aber offline" : "nicht verbunden", s.connected ? "ok" : s.paired ? "warn" : undefined);
        versionZeile(extVersion, erweiterungStand({ verbunden: s.connected, bei: s.version ?? null, aktuell: s.aktuell ?? null }));
        extVersion.hidden = false;
        geraeteListe.element.hidden = true;
        zeichneVerbinden(false);
        return;
      }
      const geraete = s.geraete.map(browserAlsGeraet);
      const verbundene = geraete.filter((g) => g.verbunden).length;
      zeichneKopf(
        verbundene === 0 ? (geraete.length ? "verbunden, aber offline" : "nicht verbunden") : verbundene === 1 ? "online" : `${verbundene} verbunden`,
        verbundene > 0 ? "ok" : geraete.length ? "warn" : undefined,
      );
      extVersion.hidden = true;
      geraeteListe.element.hidden = geraete.length === 0;
      geraeteListe.zeichne({ geraete, wahl: s.wahl ?? null, aktuell: s.aktuell }, abgefragtUm);
      if (verbundene === 0) verbindenGewaehlt = false;
      zeichneVerbinden(verbundene > 0, verbundene >= MAC_HOECHSTENS);
      vier.hidden = verbundene < MAC_HOECHSTENS;
    };

    const refresh = async (): Promise<void> => {
      const um = performance.now();
      try {
        apply(await fetchExtensionStatus(), um);
      } catch {
        // Keine rohe Fehlernummer: was los ist und dass es weitergeht. Eine schon gezeigte Liste bleibt stehen.
        zeichneKopf("Stand gerade nicht abrufbar", "warn");
      }
    };

    let pollTimer: number | null = null;
    const stopPoll = (): void => {
      if (pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    genBtn.addEventListener("click", async () => {
      genBtn.disabled = true;
      try {
        const c = await generatePairingCode();
        kopplung.zeige(c.code, c.expiresAt, "Code in Extension-Popup eingeben.");
        // Alle 3 s nachsehen, ob sich ein Browser NEU verbunden hat (mac-geraete.ts neueVerbindung) — dann geht der
        // Code weg. Ein schon verbundener, der sich nur neu meldet, zählt nicht.
        const vorher = letzterStatus;
        const ende = Date.now() + CODE_GUELTIG_MS;
        stopPoll();
        pollTimer = window.setInterval(async () => {
          if (!wrap.isConnected || Date.now() > ende) {
            stopPoll();
            return;
          }
          const um = performance.now();
          try {
            const s = await fetchExtensionStatus();
            if (neueVerbindung(vorher, s)) {
              stopPoll();
              kopplung.verstecke();
              verbindenGewaehlt = false;
            }
            apply(s, um);
          } catch {
            /* ignore — nächster Tick versucht's wieder */
          }
        }, 3000);
      } catch {
        zeichneKopf("Code gerade nicht erzeugbar", "warn");
      } finally {
        genBtn.disabled = false;
      }
    });

    void refresh();
    // Wie bei den Macs: der Stand kommt alle 10 s neu, solange die Ansicht offen ist (gesperrt, bereit, wer arbeitet).
    const statusPoll = window.setInterval(
      gesteuert(
        "live",
        () => {
          if (!wrap.isConnected) {
            window.clearInterval(statusPoll);
            return;
          }
          void refresh();
        },
        true,
      ),
      10_000,
    );
    return wrap;
  }

  private connectorsBlock(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.className = "settings-connectors";

    const head = document.createElement("h3");
    head.textContent = "Verbindungen";
    wrap.appendChild(head);

    const intro = document.createElement("p");
    intro.className = "settings-modules-hint";
    intro.textContent =
      "Verknüpfe externe Dienste, damit dein Bot sie nutzen kann. Die Tokens bleiben in deiner persönlichen DB.";
    wrap.appendChild(intro);

    // 22.10.2026: Kopf, Zustand und Stichworte aus EINEM Baustein (settings/verbindungs-karte.ts).
    const { karte: card, status } = verbindungsKarte({ titel: "Google Workspace", unter: "Gmail · Kalender · Drive" });

    const actions = document.createElement("div");
    actions.className = "connector-actions";
    const connectBtn = document.createElement("button");
    connectBtn.type = "button";
    connectBtn.className = "connector-connect";
    connectBtn.textContent = "Verbinden";
    actions.appendChild(connectBtn);

    const disconnectBtn = document.createElement("button");
    disconnectBtn.type = "button";
    disconnectBtn.className = "connector-disconnect";
    disconnectBtn.textContent = "Trennen";
    disconnectBtn.hidden = true;
    actions.appendChild(disconnectBtn);
    card.appendChild(actions);

    wrap.appendChild(card);

    const apply = (state: ConnectorsState): void => {
      const g = state.google;
      if (!g.configured) {
        status.textContent = "Server-Setup fehlt";
        status.dataset.kind = "warn";
        connectBtn.disabled = true;
        connectBtn.title = "GOOGLE_CLIENT_ID/SECRET in .env setzen";
        disconnectBtn.hidden = true;
        return;
      }
      if (g.connected) {
        status.textContent = "verbunden";
        status.dataset.kind = "ok";
        connectBtn.hidden = true;
        disconnectBtn.hidden = false;
      } else {
        status.textContent = "nicht verbunden";
        delete status.dataset.kind;
        connectBtn.hidden = false;
        connectBtn.disabled = false;
        disconnectBtn.hidden = true;
      }
    };

    const refresh = async (): Promise<void> => {
      try {
        const state = await fetchConnectors();
        apply(state);
      } catch (err) {
        status.textContent = `Fehler: ${err instanceof Error ? err.message : String(err)}`;
        status.dataset.kind = "warn";
      }
    };

    connectBtn.addEventListener("click", () => {
      // Popup öffnen — User durchläuft Google-Consent. Beim Erfolg postet
      // der Callback eine Message ans opener-Window, dann refreshen wir.
      const popup = window.open(
        "/api/oauth/google/connect",
        "voice-v8-oauth",
        "width=520,height=620",
      );
      const onMsg = (ev: MessageEvent): void => {
        if (ev.origin !== window.location.origin) return;
        if (
          (ev.data as { type?: string })?.type ===
          "voice-v8.oauth.google.connected"
        ) {
          window.removeEventListener("message", onMsg);
          void refresh();
        }
      };
      window.addEventListener("message", onMsg);
      // Fallback: wenn das Popup geschlossen wird (auch ohne Erfolg),
      // pollen wir trotzdem nach 1s.
      const checker = setInterval(() => {
        if (!popup || popup.closed) {
          clearInterval(checker);
          window.removeEventListener("message", onMsg);
          void refresh();
        }
      }, 800);
    });

    disconnectBtn.addEventListener("click", async () => {
      if (!confirm("Google-Verbindung wirklich trennen?")) return;
      disconnectBtn.disabled = true;
      try {
        await disconnectGoogle();
        await refresh();
      } finally {
        disconnectBtn.disabled = false;
      }
    });

    void refresh();

    // --- WHOOP (#386, 2026-07-20): zweite Karte nach demselben Muster. ---
    // Das Band kommt heute; sobald WHOOP_CLIENT_ID/SECRET/REDIRECT_URI auf
    // dem Server stehen, verbindet Martin hier mit EINEM Tipp.
    const { karte: wCard, status: wStatus } = verbindungsKarte({ titel: "WHOOP", unter: "Erholung \u00b7 Schlaf \u00b7 Belastung" });
    const wActions = document.createElement("div");
    wActions.className = "connector-actions";
    const wConnect = document.createElement("button");
    wConnect.type = "button";
    wConnect.className = "connector-connect";
    wConnect.textContent = "Verbinden";
    wActions.appendChild(wConnect);
    const wDisconnect = document.createElement("button");
    wDisconnect.type = "button";
    wDisconnect.className = "connector-disconnect";
    wDisconnect.textContent = "Trennen";
    wDisconnect.hidden = true;
    wActions.appendChild(wDisconnect);
    wCard.appendChild(wActions);
    wrap.appendChild(wCard);

    const wRefresh = async (): Promise<void> => {
      try {
        const r = await fetch("/api/whoop/status", {
          credentials: "same-origin",
        });
        if (r.status === 503) {
          wStatus.textContent = "Server-Setup fehlt";
          wStatus.dataset.kind = "warn";
          wConnect.disabled = true;
          wConnect.title =
            "WHOOP_CLIENT_ID/SECRET/REDIRECT_URI in .env setzen";
          wDisconnect.hidden = true;
          return;
        }
        const j = (await r.json()) as { connected?: boolean };
        if (j.connected) {
          wStatus.textContent = "verbunden";
          wStatus.dataset.kind = "ok";
          wConnect.hidden = true;
          wDisconnect.hidden = false;
        } else {
          wStatus.textContent = "nicht verbunden";
          delete wStatus.dataset.kind;
          wConnect.hidden = false;
          wConnect.disabled = false;
          wDisconnect.hidden = true;
        }
      } catch (err) {
        wStatus.textContent = `Fehler: ${err instanceof Error ? err.message : String(err)}`;
        wStatus.dataset.kind = "warn";
      }
    };

    wConnect.addEventListener("click", () => {
      const popup = window.open(
        "/api/whoop/connect",
        "voice-v8-whoop",
        "width=520,height=680",
      );
      const onMsg = (ev: MessageEvent): void => {
        if (ev.origin !== window.location.origin) return;
        if (
          (ev.data as { type?: string })?.type === "voice-v8.whoop.connected"
        ) {
          window.removeEventListener("message", onMsg);
          void wRefresh();
        }
      };
      window.addEventListener("message", onMsg);
      const checker = setInterval(() => {
        if (!popup || popup.closed) {
          clearInterval(checker);
          window.removeEventListener("message", onMsg);
          void wRefresh();
        }
      }, 800);
    });

    wDisconnect.addEventListener("click", async () => {
      if (!confirm("WHOOP-Verbindung wirklich trennen?")) return;
      wDisconnect.disabled = true;
      try {
        await fetch("/api/whoop/disconnect", {
          method: "POST",
          credentials: "same-origin",
        });
        await wRefresh();
      } finally {
        wDisconnect.disabled = false;
      }
    });

    void wRefresh();

    // 28.09.2026 (Ticket 2713): das eigene Konto im SSI-Panel — für jeden Nutzer.
    // 06.10.2026 (Martin): eingeklappt — das Panel braucht fast niemand täglich.
    // 22.10.2026 (Martin: „Da geht Panel ganz unter“): dieselbe Karte wie Google und WHOOP, zum Aufklappen in sich selbst.
    wrap.appendChild(createPanelKarte());

    // ── 2026-08-27: CLI und Browser-Erweiterung ziehen aus der Fusszeile hierher.
    //
    // Martin: "den gruenen CLI-Punkt brauche ich eigentlich auch nicht mehr,
    // weil ich kaum mit CLI etwas machen lasse. Gegebenenfalls koennten wir den
    // woanders hin, genauso wie die Chrome Extension … muss nicht unbedingt in
    // der Fusszeile diese Information stehen."
    //
    // Also nicht geloescht, sondern umgezogen — hierhin, wo alles steht, was
    // mit dem System verbunden ist.
    //
    // ⚠️ Die vorhandenen Elemente werden UMGEHAENGT, nicht nachgebaut:
    // #status-cli pollt /api/cli/health (app.ts), #status-ext haengt am
    // Erweiterungs-Handschlag. Ein Nachbau haette angefangen auseinanderzulaufen.
    // appendChild verschiebt einen vorhandenen Knoten — auch beim zweiten
    // Oeffnen bleibt es bei einem.
    // ⚠️ NUR die CLI. Die Browser-Erweiterung hat hier laengst eine eigene
    // Karte ("Chrome-Extension", weiter oben) mit eigenem Zustandstext — eine
    // zweite haette dasselbe zweimal gesagt. Ihr Fuss-Anzeiger (#status-ext)
    // faellt ersatzlos weg; die Karte sagt es besser.
    for (const [id, titel, sub] of [
      ["status-cli", "Claude CLI", "Hintergrund-Aufträge · grüner Punkt = erreichbar"],
    ] as ReadonlyArray<readonly [string, string, string]>) {
      const el = document.getElementById(id);
      if (!el) continue;
      /*
       * ⚠️⚠️ 22.09.2026 — GELIEHEN, ALSO MUSS ES ZURUECK.
       *
       * Dieser Knoten wird aus der Statusleiste UMGEHAENGT (richtig so, er
       * wird von `pollCliHealth` in app.ts ueber seine ID gefuettert). Beim
       * naechsten Reiterwechsel leert `renderActive()` aber den ganzen
       * Koerper — und damit war der Knoten endgueltig aus dem Dokument.
       *
       * Folge, unabhaengig geprueft: `pollCliHealth` findet ihn nicht mehr
       * und kehrt still zurueck; beim naechsten Oeffnen greift oben das
       * `if (!el) continue` — die Karte „Claude CLI" ist bis zum Neuladen
       * der Seite weg. Ein geliehener Knoten braucht eine Rueckgabe.
       */
      el.dataset.geliehenVon = el.parentElement?.id || "";
      if (!el.parentElement?.id && el.parentElement) {
        el.parentElement.id = "geliehen-heimat-" + id;
        el.dataset.geliehenVon = el.parentElement.id;
      }
      // Nicht zum Aufklappen: die Karte hat nichts darunter — ein Pfeil ohne Inhalt wäre ein leeres Versprechen.
      const { karte, status: zustand } = verbindungsKarte({ titel, unter: sub, status: "" });
      zustand.appendChild(el);
      wrap.appendChild(karte);
    }

    return wrap;
  }

  /**
   * Pre.335: Profil-Tab — verbindliche User-Stammdaten.
   *
   * Adressiert Martins Beschwerde 2026-05-17: "Er ist ungenau bei
   * Aussagen, soll Kontakte/Adresse verwenden statt halluzinieren."
   * Felder werden im Backend in users-Spalten gespeichert und vom
   * Persona-Prompt als harter "USER-PROFIL"-Block oben gerendert.
   */
  private profileBlock(): HTMLElement {
    // Pre.423: Identity-Tab benutzt Card-Layout wie Sprache-Tab.
    const wrap = document.createElement("section");
    wrap.className = "settings-subsection settings-subsection--profile";

    const head = document.createElement("h3");
    head.className = "settings-subsection-title";
    head.textContent = "Profil";
    wrap.appendChild(head);

    // 2026-08-27 (Martin: "Einstellungen designtechnisch reformiert"):
    // Sechs Zeilen Erklaerung standen vor dem ersten Feld, dann noch ein
    // zweiter Kasten mit drei weiteren. Zusammen elf Zeilen Text, bevor man
    // seinen Vornamen eintippen konnte. Gekuerzt auf das, was man wissen
    // muss — kein Fakt ist verloren, nur die Wiederholung.
    const intro = document.createElement("p");
    intro.className = "settings-modules-hint";
    intro.textContent =
      "Der Bot nimmt diese Werte, statt zu raten — als Absender bei E-Mails, als Startpunkt bei Routen.";
    wrap.appendChild(intro);

    /*
     * 24.09.2026, Ticket 2570: zwei Hüllen — am Rechner zwei Karten
     * nebeneinander (Profilbild und „Per Bot ausfüllen" | die Felder). Am
     * Telefon und Tablet sind sie `display: contents`: die Kinder stehen dort
     * genau wie vorher, Punkt für Punkt.
     */
    const bildKarte = document.createElement("div");
    bildKarte.className = "einst-profil-bild";
    const personKarte = document.createElement("div");
    personKarte.className = "einst-profil-person";
    wrap.append(bildKarte, personKarte);

    // Pre.336: Wizard-Button — Bot fragt mich konversationell ab.
    // Klick wechselt zur Chat-Ansicht, baut Voice-Connection auf und
    // schickt dem Bot einen klaren Onboarding-Trigger-Prompt.
    // ⚠️ 2026-08-27: Hier stand eine KARTE IN DER KARTE — der Profil-Block ist
    // schon eine, und darin sass noch ein Kasten mit eigenem Rahmen. Zwei
    // Rahmen um einen Gedanken.
    //
    // Und der Knopf darin war `settings-save`, also die volle Akzentflaeche:
    // das Lauteste der ganzen Seite fuer den NEBENweg. Wer hierher kommt, will
    // in der Regel tippen; sich durchfragen lassen ist das Angebot daneben.
    // Jetzt eine Zeile ohne eigenen Rahmen, der Knopf leise.
    const wizardBox = document.createElement("div");
    wizardBox.className = "settings-wizard-zeile";
    const wizardText = document.createElement("p");
    wizardText.className = "settings-wizard-text";
    wizardText.textContent = "Lieber durchfragen lassen? Der Bot geht die Felder mit dir durch.";
    wizardBox.appendChild(wizardText);
    const wizardBtn = document.createElement("button");
    wizardBtn.type = "button";
    wizardBtn.className = "btn btn--ghost settings-wizard-btn";
    wizardBtn.textContent = "Per Bot ausfüllen";
    wizardBtn.addEventListener("click", () => {
      // Triggert die Onboarding-Phrase in app.ts. Dort wird ein
      // pendingPrompt gesetzt + zur Chat-View gewechselt + Connect.
      window.dispatchEvent(
        new CustomEvent("voice-v8:profile-onboarding-start"),
      );
    });
    wizardBox.appendChild(wizardBtn);
    bildKarte.appendChild(wizardBox);

    // Pre.341: Avatar-Block ganz oben. Klick auf den Kreis öffnet
    // File-Picker. Nach Upload landet das Bild via /api/upload in
    // uploads-Tabelle, die ID wird beim Speichern via PUT
    // /api/user/profile referenziert. Beim erneuten Laden ist
    // profile.avatarUrl gesetzt und zeigt das Bild.
    const avatarRow = document.createElement("div");
    avatarRow.className = "settings-profile-avatar-row";
    const avatarRing = document.createElement("button");
    avatarRing.type = "button";
    avatarRing.className = "settings-profile-avatar-ring";
    avatarRing.setAttribute("aria-label", "Profilbild ändern");
    const avatarImg = document.createElement("img");
    avatarImg.className = "settings-profile-avatar-img";
    avatarImg.alt = "";
    avatarImg.hidden = true;
    avatarRing.appendChild(avatarImg);
    const avatarInitial = document.createElement("span");
    avatarInitial.className = "settings-profile-avatar-initial";
    avatarInitial.textContent = "?";
    avatarRing.appendChild(avatarInitial);
    const avatarBadge = document.createElement("span");
    avatarBadge.className = "settings-profile-avatar-badge";
    avatarBadge.setAttribute("aria-hidden", "true");
    avatarBadge.textContent = "+";
    avatarRing.appendChild(avatarBadge);
    const avatarHint = document.createElement("div");
    avatarHint.className = "settings-profile-avatar-hint";
    const avatarHintLine = document.createElement("div");
    avatarHintLine.textContent = "Profilbild";
    avatarHintLine.className = "settings-profile-avatar-label";
    avatarHint.appendChild(avatarHintLine);
    const avatarSub = document.createElement("div");
    avatarSub.className = "settings-profile-avatar-sub";
    avatarSub.textContent = "Tippen zum Hochladen — JPG/PNG, max 8 MB";
    avatarHint.appendChild(avatarSub);
    avatarRow.appendChild(avatarRing);
    avatarRow.appendChild(avatarHint);
    bildKarte.appendChild(avatarRow);
    // Verstecktes File-Input — nicht im Layout, nur per Klick triggered.
    const avatarFile = document.createElement("input");
    avatarFile.type = "file";
    avatarFile.accept = "image/jpeg,image/png,image/webp";
    avatarFile.hidden = true;
    bildKarte.appendChild(avatarFile);
    // State: aktuelle Avatar-Upload-ID (wird beim Speichern mitgesendet).
    let currentAvatarUploadId: string | null = null;
    avatarRing.addEventListener("click", () => avatarFile.click());
    avatarFile.addEventListener("change", () => {
      const file = avatarFile.files?.[0];
      if (!file) return;
      if (file.size > 8 * 1024 * 1024) {
        statusEl.textContent = "Bild zu groß (max 8 MB)";
        statusEl.dataset.kind = "error";
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        // Preview sofort zeigen.
        avatarImg.src = String(reader.result ?? "");
        avatarImg.hidden = false;
        avatarInitial.hidden = true;
      };
      reader.readAsDataURL(file);
      // Hochladen.
      void (async () => {
        try {
          avatarRing.classList.add("is-loading");
          const form = new FormData();
          form.append("file", file);
          const res = await fetch("/api/upload", {
            method: "POST",
            body: form,
            credentials: "same-origin",
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = (await res.json()) as { id: string; url: string };
          currentAvatarUploadId = data.id;
          // Vom Server gelieferte URL nutzen (statt data:URL) damit
          // Cache + CDN greift.
          if (data.url) avatarImg.src = data.url;
          // Pre.371: Auto-Save direkt nach Upload — User mussten vorher
          // selbst den Speichern-Button drücken und vergaßen das oft,
          // dann war beim Reload das Bild weg. Jetzt persistiert das
          // Upload sofort, nur die Form-Felder brauchen weiterhin
          // Speichern-Klick (weil User vielleicht noch tippt).
          try {
            const saveRes = await fetch("/api/user/profile", {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              credentials: "same-origin",
              body: JSON.stringify({ avatarUploadId: data.id }),
            });
            if (saveRes.ok) {
              statusEl.textContent = "Profilbild gespeichert.";
              statusEl.dataset.kind = "success";
            } else {
              statusEl.textContent =
                "Bild hochgeladen — bitte „Speichern“ klicken.";
              statusEl.dataset.kind = "success";
            }
          } catch {
            statusEl.textContent =
              "Bild hochgeladen — bitte „Speichern“ klicken.";
            statusEl.dataset.kind = "success";
          }
        } catch (e) {
          statusEl.textContent =
            "Upload fehlgeschlagen: " +
            (e instanceof Error ? e.message : String(e));
          statusEl.dataset.kind = "error";
          avatarImg.hidden = true;
          avatarInitial.hidden = false;
        } finally {
          avatarRing.classList.remove("is-loading");
        }
      })();
    });

    // Hilfs-Factory für einheitliche Felder
    const makeField = (
      labelText: string,
      placeholder: string,
      maxLen: number,
      inputType: "text" | "date" | "tel" = "text",
    ): { label: HTMLLabelElement; input: HTMLInputElement } => {
      const label = document.createElement("label");
      label.className = "settings-field";
      const labelSpan = document.createElement("span");
      labelSpan.className = "settings-label";
      labelSpan.textContent = labelText;
      label.appendChild(labelSpan);
      const input = document.createElement("input");
      input.type = inputType;
      input.maxLength = maxLen;
      input.placeholder = placeholder;
      input.autocomplete = "off";
      input.spellcheck = false;
      label.appendChild(input);
      return { label, input };
    };

    const firstName = makeField("Vorname", "z.B. Martin", 80);
    const lastName = makeField("Nachname", "z.B. Mollay", 80);
    const phone = makeField("Telefon", "+43 …", 40, "tel");
    const street = makeField("Straße + Nr.", "z.B. Auestraße 33", 120);
    const zip = makeField("PLZ", "z.B. 5020", 20);
    const city = makeField("Ort", "z.B. Salzburg", 80);
    const country = makeField("Land", "z.B. Österreich", 60);
    const birthday = makeField("Geburtstag", "YYYY-MM-DD", 10, "date");

    /*
     * Ticket 2570: jedes Feld trägt seinen Namen. Das Raster am Rechner
     * stellt PLZ, Ort, Land und Geburtstag über diesen Namen in eine Reihe,
     * nicht über ihre Stelle — ein neues Feld verschöbe sonst stumm die Reihe.
     */
    for (const [feld, f] of [
      ["vorname", firstName],
      ["nachname", lastName],
      ["telefon", phone],
      ["strasse", street],
      ["plz", zip],
      ["ort", city],
      ["land", country],
      ["geburtstag", birthday],
    ] as const) {
      f.label.dataset.feld = feld;
      personKarte.appendChild(f.label);
    }

    // Notizen — Freitext-Feld für alles was kein eigenes Schema-Feld hat
    // (z.B. Allergien, bevorzugte Sprache, Spitzname).
    const notesLabel = document.createElement("label");
    // Pre.429: marker-class für Grid-full-width
    notesLabel.className = "settings-field settings-field-textarea";
    const notesLabelText = document.createElement("span");
    notesLabelText.className = "settings-label";
    notesLabelText.textContent = "Zusätzliche Notizen (frei)";
    notesLabel.appendChild(notesLabelText);
    const notes = document.createElement("textarea");
    notes.className = "settings-persona-soul";
    notes.rows = 3;
    notes.maxLength = 2000;
    notes.placeholder =
      "Allergien, Spitzname, bevorzugte Anrede, Sprachpräferenzen, …";
    notesLabel.appendChild(notes);
    personKarte.appendChild(notesLabel);

    // Pre.429: Save-Button in settings-actions-Wrapper (rechts-bündig, full-width)
    const actions = document.createElement("div");
    actions.className = "settings-actions";
    const saveBtn = document.createElement("button");
    saveBtn.type = "button";
    saveBtn.className = "settings-save";
    saveBtn.textContent = "Profil speichern";
    actions.appendChild(saveBtn);
    personKarte.appendChild(actions);

    const statusEl = document.createElement("div");
    statusEl.className = "settings-inline-status";
    personKarte.appendChild(statusEl);

    // Initial-Daten vom Server laden.
    void (async () => {
      try {
        const res = await fetch("/api/user/profile", {
          credentials: "same-origin",
        });
        if (!res.ok) return;
        const data = (await res.json()) as {
          profile: {
            firstName: string | null;
            lastName: string | null;
            phone: string | null;
            addressStreet: string | null;
            addressZip: string | null;
            addressCity: string | null;
            addressCountry: string | null;
            birthday: string | null;
            profileNotes: string | null;
            avatarUploadId: string | null;
            avatarUrl: string | null;
          } | null;
        };
        const p = data.profile;
        if (!p) return;
        firstName.input.value = p.firstName ?? "";
        lastName.input.value = p.lastName ?? "";
        phone.input.value = p.phone ?? "";
        street.input.value = p.addressStreet ?? "";
        zip.input.value = p.addressZip ?? "";
        city.input.value = p.addressCity ?? "";
        country.input.value = p.addressCountry ?? "";
        birthday.input.value = p.birthday ?? "";
        notes.value = p.profileNotes ?? "";
        // Pre.341: Avatar laden falls schon hochgeladen, sonst Initial-Buchstabe.
        currentAvatarUploadId = p.avatarUploadId;
        if (p.avatarUrl) {
          avatarImg.src = p.avatarUrl;
          avatarImg.hidden = false;
          avatarInitial.hidden = true;
        } else {
          const initial = (p.firstName ?? "").trim().charAt(0).toUpperCase();
          if (initial) avatarInitial.textContent = initial;
        }
      } catch {
        /* fail-quiet */
      }
    })();

    saveBtn.addEventListener("click", async () => {
      saveBtn.disabled = true;
      saveBtn.textContent = "Speichere…";
      statusEl.textContent = "";
      try {
        const payload = {
          firstName: firstName.input.value.trim() || null,
          lastName: lastName.input.value.trim() || null,
          phone: phone.input.value.trim() || null,
          addressStreet: street.input.value.trim() || null,
          addressZip: zip.input.value.trim() || null,
          addressCity: city.input.value.trim() || null,
          addressCountry: country.input.value.trim() || null,
          birthday: birthday.input.value.trim() || null,
          profileNotes: notes.value.trim() || null,
          avatarUploadId: currentAvatarUploadId,
        };
        const res = await fetch("/api/user/profile", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        statusEl.textContent =
          kontoGruppen().anrufModell
            ? "Profil gespeichert. Bot übernimmt es ab dem nächsten Anruf."
            : "Profil gespeichert.";
        statusEl.dataset.kind = "success";
      } catch (e) {
        statusEl.textContent =
          "Speichern fehlgeschlagen: " +
          (e instanceof Error ? e.message : String(e));
        statusEl.dataset.kind = "error";
      } finally {
        saveBtn.disabled = false;
        saveBtn.textContent = "Profil speichern";
      }
    });

    return wrap;
  }

  /**
   * Pre.443: Biografie-Foundation — User definiert seine Lebens-
   * Schwerpunkte (Themen, auf die der Bot sich konzentrieren soll).
   * Chip-Input: existierende als Pillen, Eingabefeld unten, Enter fügt
   * hinzu, Klick auf Pille entfernt sie.
   */
  // Ticket #501 (23.07.): Achtsamkeits-Waechter — Master + Kategorien.
  private achtsamkeitBlock(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "settings-card";
    const h = document.createElement("h3");
    h.textContent = "Achtsamkeits-Wächter";
    wrap.appendChild(h);
    const sub = document.createElement("p");
    sub.className = "settings-hint";
    sub.textContent =
      "Hinweis bei negativer Selbst-Sprache in der Tagesaufnahme — " +
      "höchstens einer pro 10 Minuten, sechs pro Tag.";
    wrap.appendChild(sub);

    const KATS: Array<[string, string]> = [
      ["selbstabwertung", "Selbstabwertung"],
      ["verallgemeinerung", "Verallgemeinerung"],
      ["katastrophisieren", "Katastrophisieren"],
      ["ohnmacht", "Ohnmachtssprache"],
      ["mangel", "Mangel-Sätze"],
      ["etikett", "Selbst-Etiketten"],
      ["frust", "Frust-Ausbrüche (Fluchen)"],
    ];
    const lies = (): NonNullable<VoiceV8Settings["achtsamkeit"]> =>
      this.current.achtsamkeit ?? {};
    const speichere = (
      patch: Partial<NonNullable<VoiceV8Settings["achtsamkeit"]>>,
    ): void => {
      const alt = lies();
      // 23.09.2026, Ticket 2511: das GANZE Objekt — der Server legt es
      // ohnehin über seinen Stand, verliert also nichts mehr.
      void this.speichere({
        achtsamkeit: {
          ...alt,
          ...patch,
          kategorien: {
            ...(alt.kategorien ?? {}),
            ...(patch.kategorien ?? {}),
          },
        },
      });
    };

    const toggle = (
      label: string,
      istAn: boolean,
      onChange: (an: boolean) => void,
    ): HTMLElement => {
      const row = document.createElement("label");
      row.className = "settings-field settings-field-toggle";
      const lbl = document.createElement("span");
      lbl.className = "settings-label";
      lbl.textContent = label;
      row.appendChild(lbl);
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.checked = istAn;
      cb.addEventListener("change", () => onChange(cb.checked));
      row.appendChild(cb);
      return row;
    };

    // 2026-07-25 (Martin: "es sollte einstellbar sein, dass er nicht nur
    // still als Notification erscheint, sondern wirklich als Alarm, weil es
    // wichtig ist, dass ich informiert werde, weil ich gerade reaktiv war"):
    // dieselbe Signal-Auswahl wie bei den Erinnerungen. Voreinstellung still.
    const signalRow = document.createElement("label");
    signalRow.className = "settings-field";
    const signalLbl = document.createElement("span");
    signalLbl.className = "settings-label";
    signalLbl.textContent = "Signal bei Meldung";
    signalRow.appendChild(signalLbl);
    const signalSel = document.createElement("select");
    for (const [wert, text] of [
      ["still", "Still (nur Mitteilung)"],
      ["gong", "Kurzer Ton"],
      ["laeuten", "Läuten"],
      ["stimme", "Vorgelesen"],
      ["laeuten_stimme", "Läuten und vorgelesen"],
    ] as Array<[string, string]>) {
      const o = document.createElement("option");
      o.value = wert;
      o.textContent = text;
      signalSel.appendChild(o);
    }
    signalSel.value = lies().signal || "still";
    signalSel.addEventListener("change", () =>
      speichere({ signal: signalSel.value }),
    );
    signalRow.appendChild(signalSel);
    const signalHint = document.createElement("p");
    signalHint.className = "settings-hint";
    signalHint.textContent =
      "Mit Signal bleibt die Mitteilung stehen, bis du sie wegtippst, " +
      "und erreicht dich am iPhone auch im Fokus-Modus.";

    const katRows: HTMLElement[] = [];
    const master = toggle("Wächter aktiv", lies().aktiv !== false, (an) => {
      speichere({ aktiv: an });
      for (const r of katRows) r.classList.toggle("is-hidden", !an);
    });
    wrap.appendChild(master);
    wrap.appendChild(signalRow);
    wrap.appendChild(signalHint);
    for (const [key, label] of KATS) {
      const row = toggle(
        label,
        lies().kategorien?.[key] !== false,
        (an) => speichere({ kategorien: { [key]: an } }),
      );
      row.classList.toggle("is-hidden", lies().aktiv === false);
      katRows.push(row);
      wrap.appendChild(row);
    }
    return wrap;
  }

  private focusThemesBlock(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.className = "settings-subsection";

    const head = document.createElement("h3");
    head.className = "settings-subsection-title";
    head.textContent = "Schwerpunkte";
    wrap.appendChild(head);

    const intro = document.createElement("p");
    intro.className = "settings-modules-hint";
    intro.textContent =
      "Themen, die dich gerade beschäftigen oder die du vertiefen willst. Der Bot priorisiert sie im Gespräch und im täglichen Lebens-Digest. Beispiele: Spanisch lernen, Persönliche Entwicklung, Buchprojekt, Mehr Sport.";
    wrap.appendChild(intro);

    const chipsWrap = document.createElement("div");
    chipsWrap.className = "settings-chips";
    wrap.appendChild(chipsWrap);

    const inputRow = document.createElement("div");
    inputRow.className = "settings-chip-input-row";
    const input = document.createElement("input");
    input.type = "text";
    input.placeholder = "Neues Thema (Enter zum Hinzufügen)";
    input.className = "settings-chip-input";
    input.maxLength = 80;
    inputRow.appendChild(input);
    wrap.appendChild(inputRow);

    const statusEl = document.createElement("div");
    statusEl.className = "settings-inline-status";
    wrap.appendChild(statusEl);

    let themes: string[] = [];

    const render = (): void => {
      while (chipsWrap.firstChild) chipsWrap.removeChild(chipsWrap.firstChild);
      if (themes.length === 0) {
        const empty = document.createElement("div");
        empty.className = "settings-chips-empty";
        empty.textContent = "Noch keine Schwerpunkte gesetzt.";
        chipsWrap.appendChild(empty);
        return;
      }
      for (const t of themes) {
        const chip = document.createElement("span");
        chip.className = "settings-chip";
        const lbl = document.createElement("span");
        lbl.textContent = t;
        chip.appendChild(lbl);
        const x = document.createElement("button");
        x.type = "button";
        x.className = "settings-chip-x";
        x.setAttribute("aria-label", `${t} entfernen`);
        x.textContent = "×";
        x.addEventListener("click", () => {
          themes = themes.filter((x) => x !== t);
          render();
          void save();
        });
        chip.appendChild(x);
        chipsWrap.appendChild(chip);
      }
    };

    const save = async (): Promise<void> => {
      try {
        const r = await fetch("/api/user/focus-themes", {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ themes }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        statusEl.textContent = "Schwerpunkte gespeichert.";
        statusEl.dataset.kind = "success";
      } catch (err) {
        statusEl.textContent = `Speichern fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`;
        statusEl.dataset.kind = "error";
      }
    };

    input.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        const v = input.value.trim();
        if (!v) return;
        if (themes.length >= 10) {
          statusEl.textContent = "Maximal 10 Schwerpunkte.";
          statusEl.dataset.kind = "error";
          return;
        }
        if (!themes.includes(v)) {
          themes = [...themes, v];
          render();
          void save();
        }
        input.value = "";
      }
    });

    void (async () => {
      try {
        const r = await fetch("/api/user/focus-themes", {
          credentials: "include",
        });
        const j = (await r.json()) as { themes?: string[] };
        themes = Array.isArray(j.themes) ? j.themes : [];
        render();
      } catch {
        render();
      }
    })();

    return wrap;
  }

  /**
   * Pre.517 Editorial-Redesign: Erscheinung. Setzt Theme-Mode + Chat-Stil.
   * Beides gilt sofort, ohne Page-Reload. Cross-Device-Sync via /api/settings.
   */
  private appearanceBlock(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.className = "settings-subsection settings-subsection--darstellung";

    const head = document.createElement("h3");
    head.className = "settings-subsection-title";
    head.textContent = "Darstellung";
    wrap.appendChild(head);

    const intro = document.createElement("p");
    intro.className = "settings-modules-hint";
    intro.textContent =
      "Helligkeit, Farbe und die Gestalt von Zuhause sind getrennt — jede Kombination gilt.";
    wrap.appendChild(intro);

    // 2026-07-06 (Martin, Zwei-Achsen): Helligkeit (Modus) und Farbe (Palette)
    // sind jetzt ORTHOGONAL — jede Kombination gilt. Zwei getrennte Radio-
    // Reihen, jede markiert ihren eigenen aktiven Wert. Löst den alten Bug
    // "Hell + Wald springt zurück auf dunkel", weil Farbe keine Helligkeit
    // mehr mitbringt.
    const brightBtns: HTMLButtonElement[] = [];
    const paletteBtns: HTMLButtonElement[] = [];
    const zuhauseBtns: HTMLButtonElement[] = [];

    const buildRow = (
      title: string,
      values: readonly string[],
      labels: Record<string, string>,
      store: HTMLButtonElement[],
      onPick: (v: string) => void,
      gap = false,
    ): void => {
      // Ticket 2570: eine Hülle je Achse — am Rechner drei Karten
      // nebeneinander, sonst `display: contents` (Bild wie vorher).
      const wahl = document.createElement("div");
      wahl.className = "einst-achse";
      wahl.dataset.achse = title.toLowerCase();
      wrap.appendChild(wahl);
      const lbl = document.createElement("div");
      lbl.className = "settings-radio-label";
      lbl.textContent = title;
      if (gap) lbl.classList.add("settings-radio-label--gap");
      wahl.appendChild(lbl);
      const row = document.createElement("div");
      row.className = "settings-radio-row";
      for (const value of values) {
        const opt = document.createElement("button");
        opt.type = "button";
        opt.className = "settings-radio-btn";
        opt.dataset.value = value;
        opt.textContent = labels[value] ?? value;
        opt.addEventListener("click", () => {
          onPick(value);
          for (const b of store) {
            b.classList.toggle("is-active", b.dataset.value === value);
          }
        });
        row.appendChild(opt);
        store.push(opt);
      }
      wahl.appendChild(row);
    };

    buildRow("Modus", BRIGHTNESSES, BRIGHTNESS_LABELS, brightBtns, (v) =>
      setBrightness(v as (typeof BRIGHTNESSES)[number]),
    );
    buildRow(
      "Farbe",
      PALETTES,
      PALETTE_LABELS,
      paletteBtns,
      (v) => setPalette(v as (typeof PALETTES)[number]),
      true,
    );
    /*
     * 2026-08-29 (Martin, nach den drei Entwuerfen: „Ich finde das erste und
     * das dritte am besten. Geil wäre natürlich, wenn man dann in
     * Einstellungen hin und her schalten könnte."):
     *
     *   Dritte Achse, gleiche Reihe, gleicher Bau. Ein eigener Schalter an
     *   anderer Stelle waere der vierte Mechanismus fuers Aussehen — und der
     *   laeuft irgendwann auseinander.
     */
    buildRow(
      "Zuhause",
      ZUHAUSE_STILE,
      ZUHAUSE_STIL_LABELS,
      zuhauseBtns,
      (v) => setZuhauseStil(v as (typeof ZUHAUSE_STILE)[number]),
      true,
    );

    // Initial-State live aus dem appearance-Modul (synchron aus localStorage).
    const mark = (store: HTMLButtonElement[], active: string): void => {
      for (const b of store) {
        b.classList.toggle("is-active", b.dataset.value === active);
      }
    };
    mark(brightBtns, getBrightness());
    mark(paletteBtns, getPalette());
    mark(zuhauseBtns, getZuhauseStil());

    return wrap;
  }


  /**
   * Human-Design-in-Persona-Toggle. Opt-in (Default aus). Wenn aktiv, formt
   * das HD-Chart des Nutzers, WIE der Bot kommuniziert (Typ/Strategie/
   * Autorität). Persistiert wie jede andere Einstellung via saveSettings →
   * /api/settings.
   */

  /**
   * Pre.803 (#76): Geduldiges Zuhören — der Bot wartet im Anruf deutlich
   * länger, bevor er den Satz des Users als beendet wertet. Opt-in;
   * Default aus = Verhalten exakt wie bisher (pre.667-Lehre).
   */
  private buildIcalFeedsCard(): HTMLElement {
    const cardEl = document.createElement("section");
    cardEl.className = "settings-subsection";
    const title = document.createElement("h3");
    title.className = "settings-subsection-title";
    title.textContent = "Kalender-Feeds (iCal)";
    cardEl.appendChild(title);
    const hint = document.createElement("p");
    hint.className = "settings-hint";
    hint.textContent =
      "Öffentliche iCal-URLs (z.B. dein Airbnb-Kalender). Vespera kann daraus " +
      "Termine lesen — auch vergangene, z.B. für Abrechnungen. Unabhängig " +
      "von Google.";
    cardEl.appendChild(hint);

    const listEl = document.createElement("div");
    listEl.className = "vocab-list";
    cardEl.appendChild(listEl);

    type Feed = { id: string; name: string; url: string };
    const load = async (): Promise<void> => {
      try {
        const r = await fetch("/api/ical-feeds", { credentials: "same-origin" });
        const d = (await r.json()) as { feeds?: Feed[] };
        renderList(d.feeds ?? []);
      } catch {
        /* ignore */
      }
    };
    const renderList = (feeds: Feed[]): void => {
      listEl.textContent = "";
      if (feeds.length === 0) {
        const empty = document.createElement("p");
        empty.className = "settings-hint";
        empty.textContent = "Noch keine Feeds.";
        listEl.appendChild(empty);
        return;
      }
      for (const f of feeds) {
        const row = document.createElement("div");
        row.className = "vocab-row";
        const label = document.createElement("span");
        label.className = "vocab-term";
        label.textContent = `${f.name}  ·  ${f.url.replace(/^https?:\/\//, "").slice(0, 40)}…`;
        row.appendChild(label);
        const del = document.createElement("button");
        del.type = "button";
        del.className = "btn btn--sm btn--danger vocab-del";
        del.textContent = "Entfernen";
        del.addEventListener("click", () => {
          del.disabled = true;
          void fetch("/api/ical-feeds/" + encodeURIComponent(f.id), {
            method: "DELETE",
            credentials: "same-origin",
          })
            .then(() => load())
            .catch(() => {
              del.disabled = false;
            });
        });
        row.appendChild(del);
        listEl.appendChild(row);
      }
    };

    const form = document.createElement("div");
    form.className = "vocab-add";
    const nameIn = document.createElement("input");
    nameIn.type = "text";
    nameIn.className = "vocab-input";
    nameIn.placeholder = "Name (z.B. Airbnb Sagres)";
    const urlIn = document.createElement("input");
    urlIn.type = "url";
    urlIn.className = "vocab-input";
    urlIn.placeholder = "iCal-URL (https://…)";
    const addBtn = document.createElement("button");
    addBtn.type = "button";
    addBtn.className = "btn btn--primary btn--sm";
    addBtn.textContent = "Hinzufügen";
    const status = document.createElement("p");
    status.className = "settings-hint";
    const submit = async (): Promise<void> => {
      const url = urlIn.value.trim();
      if (!url) return;
      addBtn.disabled = true;
      status.textContent = "";
      try {
        const r = await fetch("/api/ical-feeds", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: nameIn.value.trim(), url }),
        });
        const d = (await r.json()) as { ok?: boolean; error?: string };
        if (!d.ok) {
          status.textContent = d.error || "Fehler beim Hinzufügen.";
        } else {
          nameIn.value = "";
          urlIn.value = "";
          await load();
        }
      } finally {
        addBtn.disabled = false;
      }
    };
    addBtn.addEventListener("click", () => void submit());
    form.appendChild(nameIn);
    form.appendChild(urlIn);
    form.appendChild(addBtn);
    cardEl.appendChild(form);
    cardEl.appendChild(status);

    void load();
    return cardEl;
  }

  /**
   * Eigene Wörter. (23.09.2026, Entwurf „Einstellungen neu geordnet")
   *
   * Martin: *„Das persönliche Vokabular in den Einstellungen ist so total
   * unbrauchbar, weil ich sehe, dass ich einfach nur eine ewig lange Liste
   * habe."* Gemessen: 57 Zeilen mit je einem roten „Entfernen", 3.611 Punkte
   * hoch. Jetzt: EIN Feld zum Suchen und Hinzufügen, der Vorschlag oben, die
   * Wörter als Marken nach Art. Entfernen gibt es nur noch im Wort selbst.
   *
   * ⚠️ Die Vorschläge behalten alles, was Martin sich dafür gewünscht hat:
   *    die Schreibweise lässt sich vor dem Übernehmen ändern („oder schreibe
   *    es noch richtig", 10.09.), und „Alle übernehmen" nennt die Zahl und
   *    geht nur in EINE Richtung — ein Sammelknopf zum Ablehnen wäre eine
   *    Falle (22.09.).
   */
  /**
   * Welches Modell die starken Auswertungen fährt.
   *
   * Martin am 04.09.2026: *„Ich stelle die Frage in den Raum, ob es wirklich
   * notwendig ist, mit Fable die Spiegel-Essenz zu erzeugen ... vielleicht
   * kann man es in den Einstellungen editierbar machen. Es geht auch ums
   * Sparen von Ressourcen, ohne die Qualität merklich zu verlieren."*
   *
   * ⚠️ Die Vorgabe bleibt Fable. Eine Einstellung, die still etwas anderes
   * tut als bisher, wäre eine Verschlechterung ohne Ansage.
   * (23.09.2026: Vorgabe ist Opus 5.5 — auf Martins Ansage. Ticket 2516
   * am selben Tag: Fable ist ganz aus der Auswahl; Opus oder Sonnet.)
   *
   * ⚠️ Der Hinweis nennt die BETROFFENEN Stellen. „Starke Auswertungen" sagt
   * niemandem, was sich ändert — und wer nicht weiß, was er umstellt, kann
   * die Qualität nicht beurteilen.
   */
  private buildStarkesModellCard(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.className = "settings-subsection";

    const head = document.createElement("h3");
    head.className = "settings-subsection-title";
    head.textContent = "Modell für die tiefen Auswertungen";
    wrap.appendChild(head);

    /*
     * ⚠️⚠️ 23.09.2026 — HIER STAND KEINE VERSION UND KEINE DENKTIEFE.
     *
     * Martin: *„Bitte alle Opus-5-Versionen auf das höchste Level anpassen
     * und entsprechend im Setting auch sichtbar machen."* Die Karte sagte
     * „Fable", „Opus", „Sonnet" — ohne Zahl. Am selben Morgen hatte ein
     * CLI-Update das Kürzel `opus` still von 5 auf 5.5 umgelegt, und
     * nirgends in der App hätte man es gesehen.
     *
     * ⚠️ Die Namen werden aus dem Register ABGELEITET (modellAusKurzform →
     *    claudeName), nicht hier hingeschrieben: beim nächsten Modell stimmen
     *    sie sonst wieder nicht — genau der Gemini-Fall vom 14.09.
     */
    const name = (kurz: StarkesModell): string =>
      claudeName(modellAusKurzform(kurz)) || kurz;

    /*
     * ⚠️ 23.09.2026, Ticket 2516 — hier stand „Fable 5.1 schreibt am
     *    dichtesten" samt eigener Auswahl. Martin: *„Fable 5.1 fällt bis auf
     *    Weiteres aus dem System."* Ein gespeichertes „fable" zeigt diese
     *    Karte als Opus — so läuft es auch (loadSettings, starkesModellAus).
     */
    const hint = document.createElement("p");
    hint.className = "settings-modules-hint";
    hint.textContent =
      "Betrifft Essenz-Spiegel, den roten Faden, das Buch „Mein Leben“, den " +
      "Wochenrückblick und Projektberichte — nicht den Chat und nicht das " +
      `Sprechen. ${name("opus")} ist gründlich und die Vorgabe, ` +
      `${name("sonnet")} ist am schnellsten.`;
    wrap.appendChild(hint);

    const zeile = document.createElement("div");
    zeile.className = "settings-field";
    const label = document.createElement("span");
    label.className = "settings-label";
    label.textContent = "Modell";
    zeile.appendChild(label);

    const wahl = document.createElement("select");
    wahl.className = "settings-input";
    for (const [wert, text] of [
      ["opus", `${name("opus")} — gründlich (Vorgabe)`],
      ["sonnet", `${name("sonnet")} — am schnellsten`],
    ] as const) {
      const o = document.createElement("option");
      o.value = wert;
      o.textContent = text;
      wahl.appendChild(o);
    }
    wahl.value = this.current.starkesModell ?? "opus";
    wahl.addEventListener("change", () => {
      const w = starkesModellAus(wahl.value);
      if (!w) return;
      void this.speichere(
        { starkesModell: w },
        `Tiefe Auswertungen laufen jetzt auf ${name(w)}.`,
      );
    });
    zeile.appendChild(wahl);
    wrap.appendChild(zeile);

    /*
     * Die Denktiefe für die zwei großen Werke. (23.09.2026)
     *
     * Martin: *„Beim Erstellen von ‚Mein Leben‘ bitte extra high und ebenso
     * beim Erstellen des täglichen Essenzspiegels."* — also sichtbar und
     * änderbar, nicht still im Code. Nur diese zwei: alles andere, was über
     * dieselbe Modellwahl läuft, denkt auf „Hoch" — sonst würde etwa der
     * Alltag-Digest bei jeder Aufnahme minutenlang nachdenken.
     */
    const tiefe = document.createElement("div");
    tiefe.className = "settings-field";
    const tiefeLabel = document.createElement("span");
    tiefeLabel.className = "settings-label";
    tiefeLabel.textContent = "Denktiefe für „Mein Leben“ und den Essenz-Spiegel";
    tiefe.appendChild(tiefeLabel);
    const tiefeWahl = document.createElement("select");
    tiefeWahl.className = "settings-input";
    for (const stufe of TIEFE_DENKSTUFEN) {
      const o = document.createElement("option");
      o.value = stufe;
      o.textContent =
        DENKSTUFE_NAME[stufe] +
        (stufe === DENKSTUFE_TIEF_VORGABE ? " (Vorgabe)" : "") +
        (stufe === "max" ? " — braucht spürbar länger" : "");
      tiefeWahl.appendChild(o);
    }
    tiefeWahl.value = this.current.tiefeDenkstufe ?? DENKSTUFE_TIEF_VORGABE;
    tiefeWahl.addEventListener("change", () => {
      const w = tiefeWahl.value;
      if (!istTiefeDenkstufe(w)) return;
      void this.speichere(
        { tiefeDenkstufe: w },
        `„Mein Leben“ und der Spiegel denken jetzt auf „${DENKSTUFE_NAME[w]}“.`,
      );
    });
    tiefe.appendChild(tiefeWahl);
    wrap.appendChild(tiefe);
    return wrap;
  }

  /**
   * Verlauf: was die Zeit aufräumt, und was man selbst anstoßen kann.
   * (22.09.2026)
   *
   * Martin, mit einem Foto der Symbol-Leiste: *„Ich möchte vermerken, dass
   * der Mistkübel verschwinden darf … ich verwende ihn jetzt schon seit
   * Wochen nicht mehr"* und *„den Reload drücke ich nie."*
   *
   * Beide Knöpfe sind aus der Leiste raus. Verloren ist nichts — sie stehen
   * hier, wo man sie einmal sucht statt täglich zu sehen.
   */
  private buildVerlaufCard(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.className = "settings-subsection";

    const head = document.createElement("h3");
    head.className = "settings-subsection-title";
    head.textContent = "Verlauf";
    wrap.appendChild(head);

    const hint = document.createElement("p");
    hint.className = "settings-modules-hint";
    hint.id = "archiv-tage-satz";
    hint.textContent = archivSatz(this.current.archivTage ?? 30);
    wrap.appendChild(hint);

    const feld = document.createElement("label");
    feld.className = "settings-field";
    const lbl = document.createElement("span");
    lbl.className = "settings-label";
    lbl.textContent = "Ins Archiv nach";
    feld.appendChild(lbl);

    const sel = document.createElement("select");
    sel.className = "settings-select";
    for (const t of ARCHIV_TAGE_AUSWAHL) {
      const o = document.createElement("option");
      o.value = String(t);
      o.textContent = t === 0 ? "nie (von Hand)" : `${t} Tagen`;
      sel.appendChild(o);
    }
    sel.value = String(this.current.archivTage ?? 30);
    sel.addEventListener("change", () => {
      const t = Number(sel.value);
      hint.textContent = archivSatz(t);
      void this.speichere({ archivTage: t });
    });
    feld.appendChild(sel);
    wrap.appendChild(feld);

    /*
     * ⚠️ Die zwei alten Knöpfe stehen hier UNTEN und ohne Symbol: sie sind
     *    Werkzeuge für den seltenen Fall, nicht für jeden Tag. Genau darum
     *    ist Martin sie in der Symbol-Leiste losgeworden.
     */
    const reihe = document.createElement("div");
    reihe.className = "settings-actions";

    const jetzt = document.createElement("button");
    jetzt.type = "button";
    jetzt.className = "settings-reset";
    jetzt.textContent = "Verlauf jetzt archivieren";
    jetzt.addEventListener("click", () => {
      const f = (
        window as unknown as { __v8VerlaufArchivieren?: () => Promise<void> }
      ).__v8VerlaufArchivieren;
      if (f) void f();
    });
    reihe.appendChild(jetzt);

    /*
     * ⚠️ 09.09.2026 (Martin: *„Wird das Material überhaupt genutzt?"*) — die
     *    ehrliche Antwort stand bis heute als Nachsatz in der Lade-Pille,
     *    wo sie bei JEDEM Laden über den halben Telefonbildschirm ging.
     *    Sie erklärt diesen Knopf, also gehört sie neben diesen Knopf.
     */
    const neuHint = document.createElement("p");
    neuHint.className = "settings-modules-hint";
    neuHint.textContent =
      "Der Knopf frischt auf, was du siehst. Vespera liest den Verlauf " +
      "ohnehin bei jedem Zug selbst aus der Datenbank — sie vergisst nichts, " +
      "wenn du ihn nicht drückst.";
    wrap.appendChild(neuHint);

    const neu = document.createElement("button");
    neu.type = "button";
    neu.className = "settings-reset";
    neu.textContent = "Verlauf neu vom Server holen";
    neu.addEventListener("click", () => {
      const f = (
        window as unknown as { __v8VerlaufNeuHolen?: () => Promise<void> }
      ).__v8VerlaufNeuHolen;
      if (f) void f();
    });
    reihe.appendChild(neu);

    wrap.appendChild(reihe);
    return wrap;
  }

  private buildAlwaysRecordCard(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.className = "settings-subsection";
    let supported = false;
    void import("./native-recorder.js").then((m) => {
      supported = m.isAlwaysRecordSupported();
      wrap.hidden = !supported;
    });
    wrap.hidden = true; // erst zeigen, wenn nativ verfuegbar

    const head = document.createElement("h3");
    head.className = "settings-subsection-title";
    head.textContent = "Immer aufzeichnen (rund um die Uhr)";
    wrap.appendChild(head);

    const hint = document.createElement("p");
    hint.className = "settings-modules-hint";
    hint.textContent =
      "Wenn aktiviert, nimmt Vespera deinen Alltag durchgehend auf — die " +
      "App startet die Aufnahme beim Öffnen selbst und setzt sie nach jeder " +
      "Unterbrechung den ganzen Tag fort. Alle zwei Stunden entsteht ein " +
      "neuer Eintrag. Zum Beenden einfach ausschalten oder auf Stopp drücken.";
    wrap.appendChild(hint);

    const toggleRow = document.createElement("label");
    toggleRow.className = "settings-field settings-field-toggle";
    const toggleLabel = document.createElement("span");
    toggleLabel.className = "settings-label";
    toggleLabel.textContent = "Durchgehende Aufnahme";
    const aufnahmeMarke = geraeteMarke("alwaysRecord");
    if (aufnahmeMarke) toggleLabel.appendChild(aufnahmeMarke);
    toggleRow.appendChild(toggleLabel);

    const cb = document.createElement("input");
    cb.type = "checkbox";
    // Ticket 745 (2026-07-27, Martin: „der Haken ist verschwunden"): Der
    // Zustand kommt jetzt aus den Einstellungen (Datenbank) statt aus dem
    // localStorage des WKWebView. Der alte Speicher ueberlebte keine
    // Neuinstallation der App — und die native Seite hat nur einen Setter,
    // kann die Wahrheit also nicht zurueckliefern.
    cb.checked = this.current.alwaysRecord === true;
    // Steht der Haken laut Datenbank, aber die App wurde frisch installiert,
    // weiss die native Seite nichts davon -> einmal nachreichen.
    if (cb.checked) {
      void import("./native-recorder.js").then((m) => {
        void m.setAlwaysDayRecording(true);
      });
    }
    cb.addEventListener("change", () => {
      const on = cb.checked;
      void this.speichere({ alwaysRecord: on });
      void import("./native-recorder.js").then(async (m) => {
        const ok = await m.setAlwaysDayRecording(on);
        if (!ok && on) {
          cb.checked = false;
          void this.speichere({ alwaysRecord: false });
          this.flashStatus("Geht nur in der iPhone-App.");
          return;
        }
        this.flashStatus(
          on
            ? "Immer aufzeichnen aktiviert — die Aufnahme läuft jetzt."
            : "Immer aufzeichnen ausgeschaltet.",
        );
      });
    });
    toggleRow.appendChild(cb);
    wrap.appendChild(toggleRow);

    return wrap;
  }

  private buildVoiceprintCard(): HTMLElement {
    const cardEl = document.createElement("section");
    cardEl.className = "settings-subsection";
    const title = document.createElement("h3");
    title.className = "settings-subsection-title";
    title.textContent = "Deine Stimme (Erkennung)";
    cardEl.appendChild(title);
    const hint = document.createElement("p");
    hint.className = "settings-hint";
    hint.textContent =
      `Sprich etwa eine Minute natürlich (erzähl einfach irgendetwas) und ` +
      `stopp dann selbst. Je mehr Stimme hier liegt, desto sicherer trennt ` +
      `Vespera dich in langen Aufnahmen von anderen. Spätestens nach ` +
      `${STIMMPROBE_MAX_S} Sekunden endet die Aufnahme von allein.`;
    cardEl.appendChild(hint);
    const status = document.createElement("div");
    status.className = "settings-voiceprint-status";
    cardEl.appendChild(status);
    status.setAttribute("role", "status");
    const actions = document.createElement("div");
    actions.className = "settings-voiceprint-actions";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "btn btn--primary btn--sm";
    btn.textContent = "Stimmprobe aufnehmen";
    actions.appendChild(btn);
    const delBtn = document.createElement("button");
    delBtn.type = "button";
    delBtn.className = "btn btn--danger btn--sm";
    delBtn.textContent = "Stimmprofil löschen";
    // Das Hauptkonto löscht nicht selbst (der Server lehnt es mit 403 ab) — dort gibt es den Knopf nicht.
    if (kontoKennung() !== 1) actions.appendChild(delBtn);
    cardEl.appendChild(actions);

    // Rückfrage IN der Karte (Ticket 3501): erst Bestätigen löscht.
    const frage = document.createElement("div");
    frage.className = "settings-voiceprint-frage";
    frage.setAttribute("role", "group");
    frage.setAttribute("aria-label", "Stimmprofil löschen");
    frage.hidden = true;
    const frageText = document.createElement("p");
    frageText.className = "settings-hint";
    frageText.textContent =
      "Das löscht deine Stimmprobe UND alle gespeicherten Stimmen anderer Personen in deiner Erkennung (z. B. benannte Sprecher). " +
      "Das gilt nur für neue Aufnahmen; bereits fertige Aufnahmen behalten ihre Namen. " +
      "Danach wird deine Stimme erst nach neuem Aufsprechen erkannt.";
    frage.appendChild(frageText);
    const frageZeile = document.createElement("div");
    frageZeile.className = "settings-voiceprint-actions";
    const jaBtn = document.createElement("button");
    jaBtn.type = "button";
    jaBtn.className = "btn btn--danger btn--sm";
    jaBtn.textContent = "Bestätigen";
    const nein = document.createElement("button");
    nein.type = "button";
    nein.className = "btn btn--ghost btn--sm";
    nein.textContent = "Abbrechen";
    frageZeile.appendChild(jaBtn);
    frageZeile.appendChild(nein);
    frage.appendChild(frageZeile);
    cardEl.appendChild(frage);

    const frageZu = (): void => {
      frage.hidden = true;
      actions.hidden = false;
    };
    delBtn.addEventListener("click", () => {
      actions.hidden = true;
      frage.hidden = false;
      nein.focus();
    });
    nein.addEventListener("click", () => {
      frageZu();
      delBtn.focus();
    });
    jaBtn.addEventListener("click", async () => {
      jaBtn.disabled = true;
      nein.disabled = true;
      status.textContent = "Lösche Stimmprofil …";
      try {
        const r = await fetch("/api/user/voiceprint/loeschen", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", "X-Vespera-Aktion": "stimmprofil" },
          body: "{}",
        });
        if (r.status === 403) {
          status.textContent = "Für das Hauptkonto bitte über den Schmied ändern lassen.";
        } else if (r.status === 429) {
          status.textContent = "Zu viele Versuche. Bitte versuche es in ein paar Minuten noch einmal.";
        } else if (!r.ok) {
          status.textContent = "Das Löschen hat nicht geklappt. Bitte versuche es noch einmal.";
        } else {
          status.textContent = "Noch keine Stimmprobe. Du kannst jetzt neu aufsprechen.";
          btn.textContent = "Stimmprobe aufnehmen";
        }
      } catch {
        status.textContent = "Das Löschen hat nicht geklappt. Bitte prüfe deine Verbindung.";
      } finally {
        jaBtn.disabled = false;
        nein.disabled = false;
        frageZu();
        delBtn.focus();
      }
    });

    // Status laden.
    void fetch("/api/user/voiceprint", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { hasVoiceprint?: boolean } | null) => {
        status.textContent = d?.hasVoiceprint
          ? "Stimmprobe gesetzt ✓"
          : "Noch keine Stimmprobe.";
        if (d?.hasVoiceprint) btn.textContent = "Neu aufnehmen";
      })
      .catch(() => {
        status.textContent = "";
      });

    let mr: MediaRecorder | null = null;
    let stream: MediaStream | null = null;
    let chunks: Blob[] = [];
    let stopTimer = 0;
    const cleanup = (): void => {
      window.clearTimeout(stopTimer);
      try {
        stream?.getTracks().forEach((t) => t.stop());
      } catch {
        /* egal */
      }
      stream = null;
      mr = null;
    };
    const upload = async (blob: Blob): Promise<void> => {
      status.textContent = "Speichere Stimmprobe …";
      try {
        const fd = new FormData();
        fd.append("audio", blob, "voiceprint.webm");
        const r = await fetch("/api/user/voiceprint", {
          method: "POST",
          credentials: "same-origin",
          body: fd,
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        status.textContent = "Stimmprobe gesetzt ✓";
        btn.textContent = "Neu aufnehmen";
      } catch (e) {
        status.textContent = `Fehler: ${e instanceof Error ? e.message : String(e)}`;
      } finally {
        btn.disabled = false;
      }
    };
    const stopAndSend = (): void => {
      if (mr && mr.state !== "inactive") {
        try {
          mr.stop();
        } catch {
          /* egal */
        }
      }
    };
    btn.addEventListener("click", async () => {
      // Laeuft schon → stoppen + senden.
      if (mr?.state === "recording") {
        btn.textContent = "Stimmprobe aufnehmen";
        stopAndSend();
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (e) {
        status.textContent =
          "Mikrofon nicht möglich — bitte Freigabe erlauben.";
        return;
      }
      chunks = [];
      mr = new MediaRecorder(stream);
      mr.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) chunks.push(ev.data);
      };
      mr.onstop = () => {
        const blob = new Blob(chunks, { type: chunks[0]?.type || "audio/webm" });
        cleanup();
        btn.disabled = true;
        void upload(blob);
      };
      mr.start();
      btn.textContent = "Stoppen & speichern";
      let sec = 20;
      status.textContent = `Aufnahme läuft … ${sec}s`;
      const tick = window.setInterval(() => {
        sec -= 1;
        if (sec > 0 && mr?.state === "recording") {
          status.textContent = `Aufnahme läuft … ${sec}s`;
        } else {
          window.clearInterval(tick);
        }
      }, 1000);
      // Reissleine, kein Ziel: der Nutzer stoppt normalerweise selbst.
      stopTimer = window.setTimeout(() => {
        btn.textContent = "Stimmprobe aufnehmen";
        stopAndSend();
      }, STIMMPROBE_MAX_S * 1000);
    });
    return cardEl;
  }


  /**
   * Innen und außen — der Baustein zur Selbstermächtigung im Prompt.
   * (24.09.2026, Ticket 2550)
   *
   * Martin: *„Der Haltungsbaustein kann gerne auf einen eigenen Schalter
   * kommen."* Er gilt für alle Konten und hängt NICHT am Schalter der
   * inneren Ausrichtung; der Server liest diesen Wert selbst, für Chat,
   * Anruf und Morgenmeditation (agent/persona.ts, selbstermaechtigungAn).
   *
   * ⚠️ Heißt bewusst nicht „Haltung": So heißt im Prompt die Tugend des
   *    Tages (#2545). Die Überschrift ist die des Bausteins selbst.
   */
  private selbstermaechtigungToggle(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.className = "settings-subsection";

    const head = document.createElement("h3");
    head.className = "settings-subsection-title";
    head.textContent = "Innen und außen";
    wrap.appendChild(head);

    // ⚠️ Der Satz nennt nur, was allein dieser Baustein bringt. Der „Schritt
    //    auf einen Menschen zu" steht auch in der inneren Ausrichtung und im
    //    Schluss der Morgenmeditation (Ticket 2549) — er bleibt dort, wenn
    //    dieser Schalter aus ist, und darf darum hier nicht versprochen werden.
    const hint = document.createElement("p");
    hint.className = "settings-modules-hint";
    hint.textContent =
      "Geht es um dich selbst, erinnert Vespera dich daran, was in deiner " +
      "Hand liegt, und kann sich dabei auf Gedanken von Epiktet, Frankl oder " +
      "Buber stützen — im Chat" +
      (kontoGruppen().anrufModell ? ", im Anruf" : "") +
      " und in der Morgenmeditation.";
    wrap.appendChild(hint);

    const toggleRow = document.createElement("label");
    toggleRow.className = "settings-field settings-field-toggle";
    const toggleLabel = document.createElement("span");
    toggleLabel.className = "settings-label";
    toggleLabel.textContent = "Selbstermächtigung einbeziehen";
    toggleRow.appendChild(toggleLabel);

    const cb = document.createElement("input");
    cb.type = "checkbox";
    // Vorgabe an: nur ein ausdrückliches „aus" gilt als aus.
    cb.checked = this.current.selbstermaechtigung !== false;
    cb.addEventListener("change", () => {
      void this.speichere(
        { selbstermaechtigung: cb.checked },
        cb.checked ? "Innen und außen eingeschaltet" : "Innen und außen ausgeschaltet",
      );
    });
    toggleRow.appendChild(cb);
    wrap.appendChild(toggleRow);

    return wrap;
  }

  /**
   * „Vespera darf mich anrufen“ (Ticket 2728, Stufe 1). Vorgabe AUS, nur das
   * Hauptkonto sieht ihn. Ein Kontowert; der Server liest ihn selbst und
   * sperrt jeden Anruf, solange er nicht ausdrücklich an ist.
   *
   * ⚠️ Ehrlich beschriftet: wie ein Telefon klingelt es nur mit der neuen
   *    App-Version (Stufe 2, VoIP/CallKit); sonst Meldung zum Antippen. Die
   *    Grenzen stehen aus derselben Quelle wie die Sperre (shared/anruf-grenzen.ts).
   *
   * 02.10.2026: Ruhezeit (von/bis, volle Stunden) und Tageslimit sind
   * einstellbar — immer sichtbar, auch bei ausgeschaltetem Schalter, damit
   * man die Grenzen vor dem Einschalten setzen kann. „von = bis“ heißt
   * „keine Ruhezeit“; die Zeile darunter sagt dann genau das.
   */
  private anrufErlaubtToggle(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.className = "settings-subsection settings-anruf";

    const head = document.createElement("h3");
    head.className = "settings-subsection-title";
    head.textContent = "Anrufe von Vespera";
    wrap.appendChild(head);

    const hint = document.createElement("p");
    hint.className = "settings-modules-hint";
    // Stufe 2 (02.10.2026): mit der neuen App-Version klingelt es über CallKit;
    // ohne sie bleibt es bei der Meldung. Die Seite weiß nicht verlässlich,
    // welche App-Version läuft — darum sagt der Satz beides.
    hint.textContent =
      "Wenn etwas nicht warten kann oder du einen Anruf bestellt hast, ruft " +
      "Vespera dich an. Mit der neuen App-Version klingelt es wie ein echter " +
      "Anruf, auch bei gesperrtem iPhone. Sonst kommt die Meldung „Vespera ruft " +
      "an“, und ein Tipp darauf beginnt das Gespräch.";
    wrap.appendChild(hint);

    const toggleRow = document.createElement("label");
    toggleRow.className = "settings-field settings-field-toggle";
    const toggleLabel = document.createElement("span");
    toggleLabel.className = "settings-label";
    toggleLabel.textContent = "Vespera darf mich anrufen";
    toggleRow.appendChild(toggleLabel);

    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.setAttribute("aria-describedby", "settings-anruf-grenzen");
    // Vorgabe AUS: nur ein ausdrückliches „an“ gilt als an.
    cb.checked = this.current.anrufErlaubt === true;
    cb.addEventListener("change", () => {
      void this.speichere(
        { anrufErlaubt: cb.checked },
        cb.checked ? "Anrufe von Vespera erlaubt" : "Anrufe von Vespera ausgeschaltet",
      );
    });
    toggleRow.appendChild(cb);
    wrap.appendChild(toggleRow);

    const grenzen = document.createElement("p");
    grenzen.id = "settings-anruf-grenzen";
    grenzen.className = "settings-modules-hint settings-anruf-grenzen";
    grenzen.setAttribute("aria-live", "polite");
    const zeigeGrenzen = (): void => {
      grenzen.textContent = anrufGrenzenText(anrufGrenzenVon(this.current));
    };
    zeigeGrenzen();

    const stunden = Array.from({ length: 24 }, (_, h) => h);
    const auswahl = (
      id: string,
      beschriftung: string,
      werte: number[],
      text: (w: number) => string,
      wert: number,
      speichern: (w: number) => Partial<VoiceV8Settings>,
    ): HTMLElement => {
      const feld = document.createElement("div");
      feld.className = "settings-field";
      const lbl = document.createElement("label");
      lbl.className = "settings-label";
      lbl.htmlFor = id;
      lbl.textContent = beschriftung;
      const sel = document.createElement("select");
      sel.id = id;
      sel.className = "settings-select";
      sel.setAttribute("aria-describedby", "settings-anruf-grenzen");
      for (const w of werte) {
        const o = document.createElement("option");
        o.value = String(w);
        o.textContent = text(w);
        sel.appendChild(o);
      }
      sel.value = String(wert);
      sel.addEventListener("change", () => {
        void this.speichere(speichern(Number(sel.value)), "Anruf-Grenzen gespeichert");
        zeigeGrenzen();
      });
      feld.append(lbl, sel);
      return feld;
    };
    const g = anrufGrenzenVon(this.current);
    wrap.appendChild(
      auswahl("settings-anruf-ruhe-ab", "Ruhezeit von", stunden, (h) => `${h} Uhr`, g.ruheAb, (h) => ({
        anrufRuheAb: h,
      })),
    );
    wrap.appendChild(
      auswahl("settings-anruf-ruhe-bis", "Ruhezeit bis", stunden, (h) => `${h} Uhr`, g.ruheBis, (h) => ({
        anrufRuheBis: h,
      })),
    );
    wrap.appendChild(
      auswahl(
        "settings-anruf-tageslimit",
        "Höchstens Anrufe pro Tag",
        [
          TAGESLIMIT_UNBEGRENZT,
          ...Array.from({ length: TAGESLIMIT_MAX - TAGESLIMIT_MIN + 1 }, (_, i) => TAGESLIMIT_MIN + i),
        ],
        (n) => (n === TAGESLIMIT_UNBEGRENZT ? "unbegrenzt" : String(n)),
        g.tageslimit,
        (n) => ({ anrufTageslimit: n }),
      ),
    );
    wrap.appendChild(grenzen);

    return wrap;
  }

  private hdPersonaToggle(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.className = "settings-subsection";

    const head = document.createElement("h3");
    head.className = "settings-subsection-title";
    head.textContent = "Human Design in Persona";
    wrap.appendChild(head);

    const hint = document.createElement("p");
    hint.className = "settings-modules-hint";
    hint.textContent =
      "Wenn aktiviert, passt der Bot seine Kommunikation an dein Human-Design-Chart an (Typ, Strategie, Autorität).";
    wrap.appendChild(hint);

    const toggleRow = document.createElement("label");
    toggleRow.className = "settings-field settings-field-toggle";
    const toggleLabel = document.createElement("span");
    toggleLabel.className = "settings-label";
    toggleLabel.textContent = "Anpassen";
    toggleRow.appendChild(toggleLabel);

    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.setAttribute("aria-label", "Human Design in Persona");
    cb.checked = this.current.useHdInPersona === true;
    cb.addEventListener("change", () => {
      void this.speichere(
        { useHdInPersona: cb.checked },
        cb.checked ? "Human Design in Persona aktiviert" : "Human Design in Persona deaktiviert",
      );
    });
    toggleRow.appendChild(cb);
    wrap.appendChild(toggleRow);

    return wrap;
  }

  /**
   * #183: Icons/Emojis in Antworten. Opt-in (Default aus).
   *
   * 29.09.2026 (Ticket 2736): drei Stufen statt eines Schalters — Martin:
   * eingeschaltet kamen sie *„so selten und zurückhaltend, dass man sie kaum
   * bemerkt"*. Dieselbe Stufenwahl wie im Blatt der Antwortlänge
   * (lib/schnell-einstellungen.ts), dasselbe Feld `emojisInReplies`.
   */
  private emojisInRepliesToggle(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.className = "settings-subsection";

    const head = document.createElement("h3");
    head.className = "settings-subsection-title";
    head.textContent = "Emojis in Antworten";
    wrap.appendChild(head);

    const hint = document.createElement("p");
    hint.className = "settings-modules-hint";
    hint.textContent =
      `Wie viele Emojis Vespera in ihre Antworten setzt, im Chat und an der Sprechtaste: keine, dezent eines hier und da, oder reichlich. Vorgelesen werden sie nicht. ${EMOJI_WANN} Im Anruf ab dem nächsten Anruf.`;
    wrap.appendChild(hint);

    const meldung: Record<EmojiStufenName, string> = {
      aus: "Emojis in Antworten: aus",
      dezent: "Emojis in Antworten: dezent",
      reichlich: "Emojis in Antworten: reichlich",
    };
    const wahl = baueStufenWahl<EmojiStufe>(
      "Emojis in Antworten",
      EMOJI_STUFEN.map((s) => ({ wert: s, text: EMOJI_TEXT[emojiStufenName(s)] })),
      (wert) => {
        wahl.zeige(wert);
        void this.speichere({ emojisInReplies: wert }, meldung[emojiStufenName(wert)]);
      },
    );
    wahl.zeige(emojiStufe(this.current.emojisInReplies));
    wrap.appendChild(wahl.el);

    return wrap;
  }

  /**
   * Darf Vespera aus der Stimme auf die Stimmung schliessen? (Ticket 2209)
   *
   * ⚠️ Steht bewusst NEBEN dem Emoji-Schalter, nicht bei den Mikrofon-
   *    Einstellungen: Es geht nicht um Technik, sondern darum, wie nah sie
   *    kommen darf. Und die Beschreibung sagt genau, was gespeichert wird —
   *    eine Erlaubnis ohne Wissen daruber ist keine.
   */
  private stimmungAusStimmeToggle(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.className = "settings-subsection";

    const head = document.createElement("h3");
    head.className = "settings-subsection-title";
    head.textContent = "Stimmung aus der Stimme";
    wrap.appendChild(head);

    const hint = document.createElement("p");
    hint.className = "settings-modules-hint";
    hint.textContent =
      "Wenn aktiviert, hört Vespera aus deinen Sprachnachrichten heraus, " +
      "wie es klingt — und ordnet vorsichtig ein, wie es wirkt (entspannt, " +
      "angespannt, müde und ähnlich). Immer als Beobachtung, nie als " +
      "Diagnose, und nur wenn sie sich sicher genug ist. Aus = sie " +
      "beschreibt nur noch den Klang, ohne Deutung.";
    wrap.appendChild(hint);

    const toggleRow = document.createElement("label");
    toggleRow.className = "settings-field settings-field-toggle";
    const toggleLabel = document.createElement("span");
    toggleLabel.className = "settings-label";
    toggleLabel.textContent = "Stimmung einordnen";
    toggleRow.appendChild(toggleLabel);

    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.checked = this.current.stimmungAusStimme !== false;
    cb.addEventListener("change", () => {
      void this.speichere(
        { stimmungAusStimme: cb.checked },
        cb.checked ? "Stimmung aus der Stimme aktiviert" : "Stimmung aus der Stimme deaktiviert",
      );
    });
    toggleRow.appendChild(cb);
    wrap.appendChild(toggleRow);

    return wrap;
  }

  /**
   * Ticket 2448 (21.09.2026): Vorlesen sofort — Beta, nur fuer Martin sichtbar.
   * Der Server liest denselben Schluessel (readDirektBeta) und reicht die
   * Soniox-Vertonung dann durch, waehrend sie entsteht. Die Warteschlange
   * faengt die zwei Juli-Schwaechen selbst (vorlesen-nachspiel.ts).
   */
  private direktStreamBlock(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.className = "settings-subsection";

    const head = document.createElement("h3");
    head.className = "settings-subsection-title";
    head.textContent = "Vorlesen sofort (Beta)";
    wrap.appendChild(head);

    const hint = document.createElement("p");
    hint.className = "settings-modules-hint";
    hint.textContent =
      "Der erste Ton kommt nach etwa einer halben Sekunde statt nach der ganzen Vertonung. Nur für dein Konto. Bricht die Wiedergabe ab, lädt die App das Stück gepuffert nach; endet sie zu früh, spielt sie den Rest nach. Bitte am iPhone ausprobieren und Bescheid geben.";
    wrap.appendChild(hint);

    const toggleRow = document.createElement("label");
    toggleRow.className = "settings-field settings-field-toggle";
    const toggleLabel = document.createElement("span");
    toggleLabel.className = "settings-label";
    toggleLabel.textContent = "Vorlesen sofort";
    toggleRow.appendChild(toggleLabel);

    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.checked = this.current.ttsDirektBeta === true;
    cb.addEventListener("change", () => {
      void this.speichere(
        { ttsDirektBeta: cb.checked },
        cb.checked ? "Vorlesen sofort aktiviert" : "Vorlesen sofort deaktiviert",
      );
    });
    toggleRow.appendChild(cb);
    wrap.appendChild(toggleRow);
    return wrap;
  }

  /**
   * Ticket 2919 (02.10.2026): Schneller Ton — drei Schalter, alle aus, nur für
   * Konto 1. Stufe 1 (PCM-Strom) bringt den ersten Ton von rund 3,5 s auf
   * rund 0,4 s; Stufe 0 (Tempo von Soniox) spart rund eine Sekunde; „Pausen
   * kürzen" ist vorbereitet und wartet auf die Hörprobe. Gleiche Klassen wie
   * „Vorlesen sofort" — kein eigenes Aussehen, keine Inline-Styles.
   */
  private schnellerWegBlock(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.className = "settings-subsection";

    const head = document.createElement("h3");
    head.className = "settings-subsection-title";
    head.textContent = "Schneller Ton (Beta)";
    wrap.appendChild(head);

    const hint = document.createElement("p");
    hint.className = "settings-modules-hint";
    hint.textContent =
      "Nur für dein Konto, alles aus, bis du es einschaltest. Die Stimme spielt direkt aus dem Datenstrom, der erste Ton kommt nach etwa einer halben Sekunde. Gilt für Soniox-Stimmen bis Tempo 1,3 und auf jedem Gerät deines Kontos. Die Stimme klingt dabei etwas anders und lauter als bisher: Das Tempo spricht immer die Stimme selbst (Sonioxs eigenes Tempo, es kürzt auch Pausen), und die Lautstärke kommt aus dem Browser. Bei gesperrtem Bildschirm kann die Wiedergabe pausieren oder auf die fertige Datei wechseln. Hakt es, schaltet sich der Weg selbst ab und liest wie bisher; aus- und wieder einschalten probiert es neu. Bitte am iPhone ausprobieren und Bescheid geben.";
    wrap.appendChild(hint);
    // Nachprüfung 2919: ein Rückfall steht hier, nicht nur im Journal — sonst wundert sich niemand, warum der Schalter nichts tut.
    if (pcmRueckfall.aktiv()) {
      const rueck = document.createElement("p");
      rueck.className = "settings-modules-hint";
      rueck.setAttribute("role", "status");
      rueck.textContent = `Zurückgeschaltet: ${pcmRueckfall.stand().grund ?? "gemerkt"}. Schalter aus und wieder an setzt es zurück.`;
      wrap.appendChild(rueck);
    }

    const zeile = (
      titel: string,
      an: boolean,
      beiAenderung: (an: boolean) => void,
    ): HTMLElement => {
      const row = document.createElement("label");
      row.className = "settings-field settings-field-toggle";
      const label = document.createElement("span");
      label.className = "settings-label";
      label.textContent = titel;
      row.appendChild(label);
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.checked = an;
      cb.addEventListener("change", () => beiAenderung(cb.checked));
      row.appendChild(cb);
      return row;
    };

    wrap.appendChild(
      zeile("Direkt aus dem Datenstrom spielen", this.current.ttsPcmBeta === true, (an) => {
        // Der Klick ist eine Geste: genau jetzt lässt sich der Ton-Kontext entsperren (iOS).
        if (an) {
          entsperrePcm(true);
          entsperreDateiSpieler(true);
        }
        else schliessePcmKontext(); // aus: die Tonsitzung nicht länger halten
        // Neu anfangen: ein früherer Rückfall auf die Datei gilt nicht mehr.
        pcmRueckfallZuruecksetzen();
        void this.speichere(
          { ttsPcmBeta: an },
          an ? "Direkt aus dem Datenstrom aktiviert" : "Direkt aus dem Datenstrom deaktiviert",
        );
      }),
    );
    wrap.appendChild(
      zeile("Tempo von der Stimme selbst", this.current.ttsTempoNativ === true, (an) => {
        void this.speichere(
          { ttsTempoNativ: an },
          an ? "Tempo von der Stimme selbst aktiviert" : "Tempo von der Stimme selbst deaktiviert",
        );
      }),
    );
    const hinweisPausen = document.createElement("p");
    hinweisPausen.className = "settings-modules-hint";
    hinweisPausen.textContent =
      "Pausen kürzen verkürzt die Pausen zwischen Sätzen um etwa eine Sekunde je Antwort. Es wirkt nur zusammen mit einem der beiden Schalter oben und ist noch nicht gehört — erst nach einer Hörprobe einschalten.";
    wrap.appendChild(hinweisPausen);
    wrap.appendChild(
      zeile("Pausen kürzen", this.current.ttsStilleKuerzen === true, (an) => {
        void this.speichere(
          { ttsStilleKuerzen: an },
          an ? "Pausen kürzen aktiviert" : "Pausen kürzen deaktiviert",
        );
      }),
    );
    // Ticket 2738 (Stufe 2, Jarvis-Modus): die Sofort-Antwort — ein Schalter, serverseitig je Zug gelesen.
    const hinweisVorantwort = document.createElement("p");
    hinweisVorantwort.className = "settings-modules-hint";
    hinweisVorantwort.textContent =
      "Bei Aufträgen, die etwas nachschlagen oder tun, sagt ein kleines Modell sofort einen kurzen Satz aus einer festen Liste, etwa „Moment, ich schaue nach.“, während die eigentliche Antwort entsteht. Er nimmt den Auftrag nur auf, nie ein Ergebnis; bei Geld, Löschen und Senden heißt er immer „Moment, ich prüfe das.“ Wirkt ohne Neustart, nur für dein Konto und nur auf der Sprechtaste, und nur, wenn der Vor-Modell-Schattenlauf läuft. Im Antwortmodus „kurz“ ist er höchstens vier Wörter lang, etwa „Gut, ich schaue nach.“";
    wrap.appendChild(hinweisVorantwort);
    wrap.appendChild(
      zeile("Sofort-Antwort bei Aufträgen", this.current.vorantwortBeta === true, (an) => {
        void this.speichere(
          { vorantwortBeta: an },
          an ? "Sofort-Antwort aktiviert" : "Sofort-Antwort deaktiviert",
        );
      }),
    );
    /*
     * 05.10.2026: der Rückweg für den Wiederanlauf stehender Stücke (lib/vorlese-wiederanlauf.ts). Vorgabe AN.
     * Ein Schalter des GERÄTS (localStorage), kein Kontowert: er wirkt sofort beim nächsten Stück und braucht
     * weder Speichern noch Neuladen — für den Fall, dass das Vorlesen am iPhone dadurch stottert.
     */
    const hinweisWiederanlauf = document.createElement("p");
    hinweisWiederanlauf.className = "settings-modules-hint";
    hinweisWiederanlauf.textContent =
      "Bleibt ein Stück beim Vorlesen stehen, etwa nach einem kurzen Wegwischen der App, startet es von selbst neu und liest an derselben Stelle weiter. Nur ausschalten, wenn das Vorlesen dadurch stottert oder etwas doppelt spricht; dann gilt wieder der frühere Hinweis „Tippen zum Weiterspielen“. Gilt für dieses Gerät, ab dem nächsten Stück.";
    wrap.appendChild(hinweisWiederanlauf);
    wrap.appendChild(zeile("Stehendes Vorlesen selbst neu starten", wiederanlaufAn(), (an) => setzeWiederanlauf(an)));
    return wrap;
  }

  private passiveKnowledgeToggle(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.className = "settings-subsection";

    const head = document.createElement("h3");
    head.className = "settings-subsection-title";
    head.textContent = "Passives Wissensmodul";
    wrap.appendChild(head);

    const hint = document.createElement("p");
    hint.className = "settings-modules-hint";
    hint.textContent =
      "Wenn aktiviert, recherchiert der Bot bei erkannten Wissenslücken im Hintergrund (ohne den Gesprächsfluss zu stören) und legt die Ergebnisse dauerhaft ab. Opt-in — kann Recherche-Kosten verursachen. Aus = keine ungefragte Recherche.";
    wrap.appendChild(hint);

    const toggleRow = document.createElement("label");
    toggleRow.className = "settings-field settings-field-toggle";
    const toggleLabel = document.createElement("span");
    toggleLabel.className = "settings-label";
    toggleLabel.textContent = "Passives Wissensmodul";
    toggleRow.appendChild(toggleLabel);

    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.checked = this.current.passiveKnowledge === true;
    cb.addEventListener("change", () => {
      void this.speichere(
        { passiveKnowledge: cb.checked },
        cb.checked ? "Passives Wissensmodul aktiviert" : "Passives Wissensmodul deaktiviert",
      );
    });
    toggleRow.appendChild(cb);
    wrap.appendChild(toggleRow);

    return wrap;
  }

  private personaBlock(): HTMLElement {
    return createPersonaBlock({
      flashStatus: (msg) => this.flashStatus(msg),
      onAvatarSaved: () => {
        new SettingsView(this.root);
      },
    });
  }


  /** Dieselbe Versionsnummer wie Kopf, Chat, Footer, Pille. */
  private versionBlock(): HTMLElement {
    const card = document.createElement("section");
    card.className = "settings-card";
    const h = document.createElement("h3");
    h.textContent = "Version";
    card.appendChild(h);
    const p = document.createElement("p");
    p.className = "settings-hint";
    p.id = "settings-version";
    p.textContent = sichtbareVersionJetzt() || "…";
    card.appendChild(p);
    return card;
  }

  /** Abmelden — in Einstellungen (Ich/Mehr), nicht neben dem Gruss/Namen. */
  private logoutBlock(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "settings-actions";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.id = "settings-logout";
    btn.className = "settings-reset";
    btn.textContent = "Abmelden";
    wrap.appendChild(btn);
    return wrap;
  }

  // 2026-07-06 (Martin): Claude-Konto selbst waehlen (auto/1/2).
  private accountBlock(): HTMLElement {
    const card = document.createElement("section");
    card.className = "settings-card";
    const h = document.createElement("h3");
    h.textContent = "Claude-Konto";
    card.appendChild(h);
    const hint = document.createElement("p");
    hint.className = "settings-hint";
    hint.textContent = "Welches Abo-Konto die Antworten liefert. Automatisch = bei Limit aufs andere ausweichen.";
    card.appendChild(hint);
    const row = document.createElement("div");
    row.className = "settings-account-row";
    const status = document.createElement("p");
    status.className = "settings-hint settings-account-status";
    const OPTIONS: Array<{ v: "auto" | 1 | 2; label: string }> = [
      { v: "auto", label: "Automatisch" },
      { v: 1, label: "Konto 1" },
      { v: 2, label: "Konto 2" },
    ];
    const btns = new Map<string, HTMLButtonElement>();
    const renderState = (st: {
      mode: "auto" | 1 | 2;
      accounts?: Array<{ n: number; label?: string; downUntil: number | null }>;
    }): void => {
      for (const [k, b] of btns) b.dataset.active = String(k === String(st.mode));
      // 2026-07-06 (Martin): Konten mit Adresse beschriften, damit man
      // office@ssi.at vs. mmollay erkennt.
      for (const a of st.accounts ?? []) {
        const b = btns.get(String(a.n));
        if (b && a.label) b.textContent = `Konto ${a.n} · ${a.label}`;
      }
      const notes: string[] = [];
      for (const a of st.accounts ?? []) {
        if (a.downUntil && a.downUntil > Date.now()) {
          const t = new Date(a.downUntil);
          notes.push(
            `Konto ${a.n} gesperrt bis ${t.getHours()}:${String(t.getMinutes()).padStart(2, "0")}`,
          );
        }
      }
      status.textContent = notes.join(" · ") || "Beide Konten frei.";
    };
    for (const o of OPTIONS) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "settings-account-btn";
      b.textContent = o.label;
      // 2026-08-20: Der Knopf kuerzt jetzt mit Ellipse, statt ueber den Rand
      // hinauszulaufen (siehe .settings-account-btn im Stylesheet). Damit die
      // volle Adresse nicht verlorengeht, steht sie im Tooltip.
      b.title = o.label;
      b.addEventListener("click", () => {
        void fetch("/api/warm-account", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ mode: o.v }),
        })
          .then((r) => r.json())
          .then((st) => renderState(st))
          .catch(() => undefined);
      });
      btns.set(String(o.v), b);
      row.appendChild(b);
    }
    card.appendChild(row);
    card.appendChild(status);
    void fetch("/api/warm-account", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((st) => {
        if (st) renderState(st);
        else card.remove(); // kein Superuser
      })
      .catch(() => undefined);
    return card;
  }

  private pushBlock(): HTMLElement {
    const wrap = document.createElement("section");
    wrap.className = "settings-push";

    const head = document.createElement("h3");
    head.textContent = "Benachrichtigungen";
    wrap.appendChild(head);

    const intro = document.createElement("p");
    intro.className = "settings-modules-hint";
    intro.textContent =
      "Bekomm eine kurze Push-Nachricht wenn der Bot dir antwortet während die App im Hintergrund ist.";
    wrap.appendChild(intro);

    const actions = document.createElement("div");
    actions.className = "settings-push-actions";

    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "settings-push-toggle";
    toggle.textContent = "wird geprüft…";
    toggle.disabled = true;
    actions.appendChild(toggle);

    const test = document.createElement("button");
    test.type = "button";
    test.className = "settings-push-test";
    test.textContent = "Test senden";
    test.hidden = true;
    actions.appendChild(test);

    wrap.appendChild(actions);

    const refresh = async (): Promise<void> => {
      const status = await push.getStatus();
      if (status.permission === "unsupported") {
        // 2026-06-29 (Martin): Web-Push gibt es im nativen App-Fenster nicht.
        // Klare Botschaft statt „Nicht unterstützt"-Alarm — native App-Push
        // (APNs) ist in Arbeit.
        const cap = (
          window as unknown as {
            Capacitor?: { isNativePlatform?: () => boolean };
          }
        ).Capacitor;
        const isNative =
          typeof cap?.isNativePlatform === "function" &&
          cap.isNativePlatform();
        toggle.textContent = isNative
          ? "Native App-Benachrichtigungen — in Arbeit"
          : "In diesem Browser nicht verfügbar (als App installieren)";
        toggle.disabled = true;
        return;
      }
      if (status.permission === "denied") {
        toggle.textContent =
          "Im Browser blockiert — bitte in den Einstellungen erlauben";
        toggle.disabled = true;
        return;
      }
      toggle.disabled = false;
      if (status.subscribed) {
        toggle.textContent = "Push ist aktiv — abschalten";
        toggle.dataset.state = "on";
        test.hidden = false;
      } else {
        toggle.textContent = "Push aktivieren";
        toggle.dataset.state = "off";
        test.hidden = true;
      }
    };

    toggle.addEventListener("click", async () => {
      toggle.disabled = true;
      const state = toggle.dataset.state;
      if (state === "on") {
        await push.unsubscribe();
        this.flashStatus("Push abgeschaltet");
      } else {
        const result = await push.subscribe();
        if (!result.ok) {
          this.flashStatus(`Push fehlgeschlagen: ${result.error}`);
        } else {
          this.flashStatus("Push aktiv");
        }
      }
      await refresh();
    });

    test.addEventListener("click", async () => {
      test.disabled = true;
      try {
        const result = await push.sendTest();
        this.flashStatus(`Test gesendet (${result.sent}/${result.total})`);
      } catch (err) {
        this.flashStatus(
          `Fehler: ${err instanceof Error ? err.message : String(err)}`,
        );
      } finally {
        test.disabled = false;
      }
    });

    void refresh();
    return wrap;
  }



  // Pre.970: Realtime-Modell-Picker entfernt — nur noch 3.1 nutzbar, das
  // Modell ist fest auf den Provider-Default verdrahtet. Der legacy
  // Realtime-Voice-Picker (durch unifiedVoicePicker, Pre.419, ersetzt) wurde
  // ebenfalls entfernt; die aktive Stimmenwahl läuft über unifiedVoicePicker.
  // Pre.180: TTS-Provider-Picker — ElevenLabs / Google / Auto-Fallback.

  private fieldRange(
    label: string,
    key: keyof VoiceV8Settings,
    min: number,
    max: number,
    step: number,
    /** Wie der Wert neben dem Namen steht (Ticket 2800: „1,4×“ wie im Blatt). */
    text: (wert: number) => string,
  ): HTMLElement {
    const row = document.createElement("label");
    row.className = "settings-field";
    // Der Feldname an der Zeile: die Suche springt dorthin (settings/einst-suche.ts).
    row.dataset.feld = key;

    const head = document.createElement("span");
    head.className = "settings-label";
    const valueDisplay = document.createElement("span");
    valueDisplay.className = "settings-range-value";
    valueDisplay.textContent = text(Number(this.current[key]));
    head.textContent = label;
    head.appendChild(valueDisplay);
    row.appendChild(head);

    const range = document.createElement("input");
    range.type = "range";
    range.min = String(min);
    range.max = String(max);
    range.step = String(step);
    range.value = String(this.current[key]);
    range.addEventListener("input", () => {
      const v = Number(range.value);
      valueDisplay.textContent = text(v);
      // Jede Reglerbewegung merkt vor; gesendet wird gebündelt (ein PUT zur
      // Zeit, einstellungen-abgleich.ts).
      void this.speichere({ [key]: v });
    });
    row.appendChild(range);
    return row;
  }

  private resetToDefaults(root: HTMLElement): void {
    this.destroy();
    // 19.10.2026: nur Felder, die dieses Konto hat — der Server verwirft die übrigen und die App meldete sonst einen Fehler.
    const lage = kontoLage();
    void speichereEinstellungen(
      Object.fromEntries(Object.entries(DEFAULT_SETTINGS).filter(([k]) => feldErlaubtFuerKonto(lage, k))),
    );
    new SettingsView(root);
  }

  /**
   * Eine kurze Zeile unten. `art`: Fehler und Hinweise bleiben länger stehen
   * und heben sich ab (23.09.2026, Ticket 2511 — „sichtbar statt still").
   */
  private flashStatus(msg: string, art?: "fehler" | "hinweis"): void {
    this.status.textContent = msg;
    this.status.dataset.flash = "1";
    if (art) this.status.dataset.art = art;
    else delete this.status.dataset.art;
    this.status.setAttribute("role", art === "fehler" ? "alert" : "status");
    const text = msg;
    setTimeout(
      () => {
        // Eine neuere Meldung räumt ihre eigene Zeit selbst ab.
        if (this.status.textContent !== text) return;
        delete this.status.dataset.flash;
        delete this.status.dataset.art;
        this.status.textContent = "";
      },
      art ? 6000 : 1500,
    );
  }

  /**
   * Reißt die aktive Vorschau-Audio sauber ab — Pause + Source entfernen
   * + Blob-URL revoken. Idempotent.
   */
  private stopPreview(): void {
    if (this.previewAudio) {
      try {
        this.previewAudio.pause();
        this.previewAudio.removeAttribute("src");
        this.previewAudio.load();
      } catch {
        /* element already detached */
      }
      this.previewAudio = null;
    }
    if (this.previewBlobUrl) {
      try {
        URL.revokeObjectURL(this.previewBlobUrl);
      } catch {
        /* ignore */
      }
      this.previewBlobUrl = null;
    }
  }

  /**
   * Public Cleanup-Hook — vom Caller (app.ts) aufrufbar wenn die View
   * komplett verschwindet. Aktuell konstruiert der App-Code immer ein
   * neues SettingsView-Objekt; der GC fängt das alte. Wir bieten dispose()
   * trotzdem an damit künftig kein Audio im Hintergrund weiterspielt.
   */
  public dispose(): void {
    this.stopPreview();
  }

  /**
   * Voice-Picker — gruppiert nach Geschlecht, mit Vorschau-Button.
   * Klick auf Card wählt die Stimme; Klick auf Play holt /api/voice-preview
   * und spielt das Sample ab. Nur EINE Vorschau aktiv gleichzeitig.
   */
  /**
   * Pre.419: Einheitlicher Voice-Picker für beide Modi (Pipeline + Realtime).
   * Gemini-Live-Voices = Chirp3-HD-Voices (gleiche Voice-Family seit 2025).
   * Auswahl wird in settings.realtimeVoice gespeichert. Agent mappt
   * automatisch auf Chirp3-HD wenn Pipeline-Mode aktiv ist (Pre.410).
   */
  private unifiedVoicePicker(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "settings-field voice-picker";

    const head = document.createElement("span");
    head.className = "settings-label";
    head.textContent = "Stimme";
    wrap.appendChild(head);

    // 2026-07-20 (Martin, "kann nur maennlich/weiblich waehlen"): der
    // Picker traegt jetzt BEIDE Welten mit ehrlicher Beschriftung —
    // Anruf (Gemini, feste Stimmen) und Vorlesen/PTT (ElevenLabs, hier
    // wirkt die eigene Stimme).
    const callHead = document.createElement("p");
    callHead.className = "settings-modules-hint";
    callHead.textContent =
      "ANRUF-STIMME — gilt nur im Live-Anruf (feste Gemini-Stimmen). " +
      "Beide Auswahlen unten gelten GLEICHZEITIG: eine fürs Telefonieren, " +
      "eine fürs Vorlesen.";
    // 19.10.2026: Die Anruf-Stimmen nur mit Anruf im Paket — sonst nur die Vorlese-Stimme darunter.
    const mitAnrufStimme = kontoGruppen().anrufStimme;
    if (mitAnrufStimme) wrap.appendChild(callHead);
    // 2026-06-29 (Martin): bei nur zwei Stimmen die Gender-Gruppen NEBEN-
    // einander statt untereinander — spart Platz.
    const sectionsRow = document.createElement("div");
    sectionsRow.className = "voice-sections-row";

    // Pre.664: selbes Fundament wie die übrigen Picker — ♀/♂-Gruppen, Gender
    // aus dem echten `gender`-Feld (nicht mehr aus der Beschreibung geraten).
    const groups: Array<{ id: VoiceGender; title: string }> = [
      { id: "female", title: "♀ Weiblich" },
      { id: "male", title: "♂ Männlich" },
    ];

    // Aktuelle Auswahl: realtimeVoice (oder "Aoede" als Default).
    const activeId = this.current.realtimeVoice || "Aoede";

    const cardByVoice = new Map<string, HTMLElement>();
    const setActive = (id: string): void => {
      cardByVoice.forEach((card, vid) => {
        card.dataset.active = String(vid === id);
        card.setAttribute("aria-checked", String(vid === id));
      });
    };

    for (const group of groups) {
      const voices = GEMINI_REALTIME_VOICES.filter(
        (v) => v.gender === group.id,
      );
      if (voices.length === 0) continue;

      const section = document.createElement("section");
      section.className = "voice-section";
      section.dataset.gender = group.id;

      const title = document.createElement("h4");
      title.className = "voice-section-title";
      title.textContent = group.title;
      section.appendChild(title);

      for (const v of voices) {
        const card = document.createElement("div");
        card.className = "voice-card";
        card.dataset.voiceId = v.id;
        card.dataset.active = String(activeId === v.id);
        card.setAttribute("role", "radio");
        card.setAttribute("aria-checked", String(activeId === v.id));
        card.tabIndex = 0;
        cardByVoice.set(v.id, card);

        const info = document.createElement("div");
        info.className = "voice-card-info";
        const name = document.createElement("div");
        name.className = "voice-card-name";
        name.textContent = v.label;
        info.appendChild(name);
        card.appendChild(info);

        const play = document.createElement("button");
        play.type = "button";
        play.className = "voice-card-play";
        play.setAttribute("aria-label", `Vorschau ${v.label}`);
        play.title = "Vorschau";
        play.dataset.state = "idle";
        this.setPlayIcon(play, "idle");
        card.appendChild(play);

        const select = (): void => {
          if (this.current.realtimeVoice === v.id) return;
          void this.speichere({ realtimeVoice: v.id }, `Stimme gespeichert: ${v.label}`);
          setActive(v.id);
        };

        card.addEventListener("click", (ev) => {
          if (
            ev.target instanceof HTMLElement &&
            ev.target.closest(".voice-card-play")
          ) {
            return;
          }
          select();
        });
        card.addEventListener("keydown", (ev) => {
          if (ev.key === " " || ev.key === "Enter") {
            ev.preventDefault();
            select();
          }
        });

        play.addEventListener("click", async (ev) => {
          ev.stopPropagation();
          await this.playRealtimePreview(v.id, play);
        });

        section.appendChild(card);
      }
      sectionsRow.appendChild(section);
    }
    if (mitAnrufStimme) wrap.appendChild(sectionsRow);

    // ── Vorlesen & Push-to-Talk (ElevenLabs) — hier lebt die eigene Stimme.
    const readHead = document.createElement("p");
    readHead.className = "settings-modules-hint";
    readHead.textContent =
      mitAnrufStimme
        ? "VORLESE-STIMME — EINE Stimme für alles außer dem Anruf: Antworten, " + "Vorlesen, Buch, Hörfassung, Tiefe Fragen."
        : "VORLESE-STIMME — EINE Stimme für alles: Antworten, Vorlesen, Buch, Hörfassung, Tiefe Fragen.";
    wrap.appendChild(readHead);

    const readRow = document.createElement("div");
    readRow.className = "voice-sections-row";
    const readSection = document.createElement("section");
    readSection.className = "voice-section";
    const readTitle = document.createElement("h4");
    readTitle.className = "voice-section-title";
    readTitle.textContent = "Vorlese-Stimme";
    readSection.appendChild(readTitle);

    // 2026-07-23 (Martin, "eine Stimme fuer alles"): drei Anbieter,
    // klar gruppiert — Gemini (Anruf-Klangfamilie), ElevenLabs (inkl.
    // eigener Stimme), Soniox. Anbieter ergibt sich aus dem id-Praefix.
    const provOf = (id: string): string =>
      id.startsWith("de-DE-")
        ? "Gemini"
        : id.startsWith("soniox:")
          ? "Soniox"
          : "ElevenLabs";
    // 2026-07-25 (Ticket 557): Die EINGEBAUTEN ElevenLabs-Stimmen
    // (Sarah/George/Brian) verschwinden aus der Auswahl — Martin nutzt
    // Soniox. ElevenLabs bleibt im Code voll funktionsfaehig; nur die
    // Auswahl belastet den Nutzer nicht mehr. Die EIGENE geklonte Stimme
    // (customVoices) bleibt selbstverstaendlich, ebenso eine aktuell
    // gewaehlte Eleven-Stimme — sonst stuende der Picker leer auf einer
    // unsichtbaren Auswahl.
    /*
     * ⚠️ 2026-09-10 (Martin): *„bei der Vorlesestimme 2 von Eleven Labs zur
     *    Auswahl."*
     *
     *    Der Filter vom 24.07. liess NUR Soniox durch (plus die gerade
     *    gewaehlte Stimme). Am 09.09. wurde VOICE_OPTIONS auf genau sechs
     *    kuratiert — vier Soniox und zwei ElevenLabs (Sarah, George), und
     *    die zwei standen seither in der Liste, ohne je sichtbar zu sein.
     *    Die Liste IST jetzt die Auswahl; ein zweiter Filter darueber
     *    waere die naechste Stelle, die auseinanderlaeuft.
     */
    const alleStimmen = [
      ...GEMINI_READ_VOICES,
      ...VOICE_OPTIONS,
      ...(this.current.customVoices ?? []),
    ];
    /*
     * ⚠️ 16.09.2026, Martin: *„Bitte beim User nur Soniox und Gemini anzeigen
     *    […] Dann auch natürlich bei Soniox männlich und weiblich nebeneinander,
     *    sodass das ordentlich sortiert ist. Wir müssen weiter zusammenräumen,
     *    sodass der User nicht unnötige Informationen hat, die ihn verwirren."*
     *
     *    Zwei Anbieter, sonst nichts. Innerhalb eines Anbieters erst die
     *    weiblichen, dann die männlichen — so wie es Gemini mit seinen zwei
     *    Stimmen schon vormacht (Aoede vor Charon).
     *
     * ⚠️ ELEVENLABS WIRD NICHT STILL ENTFERNT. Wer heute eine solche Stimme
     *    eingestellt hat, sähe sie sonst aus der Auswahl verschwinden, ohne
     *    dass sich etwas geändert hätte. Deshalb: die Gruppe fällt weg, aber
     *    eine BEREITS GEWÄHLTE Stimme bleibt sichtbar und wählbar. Wer sie
     *    verlässt, kommt nicht mehr zurück — das ist die Absicht.
     */
    const nachGeschlecht = (liste: typeof alleStimmen): typeof alleStimmen => [
      ...liste.filter((v) => v.gender === "female"),
      ...liste.filter((v) => v.gender !== "female"),
    ];
    const gewaehlteFremde = alleStimmen.filter(
      (v) => provOf(v.id) === "ElevenLabs" && v.id === this.current.voiceId,
    );
    const readVoices = [
      ...nachGeschlecht(alleStimmen.filter((v) => provOf(v.id) === "Gemini")),
      ...nachGeschlecht(alleStimmen.filter((v) => provOf(v.id) === "Soniox")),
      ...gewaehlteFremde,
    ];
    let letzterAnbieter = "";
    const readCardByVoice = new Map<string, HTMLElement>();
    // Ohne gewählte Vorlese-Stimme liest die Stimme aus `realtimeVoice` (Chirp) — ohne Anruf steht sie als die gewählte da.
    const readActive = this.current.readVoiceChosen
      ? this.current.voiceId
      : mitAnrufStimme
        ? ""
        : `de-DE-Chirp3-HD-${this.current.realtimeVoice || "Aoede"}`;
    const setReadActive = (id: string): void => {
      readCardByVoice.forEach((card, vid) => {
        card.dataset.active = String(vid === id);
        card.setAttribute("aria-checked", String(vid === id));
      });
    };
    for (const v of readVoices) {
      const anbieter = provOf(v.id);
      if (anbieter !== letzterAnbieter) {
        letzterAnbieter = anbieter;
        const gruppe = document.createElement("p");
        gruppe.className = "settings-modules-hint";
        // Label spannt die volle Rasterzeile, statt eine Karten-Zelle zu belegen.
        gruppe.style.gridColumn = "1 / -1";
        gruppe.style.margin = "6px 0 0";
        // ⚠️ Nur der Anbieter. „günstig", „gleiche Klangfamilie wie der
        //    Anruf" und „natürliche Stimmen" waren Begründungen für eine
        //    Wahl, die der Mensch ohnehin durch Hören trifft — Martin:
        //    „Einfach nur die Anbieter anzeigen und fertig."
        gruppe.textContent =
          anbieter === "ElevenLabs" ? "Deine bisherige Stimme" : anbieter;
        readSection.appendChild(gruppe);
      }
      const card = document.createElement("div");
      card.className = "voice-card";
      card.dataset.voiceId = v.id;
      card.dataset.active = String(readActive === v.id);
      card.setAttribute("role", "radio");
      card.setAttribute("aria-checked", String(readActive === v.id));
      card.tabIndex = 0;
      readCardByVoice.set(v.id, card);

      const info = document.createElement("div");
      info.className = "voice-card-info";
      const name = document.createElement("div");
      name.className = "voice-card-name";
      // 2026-07-24 (Martin, "Icon passt nicht"): Unicode ♀/♂ rendert iOS
      // als BUNTE EMOJI — stattdessen saubere gezeichnete Symbole (SVG
      // Venus/Mars), dezente Themenfarbe, fluchtend.
      if (v.gender === "female" || v.gender === "male") {
        const g = document.createElement("span");
        g.className = "voice-card-gender voice-card-gender--" + v.gender;
        g.setAttribute(
          "aria-label",
          v.gender === "female" ? "weiblich" : "männlich",
        );
        g.innerHTML =
          v.gender === "female"
            ? '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="5"/><path d="M12 13v8M9 18h6"/></svg>'
            : '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="10" cy="14" r="5"/><path d="M13.5 10.5 20 4M15 4h5v5"/></svg>';
        name.appendChild(g);
      }
      const nameLbl = document.createElement("span");
      nameLbl.className = "voice-card-label";
      nameLbl.textContent = v.label;
      name.appendChild(nameLbl);
      info.appendChild(name);
      card.appendChild(info);

      const play = document.createElement("button");
      play.type = "button";
      play.className = "voice-card-play";
      play.setAttribute("aria-label", `Vorschau ${v.label}`);
      play.title = "Vorschau";
      play.dataset.state = "idle";
      this.setPlayIcon(play, "idle");
      card.appendChild(play);

      const select = (): void => {
        void this.speichere(
          { voiceId: v.id, readVoiceChosen: true },
          `Vorlese-Stimme gespeichert: ${v.label}`,
        );
        setReadActive(v.id);
      };
      card.addEventListener("click", (ev) => {
        if (
          ev.target instanceof HTMLElement &&
          ev.target.closest(".voice-card-play")
        ) {
          return;
        }
        select();
      });
      card.addEventListener("keydown", (ev) => {
        if (ev.key === " " || ev.key === "Enter") {
          ev.preventDefault();
          select();
        }
      });
      play.addEventListener("click", async (ev) => {
        ev.stopPropagation();
        await this.playReadVoicePreview(v.id, play, v.model);
      });
      readSection.appendChild(card);
    }
    readRow.appendChild(readSection);
    wrap.appendChild(readRow);

    // 06.09.2026 (Martin): die Rückfallstimme gehört in die Einstellungen —
    // heute Morgen las der ElevenLabs-Klon, weil Soniox leer war, und war
    // hier nirgends wählbar. Alles außer Soniox kommt in Frage.
    const rueckfallRow = document.createElement("div");
    rueckfallRow.className = "settings-row settings-row--rueckfall";
    const rueckfallLbl = document.createElement("label");
    rueckfallLbl.className = "settings-label";
    rueckfallLbl.textContent = "Rückfallstimme — liest, wenn die gewählte Stimme ausfällt";
    const rueckfallSel = document.createElement("select");
    rueckfallSel.className = "set-select";
    rueckfallSel.setAttribute("aria-label", "Rückfallstimme");
    const auto = document.createElement("option");
    auto.value = "";
    auto.textContent = "Automatisch (ElevenLabs-Stimme des Servers)";
    rueckfallSel.appendChild(auto);
    for (const v of readVoices) {
      if (v.id.startsWith("soniox:")) continue;
      const o = document.createElement("option");
      o.value = v.id;
      o.textContent = `${v.label} · ${provOf(v.id)}`;
      rueckfallSel.appendChild(o);
    }
    rueckfallSel.value = this.current.rueckfallVoiceId ?? "";
    if (rueckfallSel.value !== (this.current.rueckfallVoiceId ?? "")) rueckfallSel.value = "";
    rueckfallSel.addEventListener("change", () => {
      void this.speichere(
        { rueckfallVoiceId: rueckfallSel.value || null },
        rueckfallSel.value ? "Rückfallstimme gespeichert" : "Rückfallstimme: automatisch",
      );
    });
    rueckfallRow.append(rueckfallLbl, rueckfallSel);
    wrap.appendChild(rueckfallRow);

    /*
     * 24.09.2026, Ticket 2556: Martin zur ersten Morgenmeditation: *„die
     * Stimme ist zu wach, zu schnell, wirkt eher gestresst."* Die Meditation
     * hat jetzt eine eigene Stimme, getrennt von der Vorlese-Stimme oben.
     * „Automatisch" ist die ruhige Vorgabe aus shared/meditations-stimme.ts;
     * der Nachtlauf entscheidet sie am Server mit derselben Regel, und die
     * Namen im Text kommen aus denselben Konstanten.
     */
    const meditationRow = document.createElement("div");
    meditationRow.className = "settings-row settings-row--meditation-stimme";
    const meditationSel = document.createElement("select");
    meditationSel.id = "einst-meditation-stimme";
    meditationSel.className = "set-select";
    const meditationLbl = document.createElement("label");
    meditationLbl.htmlFor = meditationSel.id;
    meditationLbl.className = "settings-label";
    // 24.09.2026, Ticket 2569: nicht mehr „langsamer" — sie liest im normalen
    // Tempo, die Ruhe kommt aus der Stille zwischen den Absätzen.
    meditationLbl.textContent = "Stimme der Meditation — liest die Morgenmeditation, mit Stille zwischen den Absätzen";
    const meditationAuto = document.createElement("option");
    meditationAuto.value = "";
    meditationAuto.textContent = `Automatisch (${vorgabeBeschreibung()})`;
    meditationSel.appendChild(meditationAuto);
    for (const v of MEDITATIONS_STIMMEN) {
      const o = document.createElement("option");
      o.value = v.id;
      o.textContent = `${v.label} · Soniox`;
      meditationSel.appendChild(o);
    }
    meditationSel.value = this.current.meditationVoiceId ?? "";
    if (meditationSel.value !== (this.current.meditationVoiceId ?? "")) meditationSel.value = "";
    meditationSel.addEventListener("change", () => {
      void this.speichere(
        { meditationVoiceId: meditationSel.value || null },
        meditationSel.value
          ? "Stimme der Meditation gespeichert"
          : "Stimme der Meditation: automatisch",
      );
    });
    meditationRow.append(meditationLbl, meditationSel);
    wrap.appendChild(meditationRow);
    // Ticket 3438: Stil der Morgenmeditation — Standard bleibt die tagesbezogene.
    const stilRow = document.createElement("div");
    stilRow.className = "settings-row settings-row--meditation-stil";
    const stilSel = document.createElement("select");
    stilSel.id = "einst-meditation-stil";
    stilSel.className = "set-select";
    const stilLbl = document.createElement("label");
    stilLbl.htmlFor = stilSel.id;
    stilLbl.className = "settings-label";
    stilLbl.textContent = MEDITATIONS_STIL_TITEL;
    for (const id of MEDITATIONS_STILE) {
      const o = document.createElement("option");
      o.value = id;
      o.textContent = MEDITATIONS_STIL_NAMEN[id];
      stilSel.appendChild(o);
    }
    stilSel.value = meditationsStilAus(this.current.meditationStil);
    stilSel.addEventListener("change", () => {
      const wahl = meditationsStilAus(stilSel.value);
      void this.speichere({ meditationStil: wahl }, `Stil der Morgenmeditation: ${MEDITATIONS_STIL_NAMEN[wahl]}`);
    });
    stilRow.append(stilLbl, stilSel);
    wrap.appendChild(stilRow);
    // 07.10.2026 (Martin: „NUR FÜR DIE MEDITATION die ElevenLabs V4“): nur Konto 1.
    const uidMeditation = (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid;
    if (meditationElevenWaehlbar(uidMeditation)) {
      wrap.appendChild(this.meditationElevenKarte(meditationSel));
    }

    return wrap;
  }

  /**
   * ElevenLabs v4 für die Meditation. (07.10.2026, nur Konto 1)
   *
   * Martin: *„In erster Linie möchte ich alternativ NUR FÜR DIE MEDITATION die
   * ElevenLabs V4 haben."* Eine eigene Wahl unter „Stimme der Meditation",
   * weil es zwei Felder sind (shared/meditations-stimme.ts): die Soniox-Stimme
   * darüber bleibt stehen und liest, wenn ElevenLabs nicht spricht. Dazu die
   * Hörprobe — ein ruhiger Satz mit genau der Stimme, die die Meditation
   * lesen wird. Texte: meditation-eleven.ts.
   */
  private meditationElevenKarte(sonioxWahl: HTMLSelectElement): HTMLElement {
    const karte = document.createElement("div");
    karte.className = "meditation-eleven";

    const zeile = document.createElement("div");
    zeile.className = "settings-row settings-row--meditation-eleven";
    const wahl = document.createElement("select");
    wahl.id = "einst-meditation-eleven";
    wahl.className = "set-select";
    const titel = document.createElement("label");
    titel.htmlFor = wahl.id;
    titel.className = "settings-label";
    titel.textContent = ELEVEN_TITEL;
    const aus = document.createElement("option");
    aus.value = "";
    aus.textContent = ELEVEN_AUS;
    wahl.appendChild(aus);
    // Die bekannten ElevenLabs-Stimmen, dazu eigene des Kontos (Kennung geprüft wie am Server).
    const stimmen = [
      ...MEDITATION_ELEVEN_STIMMEN,
      ...(this.current.customVoices ?? []).filter(
        (v) => istElevenStimmKennung(v.id) && !MEDITATION_ELEVEN_STIMMEN.some((m) => m.id === v.id),
      ),
    ];
    for (const v of stimmen) {
      const o = document.createElement("option");
      o.value = v.id;
      o.textContent = v.label;
      wahl.appendChild(o);
    }
    /*
     * Eine gespeicherte Kennung, die nicht (mehr) in der Liste steht — etwa
     * eine gelöschte eigene Stimme. Der Server liest sie weiter mit v4 und
     * bucht Credits; „Aus" zu zeigen wäre gelogen. Darum eine eigene Zeile
     * mit Hinweis, und „Aus" ist eine Wahl entfernt.
     */
    const gespeichert = this.current.meditationElevenStimme ?? "";
    const verwaist = gespeichert !== "" && !stimmen.some((v) => v.id === gespeichert);
    if (verwaist) {
      const o = document.createElement("option");
      o.value = gespeichert;
      o.textContent = ELEVEN_VERWAIST;
      wahl.appendChild(o);
    }
    wahl.value = gespeichert;
    const hinweis = document.createElement("p");
    hinweis.id = "einst-meditation-eleven-hinweis";
    hinweis.className = "meditation-eleven-hinweis";
    hinweis.textContent = ELEVEN_VERWAIST_HINWEIS;
    const zeigeHinweis = (): void => {
      hinweis.hidden = !verwaist || wahl.value !== gespeichert;
      if (hinweis.hidden) wahl.removeAttribute("aria-describedby");
      else wahl.setAttribute("aria-describedby", hinweis.id);
    };
    zeigeHinweis();
    wahl.addEventListener("change", () => {
      const name =
        stimmen.find((v) => v.id === wahl.value)?.label ??
        (verwaist && wahl.value === gespeichert ? "die gespeicherte Stimme" : null);
      void this.speichere({ meditationElevenStimme: wahl.value || null }, elevenGespeichert(name));
      zeigeHinweis();
    });
    zeile.append(titel, wahl);

    const satz = document.createElement("p");
    satz.className = "meditation-eleven-satz";
    satz.textContent = meditationKostenSatz();

    const probe = document.createElement("button");
    probe.type = "button";
    probe.className = "btn btn--ghost meditation-probe";
    const zeige = (zustand: "idle" | "loading" | "playing"): void => {
      this.setPlayIcon(probe, zustand);
      const text = document.createElement("span");
      text.textContent =
        zustand === "loading" ? "Probe lädt …" : zustand === "playing" ? "Probe anhalten" : "Probe hören";
      probe.appendChild(text);
      probe.setAttribute("aria-busy", String(zustand === "loading"));
    };
    zeige("idle");
    probe.addEventListener("click", () => {
      if (probe.dataset.state === "loading") return;
      if (probe.dataset.state === "playing") {
        this.stopPreview();
        zeige("idle");
        return;
      }
      // Was die Meditation lesen wird: ElevenLabs, wenn gewählt — sonst die Soniox-Stimme darüber.
      void this.spieleMeditationsProbe(wahl.value || sonioxWahl.value || null, wahl.value !== "", zeige);
    });

    karte.append(zeile, hinweis, satz, probe, einstMehr(ELEVEN_MEHR_FRAGE, ELEVEN_MEHR_ANTWORT));
    return karte;
  }

  /**
   * Die Hörprobe der Meditationsstimme. Ohne Wahl spielt die Vorgabe zur
   * Alltagsstimme (dieselbe Regel wie der Nachtlauf). Nie stumm: was nicht
   * spielt oder wer statt ElevenLabs spricht, steht in der Zeile unten.
   */
  private async spieleMeditationsProbe(
    gewaehlt: string | null,
    eleven: boolean,
    zeige: (zustand: "idle" | "loading" | "playing") => void,
  ): Promise<void> {
    this.stopPreview();
    zeige("loading");
    const stimme = gewaehlt ?? meditationsVorgabe(chosenReadVoice());
    let deutung: ProbeDeutung | null = null;
    try {
      const res = await fetch("/api/voice-preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(
          eleven
            ? { voiceId: stimme, ttsProvider: "elevenlabs", zweck: PROBE_ZWECK, text: MEDITATIONS_PROBE }
            : { voiceId: stimme, ttsProvider: "soniox", text: MEDITATIONS_PROBE },
        ),
      });
      if (eleven) {
        deutung = deuteElevenProbe({
          ok: res.ok,
          status: res.status,
          kopf: (n) => res.headers.get(n),
          text: res.ok ? undefined : await res.text().catch(() => ""),
        });
      } else if (!res.ok) {
        deutung = { spielt: false, art: "fehler", meldung: "Soniox antwortet gerade nicht — die Probe kann nicht spielen." };
      } else if (/rueckfall/.test(res.headers.get("X-Voice-Provider") ?? "")) {
        deutung = { spielt: true, art: "hinweis", meldung: "Soniox ist gerade nicht erreichbar — du hörst die Rückfallstimme." };
      }
      if (deutung && !deutung.spielt) {
        zeige("idle");
        this.flashStatus(deutung.meldung, deutung.art);
        return;
      }
      const url = URL.createObjectURL(await res.blob());
      const audio = new Audio(url);
      this.previewAudio = audio;
      this.previewBlobUrl = url;
      const fertig = (): void => {
        zeige("idle");
        URL.revokeObjectURL(url);
        if (this.previewBlobUrl === url) this.previewBlobUrl = null;
        if (this.previewAudio === audio) this.previewAudio = null;
      };
      audio.addEventListener("ended", fertig);
      audio.addEventListener("error", fertig);
      zeige("playing");
      await audio.play();
      if (deutung?.meldung) this.flashStatus(deutung.meldung, deutung.art);
    } catch {
      zeige("idle");
      this.flashStatus("Die Probe kann gerade nicht spielen — bitte gleich noch einmal.", "fehler");
    }
  }

  /** Vorlese-Vorschau via /api/voice-preview (ElevenLabs-Pfad). */
  private async playReadVoicePreview(
    voiceId: string,
    btn: HTMLButtonElement,
    modelId?: string,
    // Das Blatt eines Wortes spricht das Wort selbst (23.09.2026).
    text = "Hallo! So klingt diese Stimme beim Vorlesen.",
  ): Promise<void> {
    document
      .querySelectorAll<HTMLButtonElement>(
        '.voice-card-play[data-state="playing"], .voice-card-play[data-state="loading"]',
      )
      .forEach((prev) => {
        if (prev !== btn) this.setPlayIcon(prev, "idle");
      });
    this.setPlayIcon(btn, "loading");
    try {
      // 2026-07-24 (Martin): Die Probe uebernimmt das eingestellte
      // Vorlese-Tempo — so hoert man, ob es zu schnell ist.
      const rate = this.current.speed ?? 1.0;
      const res = await fetch("/api/voice-preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(
          voiceId.startsWith("de-DE-Chirp3-HD-")
            ? {
                voiceId,
                // Gemini-Stimme: Einheits-Zweig gezielt mit DIESER Stimme
                // (ohne geminiVoice faellt er immer auf Charon zurueck).
                ttsProvider: "google",
                geminiVoice: voiceId.slice("de-DE-Chirp3-HD-".length),
                speakingRate: rate,
                text,
              }
            : {
                voiceId,
                ttsProvider: voiceId.startsWith("soniox:")
                  ? "soniox"
                  : "elevenlabs",
                modelId,
                speakingRate: rate,
                text,
              },
        ),
      });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      this.setPlayIcon(btn, "playing");
      audio.addEventListener("ended", () => {
        URL.revokeObjectURL(url);
        this.setPlayIcon(btn, "idle");
      });
      audio.addEventListener("error", () => {
        URL.revokeObjectURL(url);
        this.setPlayIcon(btn, "idle");
      });
      await audio.play();
    } catch (err) {
      this.setPlayIcon(btn, "idle");
      // 2026-07-24 (Martin, "immer ein Ton"): bei leerem ElevenLabs-
      // Guthaben (401) klar sagen, WARUM nichts kommt — statt stumm.
      const isEleven =
        !voiceId.startsWith("soniox:") && !voiceId.startsWith("de-DE-");
      const quota = String(err).includes("401") || String(err).includes("429");
      this.flashStatus(
        isEleven && quota
          ? "ElevenLabs hat gerade kein Guthaben — wähle eine Gemini- oder Soniox-Stimme (beide günstig)."
          : "Vorschau gerade nicht möglich.",
      );
    }
  }

  /** Preview via /api/voice-preview-realtime, gleicher Endpoint wie alt-Picker. */
  private async playRealtimePreview(
    voiceId: string,
    btn: HTMLButtonElement,
  ): Promise<void> {
    // Pre.458 UI-Bug: Wenn der User auf eine andere Stimme klickt, blieb
    // der vorherige Button auf "playing" stehen → mehrere Pause-Icons
    // gleichzeitig sichtbar. Vor jedem neuen Preview ALLE Buttons im
    // Voice-Picker auf idle zurücksetzen.
    document
      .querySelectorAll<HTMLButtonElement>(
        '.voice-card-play[data-state="playing"], .voice-card-play[data-state="loading"]',
      )
      .forEach((prev) => {
        if (prev !== btn) {
          prev.dataset.state = "idle";
          this.setPlayIcon(prev, "idle");
        }
      });

    // Pre.458: Klick auf BEREITS spielende Stimme → Toggle (stoppen).
    if (btn.dataset.state === "playing") {
      this.stopPreview();
      btn.dataset.state = "idle";
      this.setPlayIcon(btn, "idle");
      return;
    }

    this.stopPreview();
    btn.dataset.state = "loading";
    this.setPlayIcon(btn, "loading");
    try {
      const r = await fetch("/api/voice-preview-realtime", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider: "gemini", voice: voiceId }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      this.previewAudio = audio;
      this.previewBlobUrl = url;
      btn.dataset.state = "playing";
      this.setPlayIcon(btn, "playing");
      audio.onended = (): void => {
        btn.dataset.state = "idle";
        this.setPlayIcon(btn, "idle");
        URL.revokeObjectURL(url);
        if (this.previewBlobUrl === url) this.previewBlobUrl = null;
        if (this.previewAudio === audio) this.previewAudio = null;
      };
      await audio.play();
    } catch (err) {
      btn.dataset.state = "idle";
      this.setPlayIcon(btn, "idle");
      this.flashStatus(
        `Vorschau fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  private setPlayIcon(
    btn: HTMLButtonElement,
    state: "idle" | "loading" | "playing",
  ): void {
    while (btn.firstChild) btn.removeChild(btn.firstChild);
    btn.dataset.state = state;
    const ns = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(ns, "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("width", "18");
    svg.setAttribute("height", "18");
    svg.setAttribute("aria-hidden", "true");
    if (state === "loading") {
      // Kreis-Spinner via CSS-Animation auf .voice-card-play[data-state=loading]
      const circle = document.createElementNS(ns, "circle");
      circle.setAttribute("cx", "12");
      circle.setAttribute("cy", "12");
      circle.setAttribute("r", "8");
      circle.setAttribute("fill", "none");
      circle.setAttribute("stroke", "currentColor");
      circle.setAttribute("stroke-width", "2.5");
      circle.setAttribute("stroke-linecap", "round");
      circle.setAttribute("stroke-dasharray", "30 100");
      svg.appendChild(circle);
    } else if (state === "playing") {
      const path = document.createElementNS(ns, "path");
      path.setAttribute("fill", "currentColor");
      path.setAttribute("d", "M6 5h4v14H6zM14 5h4v14h-4z");
      svg.appendChild(path);
    } else {
      const path = document.createElementNS(ns, "path");
      path.setAttribute("fill", "currentColor");
      path.setAttribute("d", "M8 5v14l11-7z");
      svg.appendChild(path);
    }
    btn.appendChild(svg);
  }

}
